/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
// eslint-disable-next-line @typescript-eslint/no-inferrable-types
import {
    AlignmentType,
    Document,
    Footer,
    HeadingLevel,
    LevelFormat,
    PageNumber,
    Paragraph,
    TextRun,
    UnderlineType,
    LevelSuffix,
} from 'docx'
import lowerfirst from 'lodash.lowerfirst'
import { isClaim } from '../../components/ClaimSelect/ClaimSelect'
import { IFigure } from '../../DiagramsLib/DiagramsLib.types'
import { ClaimsState } from '../../store/slices/claims/claims.types'
import { getQuestions, ValidQuestionKeys } from '../../hooks/useGerateQuestions'
import {
    CustomFormInstance,
    isCompositionForm,
    isMachineForm,
    isManufactureForm,
    isMethodsForm,
    ValueOf,
} from '../../types'
import {
    AdditionalCharacteristics,
    FormValues,
    MachineForm,
    MachineNames,
    MachineValues,
    MethodsForm,
    MethodsNames,
    SharedNames,
    Range,
    UserDefinedFormat,
    ManufactureValues,
    PrimaryComponent,
    SubComponent,
    PrimaryAdditionalComponent,
} from '../../utils/forms/methods/types'
import { getDocxStyles } from '../../utils/getDocxStyles'
import {
    extractComponentName,
    extractIndefiniteArticle,
} from '../../utils/functions'
import { connectionRelation, UNITS } from '../../const'

const docxStyles = getDocxStyles()

const ILLUSTRATION_TYPE = {
    schematic: 'schematic',
    scale: 'to scale',
}

const FIGURE_TYPE = {
    flow: 'flow chart',
    illustration: 'illustration of an embodiment',
}

export const generateDOCX = (
    formRef: CustomFormInstance,
    activeQuestions: ValidQuestionKeys[],
    category: keyof getQuestions,
    figures: undefined | IFigure[],
    claims: ClaimsState,
    isDownload = false
) => {
    let claimsCounter = 0
    let pushSubcomponents = true
    let pushCharacteristics = true
    let pushAdditionalCharacteristic = true
    let pushFormatUser = true
    let pushSubcharacteristic = true
    let pushSubAdditionalCharacteristic = true
    let pushSubFormatUser = true
    let pushAddCompCharacteristics = true
    let pushAddCompAdditionalCharacteristic = true
    let pushAddCompFormatUser = true
    let pushFigures = true
    // let authorsCount = 0
    let parentClaimPrimaryComponents: PrimaryComponent[] = []
    let parentClaimAdditionalComponents: PrimaryAdditionalComponent[] = []
    let parentClaimSubComponentsIdList: (string | undefined)[] = []
    const arr: any[] = []
    const headingArr: any[] = []
    let titleArr: any[] = []
    let drawingsDescriptionArr: any[] = []
    let backgroundInventionArr: any[] = []
    let detailedDescriptionArr: any[] = []
    let summaryArr: any[] = []
    const parentClaimArr: any[] = []
    const childClaimArr: any[] = []
    const abstractArr: any[] = []

    // Claim tracking system for managing dependencies and references
    interface ClaimInfo {
        claimNumber: number
        componentId: string // component ID that this claim contains
    }
    const childClaimMap: Map<number, ClaimInfo> = new Map()

    /**
     * Registers a claim and its component in the tracking system
     * Used to maintain claim dependencies and cross-references
     */
    const registerClaim = (claimNumber: number, componentId: string) => {
        const claimInfo: ClaimInfo = {
            claimNumber,
            componentId,
        }
        childClaimMap.set(claimNumber, claimInfo)
    }

    /**
     * Gets the claim number that contains a specific component
     * Returns the dependent claim if component was moved out, otherwise returns 1
     */
    const getClaimForFeature = (componentId: string): number => {
        let claimNo = 1
        childClaimMap.forEach((claim) => {
            if (claim.componentId === componentId) {
                claimNo = claim.claimNumber
            }
        })

        // no found and starts with "add_", try without the prefix
        if (claimNo === 1 && componentId.startsWith('add_')) {
            const actualId = componentId.substring(4) // Remove "add_"
            childClaimMap.forEach((claim) => {
                if (claim.componentId === actualId) {
                    claimNo = claim.claimNumber
                }
            })
        }

        // not found and contains "+=>" (subcomponent), check parent
        if (claimNo === 1 && componentId.includes('+=>')) {
            const parentId = componentId.split('+=>')[0]
            childClaimMap.forEach((claim) => {
                if (claim.componentId === parentId) {
                    claimNo = claim.claimNumber
                }
            })
        }
        return claimNo
    }

    /**
     * Returns the correct claim reference string for a relationship between two components.
     * - If both are in same claim (or one is in claim 1), returns that single claim number.
     * - If they are in two DIFFERENT child claims, the dependent claim hangs from comp2's
     *   claim (the anchor). comp1 is explicitly reintroduced via getComp1ReintroductionClause.
     */
    const getClaimRefForTwoComponents = (
        comp1Id: string,
        comp2Id: string
    ): string => {
        const claimA = getClaimForFeature(comp1Id)
        const claimB = getClaimForFeature(comp2Id)
        if (claimA === claimB) return `${claimA}`
        if (claimA === 1) return `${claimB}`
        if (claimB === 1) return `${claimA}`

        return `${claimB}`
    }

    const comp1IsInDifferentChildClaim = (
        comp1Id: string,
        comp2Id: string
    ): boolean => {
        const claimA = getClaimForFeature(comp1Id)
        const claimB = getClaimForFeature(comp2Id)
        return claimA !== claimB && claimA !== 1 && claimB !== 1
    }

    const getComp1ReintroductionClause = (
        comp1Id: string,
        comp2Id: string
    ): string => {
        if (!comp1IsInDifferentChildClaim(comp1Id, comp2Id)) return ''

        const article = extractIndefiniteArticle(comp1Id, formRef)
        const articlePrefix = article ? `${article} ` : ''

        if (comp1Id.includes('+=>')) {
            const [parentId, subId] = comp1Id.split('+=>')
            const primaryComponents = formRef.getFieldValue<
                MachineForm['primaryComponents']
            >(['primaryComponents'])
            const parent = primaryComponents?.find(
                (x: any) => x?.id === parentId
            )
            const subComponents =
                formRef.getFieldValue<
                    ValueOf<MachineForm['primaryComponentSubComponentsList']>
                >(['primaryComponentSubComponentsList', parentId]) ?? []
            const sub = subComponents.find((x: any) => x?.id === subId)
            const parentName = parent?.component ?? ''
            const subName = sub?.component ?? ''
            return `wherein the ${parentName} comprises: ${articlePrefix}${subName}; `
        }
        if (comp1Id.startsWith('add_')) {
            const additionalComponents = formRef.getFieldValue<
                MachineForm['primaryAdditionalComponents']
            >(['primaryAdditionalComponents'])
            const addComp = additionalComponents?.find(
                (x: any) => x?.id === comp1Id.substring(4)
            )
            const compName = addComp?.component ?? ''
            return `wherein the ${category} comprises: ${articlePrefix}${compName}; `
        }
        const primaryComponents = formRef.getFieldValue<
            MachineForm['primaryComponents']
        >(['primaryComponents'])
        const comp = primaryComponents?.find((x: any) => x?.id === comp1Id)
        const compName = comp?.component ?? ''
        return `wherein the ${category} comprises: ${articlePrefix}${compName}; `
    }

    /**
      Reusable claimBuild -> Splits a claim text string into TextRun[]  with line breaks inserted
      after every ':' and ';'
     */
    const buildClaimRuns = (text: string): TextRun[] => {
        const formatted = text.replace(/:\s+/g, ':\n').replace(/;\s+/g, ';\n')
        const segments = formatted.split('\n').filter((s) => s.trim() !== '')
        return segments.map(
            (segment, i) =>
                new TextRun({
                    text: segment,
                    break: i === 0 ? 0 : 1,
                    color: '000000',
                    size: 16,
                })
        )
    }

    headingArr.push(
        new Paragraph({
            heading: HeadingLevel.HEADING_1,
            alignment: AlignmentType.CENTER,
            spacing: {
                after: 250,
            },
            children: [
                new TextRun({
                    text: 'APPLICATION FOR UNITED STATES LETTERS PATENT',
                    color: '000000',
                    size: 16,
                }),
            ],
        })
    )

    headingArr.push(
        new Paragraph({
            heading: HeadingLevel.HEADING_1,
            alignment: AlignmentType.CENTER,
            spacing: {
                after: 250,
            },
            children: [
                new TextRun({
                    text: 'FOR',
                    color: '000000',
                    size: 16,
                }),
            ],
        })
    )
    let counter = 0

    const documentOrder = [
        SharedNames.titleComponent,
        SharedNames.backgroundInvention,
        'abstractOfTheDisclosure', // This key triggers the "Summary" section generation
        SharedNames.drawingsDescription,
        MachineNames.detailedDescription,
    ]

    const sortedQuestions = [...activeQuestions].sort((a, b) => {
        const indexA = documentOrder.indexOf(a)
        const indexB = documentOrder.indexOf(b)

        if (indexA !== -1 && indexB !== -1) return indexA - indexB
        if (indexA !== -1) return -1

        if (indexB !== -1) return 1
        return 0
    })
    // let briefDrawingsCounter = 0
    sortedQuestions.forEach((question) => {
        if (question === SharedNames.titleComponent) {
            titleArr = []
            const data = formRef.getFieldValue<FormValues['titleComponent']>([
                'titleComponent',
            ])
            if (data?.title) {
                titleArr.push(
                    new Paragraph({
                        heading: HeadingLevel.HEADING_1,
                        alignment: AlignmentType.CENTER,
                        spacing: {
                            after: 250,
                        },
                        children: [
                            new TextRun({
                                text: data.title.toUpperCase(),
                                color: '000000',
                                size: 16,
                                // bold: true,
                            }),
                        ],
                    })
                )
            }
            if (data?.author) {
                const filteredAuthors = data.author.filter(
                    (authorItem) => authorItem?.author !== undefined
                )
                if (filteredAuthors.length > 0) {
                    titleArr.push(
                        new Paragraph({
                            heading: HeadingLevel.HEADING_1,
                            alignment: AlignmentType.CENTER,
                            children: [
                                new TextRun({
                                    text: 'BY:',
                                    color: '000000',
                                    size: 16,
                                }),
                            ],
                        })
                    )
                    filteredAuthors.forEach((authorItem) => {
                        // authorsCount += 1
                        titleArr.push(
                            new Paragraph({
                                heading: HeadingLevel.HEADING_1,
                                alignment: AlignmentType.CENTER,
                                children: [
                                    new TextRun({
                                        text: authorItem.author,
                                        color: '000000',
                                        size: 16,
                                    }),
                                ],
                            })
                        )
                    })
                }
            }
        }
        if (question === SharedNames.drawingsDescription) {
            drawingsDescriptionArr = []
            drawingsDescriptionArr.push(
                new Paragraph({
                    heading: HeadingLevel.HEADING_1,
                    alignment: AlignmentType.CENTER,
                    spacing: {
                        before: 250,
                        after: 250,
                    },
                    children: [
                        new TextRun({
                            text: 'BRIEF DESCRIPTION OF THE DRAWINGS',
                            color: '000000',
                            size: 16,
                            // bold: true,
                            underline: {
                                type: UnderlineType.SINGLE,
                                color: '000000',
                            },
                        }),
                    ],
                })
            )
            const figure = formRef.getFieldValue<any>([
                'drawingsDescription',
                'figure',
            ])
            if (figure) {
                interface IValue {
                    illustrationType?: keyof typeof ILLUSTRATION_TYPE
                    figureType?: keyof typeof FIGURE_TYPE
                    figureElements?: string[]
                    figureDescription: string
                }
                Object.entries(figure).forEach((entity, i: number) => {
                    const [, entityVal] = entity
                    const val = entityVal as IValue
                    const { illustrationType, figureType, figureDescription } =
                        val
                    counter++
                    const text = `depicts a ${
                        illustrationType
                            ? ILLUSTRATION_TYPE[illustrationType]
                            : '__'
                    } of ${figureType ? FIGURE_TYPE[figureType] : '__'} of ${
                        figureDescription || '__'
                    }.`
                    drawingsDescriptionArr.push(
                        new Paragraph({
                            heading: HeadingLevel.HEADING_6,
                            alignment: AlignmentType.LEFT,
                            ...(isDownload
                                ? {
                                      numbering: {
                                          reference: 'my-reference',
                                          level: 0,
                                      },
                                  }
                                : {}),
                            children: [
                                ...(!isDownload
                                    ? [
                                          new TextRun({
                                              text: `[${counter}]`,
                                              color: '000000',
                                              size: 16,
                                          }),
                                      ]
                                    : []),
                                new TextRun({
                                    text: ` Figure ${i + 1} `,
                                    color: '000000',
                                    size: 16,
                                }),
                                new TextRun({
                                    text,
                                    color: '000000',
                                    size: 16,
                                }),
                            ],
                        })
                    )
                })
            }
        }

        if (question === SharedNames.backgroundInvention) {
            backgroundInventionArr = []
            const months = [
                'January',
                'February',
                'March',
                'April',
                'May',
                'June',
                'July',
                'August',
                'September',
                'October',
                'November',
                'December',
            ]
            let numbersText = `This application claims priority to `
            const title = formRef.getFieldValue<
                FormValues['titleComponent']['title']
            >(['titleComponent', 'title'])
            const {
                notSet,
                nonprovisionalNumbers,
                provisionalNumbers,
                customCharacteristic: customFailToProvide,
                // isCommunicated,
                isIncorporated,
                instruction,
            } = formRef.getFieldValue<FormValues['backgroundInvention']>([
                'backgroundInvention',
            ]) ?? {}
            // if (!isCommunicated) numbersText = 'N/A '
            if (isIncorporated) {
                numbersText += instruction || ''
            } else {
                numbersText = 'N/A'
            }
            if (nonprovisionalNumbers && nonprovisionalNumbers.length > 0) {
                numbersText += 'U.S. Nonprovisional Patent Application '
                nonprovisionalNumbers.forEach((item) => {
                    if (item) {
                        const nonprovisional = item.number

                        let formatedNonProvisionalDate = ''

                        const nonprovisionalDate = item.date
                        if (nonprovisionalDate) {
                            const tempDate = new Date(nonprovisionalDate)
                            formatedNonProvisionalDate = `${
                                months[tempDate.getMonth()]
                            } ${tempDate.getDate()} ${tempDate.getFullYear()}`
                        }
                        numbersText += `No.${nonprovisional}, filed on ${formatedNonProvisionalDate}, `
                    }
                })
                numbersText += `entitled "${title}" `
            }
            if (provisionalNumbers && provisionalNumbers.length > 0) {
                if (nonprovisionalNumbers) {
                    numbersText += `which claims priority to `
                }
                numbersText += 'U.S. Provisional Patent Application '
                provisionalNumbers.forEach((item) => {
                    if (item) {
                        const provisional = item.number

                        let formatedProvisionalDate = ''

                        const provisionalDate = item.date
                        if (provisionalDate) {
                            const tempDate = new Date(provisionalDate)
                            formatedProvisionalDate = `${
                                months[tempDate.getMonth()]
                            } ${tempDate.getDate()} ${tempDate.getFullYear()}`
                        }
                        numbersText += `No.${provisional}, filed on ${formatedProvisionalDate}, `
                    }
                })
                numbersText += `entitled "${title}"`
            }

            backgroundInventionArr.push(
                new Paragraph({
                    heading: HeadingLevel.HEADING_1,
                    alignment: AlignmentType.CENTER,
                    spacing: {
                        before: 250,
                        after: 250,
                    },
                    children: [
                        new TextRun({
                            // text: 'BACKGROUND OF THE INVENTION',
                            text: 'BACKGROUND',
                            color: '000000',
                            size: 16,
                            // bold: true,
                            underline: {
                                type: UnderlineType.SINGLE,
                                color: '000000',
                            },
                        }),
                    ],
                })
            )
            counter++
            backgroundInventionArr.push(
                new Paragraph({
                    heading: HeadingLevel.HEADING_6,
                    alignment: AlignmentType.LEFT,
                    children: [
                        new TextRun({
                            text: 'Cross-Reference and Related Applications',
                            color: '000000',
                            size: 16,
                            underline: {
                                type: UnderlineType.SINGLE,
                                color: '000000',
                            },
                        }),
                    ],
                })
            )
            if (!notSet) {
                backgroundInventionArr.push(
                    new Paragraph({
                        heading: HeadingLevel.HEADING_6,
                        alignment: AlignmentType.LEFT,
                        ...(isDownload
                            ? {
                                  numbering: {
                                      reference: 'my-reference',
                                      level: 0,
                                  },
                              }
                            : {}),
                        children: [
                            ...(!isDownload
                                ? [
                                      new TextRun({
                                          text: `[${counter}]    `,
                                          color: '000000',
                                          size: 16,
                                          // bold: true,
                                      }),
                                  ]
                                : []),
                            new TextRun({
                                text: numbersText,
                                color: '000000',
                                size: 16,
                            }),
                        ],
                    })
                )
            } else {
                backgroundInventionArr.push(
                    new Paragraph({
                        heading: HeadingLevel.HEADING_6,
                        alignment: AlignmentType.LEFT,
                        ...(isDownload
                            ? {
                                  numbering: {
                                      reference: 'my-reference',
                                      level: 0,
                                  },
                              }
                            : {}),
                        children: [
                            ...(!isDownload
                                ? [
                                      new TextRun({
                                          text: `[${counter}]    `,
                                          color: '000000',
                                          size: 16,
                                          // bold: true,
                                      }),
                                  ]
                                : []),
                            new TextRun({
                                text: 'Not applicable.',
                                color: '000000',
                                size: 16,
                            }),
                        ],
                    })
                )
            }
            backgroundInventionArr.push(
                new Paragraph({
                    heading: HeadingLevel.HEADING_6,
                    alignment: AlignmentType.LEFT,
                    children: [
                        new TextRun({
                            text: 'TECHNICAL FIELD',
                            color: '000000',
                            size: 16,
                            underline: {
                                type: UnderlineType.SINGLE,
                                color: '000000',
                            },
                        }),
                    ],
                })
            )
            counter++
            backgroundInventionArr.push(
                new Paragraph({
                    heading: HeadingLevel.HEADING_6,
                    alignment: AlignmentType.LEFT,
                    ...(isDownload
                        ? {
                              numbering: {
                                  reference: 'my-reference',
                                  level: 0,
                              },
                          }
                        : {}),
                    children: [
                        ...(!isDownload
                            ? [
                                  new TextRun({
                                      text: `[${counter}]    `,
                                      color: '000000',
                                      size: 16,
                                  }),
                              ]
                            : []),
                        new TextRun({
                            text: `This disclosure relates to a ${title.toLowerCase()}.`,
                            color: '000000',
                            size: 16,
                        }),
                    ],
                })
            )

            if (customFailToProvide) {
                backgroundInventionArr.push(
                    new Paragraph({
                        heading: HeadingLevel.HEADING_6,
                        alignment: AlignmentType.LEFT,
                        children: [
                            new TextRun({
                                text: `BACKGROUND SECTION`,
                                color: '000000',
                                size: 16,
                                // bold: true,
                            }),
                        ],
                    })
                )
                customFailToProvide.forEach((item) => {
                    counter++
                    backgroundInventionArr.push(
                        new Paragraph({
                            heading: HeadingLevel.HEADING_6,
                            alignment: AlignmentType.LEFT,
                            ...(isDownload
                                ? {
                                      numbering: {
                                          reference: 'my-reference',
                                          level: 0,
                                      },
                                  }
                                : {}),
                            children: [
                                ...(!isDownload
                                    ? [
                                          new TextRun({
                                              text: `[${counter}]    `,
                                              color: '000000',
                                              size: 16,
                                          }),
                                      ]
                                    : []),
                                new TextRun({
                                    text: `Existing ${category}s fail to provide ${item}.`,
                                    color: '000000',
                                    size: 16,
                                    // bold: true,
                                }),
                            ],
                        })
                    )
                })
            }
        }

        if (
            question === MachineNames.detailedDescription &&
            isMachineForm(formRef, question)
        ) {
            detailedDescriptionArr = []
            const { customCharacteristic: failToProvide } =
                (formRef.getFieldValue([
                    SharedNames.backgroundInvention,
                ]) as FormValues['backgroundInvention']) ?? {}

            const describeDisclosure = formRef.getFieldValue<
                MachineForm['detailedDescription']
            >([MachineNames.detailedDescription, 'describe-disclosure'])
            detailedDescriptionArr.push(
                new Paragraph({
                    heading: HeadingLevel.HEADING_1,
                    alignment: AlignmentType.CENTER,
                    spacing: {
                        before: 250,
                        after: 250,
                    },
                    children: [
                        new TextRun({
                            text: `DETAILED DESCRIPTION`,
                            color: '000000',
                            size: 16,
                            // bold: true,
                            underline: {
                                type: UnderlineType.SINGLE,
                                color: '000000',
                            },
                        }),
                    ],
                })
            )

            counter++
            detailedDescriptionArr.push(
                new Paragraph({
                    heading: HeadingLevel.HEADING_6,
                    alignment: AlignmentType.LEFT,
                    ...(isDownload
                        ? {
                              numbering: {
                                  reference: 'my-reference',
                                  level: 0,
                              },
                          }
                        : {}),
                    children: [
                        ...(!isDownload
                            ? [
                                  new TextRun({
                                      text: `[${counter}]    `,
                                      color: '000000',
                                      size: 16,
                                  }),
                              ]
                            : []),
                        new TextRun({
                            text: `Many examples of the ${category} of the present disclosure are provided throughout this disclosure.  Modifications, including, for example, structural changes, substitutions, and rearrangements, can be made to the examples provided in this disclosure without departing from the scope of this disclosure.  Some aspects of the ${category} or any combination thereof according to this disclosure will now be further described with reference being made to the accompanying figure or figures which form a part hereof and which provide illustrative examples.  Unless otherwise indicated, each identical or substantially similar element in the figures uses the same reference number.  However, to avoid unnecessarily cluttering the figures and causing distraction, the reference numbers for the elements are not repeated on every figure.`,
                            color: '000000',
                            size: 16,
                        }),
                    ],
                })
            )
            counter++
            detailedDescriptionArr.push(
                new Paragraph({
                    heading: HeadingLevel.HEADING_6,
                    alignment: AlignmentType.LEFT,
                    ...(isDownload
                        ? {
                              numbering: {
                                  reference: 'my-reference',
                                  level: 0,
                              },
                          }
                        : {}),
                    children: [
                        ...(!isDownload
                            ? [
                                  new TextRun({
                                      text: `[${counter}]    `,
                                      color: '000000',
                                      size: 16,
                                  }),
                              ]
                            : []),
                        new TextRun({
                            text: `As will be seen in greater detail throughout this disclosure, some embodiments of the ${category} described in this disclosure can be provided with one or any combination of the following, non-limiting advantages.`,
                            color: '000000',
                            size: 16,
                        }),
                    ],
                })
            )
            failToProvide?.forEach((item) => {
                counter++
                detailedDescriptionArr.push(
                    new Paragraph({
                        heading: HeadingLevel.HEADING_6,
                        alignment: AlignmentType.LEFT,
                        ...(isDownload
                            ? {
                                  numbering: {
                                      reference: 'my-reference',
                                      level: 0,
                                  },
                              }
                            : {}),
                        children: [
                            ...(!isDownload
                                ? [
                                      new TextRun({
                                          text: `[${counter}]    `,
                                          color: '000000',
                                          size: 16,
                                      }),
                                  ]
                                : []),
                            new TextRun({
                                text: `Some embodiments of the ${category}s described in this disclosure are capable of providing ${
                                    item || '__'
                                }.`,
                                color: '000000',
                                size: 16,
                            }),
                        ],
                    })
                )
            })

            // modified for ix-82 modification:

            counter++
            detailedDescriptionArr.push(
                new Paragraph({
                    heading: HeadingLevel.HEADING_6,
                    alignment: AlignmentType.LEFT,
                    ...(isDownload
                        ? {
                              numbering: {
                                  reference: 'my-reference',
                                  level: 0,
                              },
                          }
                        : {}),
                    children: [
                        ...(!isDownload
                            ? [
                                  new TextRun({
                                      text: `[${counter}]    `,
                                      color: '000000',
                                      size: 16,
                                  }),
                              ]
                            : []),
                        new TextRun({
                            text: `Of course, other advantages of the ${category} described in this disclosure will be evident to a skilled person reading this disclosure.  Thus, the listing of advantages provided in this disclosure is illustrative and not limiting.  Accordingly, while some embodiments of the ${category} described herein can be provided with one of the advantages described herein or any combination of the advantages described herein, additional embodiments of the ${category} described herein are not required to provide all or any of the advantages described herein.`,
                            color: '000000',
                            size: 16,
                        }),
                    ],
                })
            )
            if (figures && pushFigures) {
                const figuresNumber =
                    figures.length > 1 ? `1-${figures.length}` : '1'
                pushFigures = false
                const normalizedClaims = [
                    {
                        uid: claims.id as string | undefined,
                        id: claims.id,
                        number: claims.number,
                        name: claims.name,
                    },
                ]
                claims.components.forEach((claim) => {
                    normalizedClaims.push({
                        uid: claim.uid,
                        id: claim.id,
                        number: claim.number,
                        name: claim.name,
                    })
                    if (isClaim(claim)) {
                        claim.components.forEach((component) => {
                            normalizedClaims.push({
                                uid: component.uid,
                                id: component.id,
                                number: component.number,
                                name: component.name,
                            })
                        })
                    }
                })
                interface INormalizedFigure {
                    uid: string | undefined
                    number: string
                    name: string
                }
                const normalizedFigures: INormalizedFigure[][] = []

                figures.forEach((figure) => {
                    const claimsToSearch: string[] = []
                    figure.claims.forEach((claimStruct) => {
                        claimStruct.components.forEach((component) => {
                            claimsToSearch.push(component)
                        })
                        if (claimStruct.claim) {
                            const findClaim = claimsToSearch.filter((claim) => {
                                return claim === claimStruct.claim
                            })
                            if (!findClaim.length) {
                                claimsToSearch.push(claimStruct.claim)
                            }
                        }
                    })
                    const normalizedFigure: INormalizedFigure[] = []
                    claimsToSearch.forEach((claimToSearch) => {
                        const foundClaim = normalizedClaims.find(
                            (normalizedClaim) =>
                                normalizedClaim.id === claimToSearch
                        )
                        if (foundClaim) {
                            normalizedFigure.push({
                                uid: foundClaim.uid,
                                number: foundClaim.number,
                                name: foundClaim.name,
                            })
                        }
                    })
                    if (normalizedFigure.length > 0) {
                        normalizedFigures.push(normalizedFigure)
                    }
                })
                if (normalizedFigures.length !== 0) {
                    counter++
                    detailedDescriptionArr.push(
                        new Paragraph({
                            heading: HeadingLevel.HEADING_6,
                            alignment: AlignmentType.LEFT,
                            ...(isDownload
                                ? {
                                      numbering: {
                                          reference: 'my-reference',
                                          level: 0,
                                      },
                                  }
                                : {}),
                            children: [
                                ...(!isDownload
                                    ? [
                                          new TextRun({
                                              text: `[${counter}]    `,
                                              color: '000000',
                                              size: 16,
                                          }),
                                      ]
                                    : []),
                                new TextRun({
                                    text: `For ease of comprehending how the ${category} can be employed, an example of a  ${category} ${normalizedClaims[0].number}   will now be described with reference to FIGURES ${figuresNumber}.`,
                                    color: '000000',
                                    size: 16,
                                }),
                            ],
                        })
                    )
                }
                // WORK ON NORMALIZED FIGURES. IT'S AN ARR OF OBJECTS WITH NAME AND NUMBER OF CLAIM
                normalizedFigures.forEach((normalizedFigure, i) => {
                    counter++
                    const title = formRef.getFieldValue<
                        FormValues['titleComponent']['title']
                    >(['titleComponent', 'title'])

                    const currentFigure = figures?.[i]
                    const figureDataArr = currentFigure
                        ? Object.entries(currentFigure)
                        : []

                    //  Use the title as the default description, fallback to category if title is missing
                    let figureDescription = title
                        ? title.toLowerCase()
                        : `${category}`

                    if (figureDataArr.length > 0) {
                        const [, figVal] = figureDataArr as any
                        // Only overwrite if a specific description exists and is not empty
                        if (figVal?.figureDescription) {
                            figureDescription = figVal.figureDescription
                        }
                    }

                    detailedDescriptionArr.push(
                        new Paragraph({
                            heading: HeadingLevel.HEADING_6,
                            alignment: AlignmentType.LEFT,
                            ...(isDownload
                                ? {
                                      numbering: {
                                          reference: 'my-reference',
                                          level: 0,
                                      },
                                  }
                                : {}),
                            children: [
                                ...(!isDownload
                                    ? [
                                          new TextRun({
                                              text: `[${counter}]    `,
                                              color: '000000',
                                              size: 16,
                                          }),
                                      ]
                                    : []),
                                new TextRun({
                                    text: `With reference to Figure ${
                                        i + 1
                                    }, a ${figureDescription} is illustrated. The ${category} can comprise several components.`,
                                    color: '000000',
                                    size: 16,
                                }),
                            ],
                        })
                    )
                    normalizedFigure.forEach((element) => {
                        // newArr.push(
                        //     new Paragraph({
                        //         text: `[${counter}]      Figure ${
                        //             i + 1
                        //         } contains ${normalizedFigure.map(
                        //             ({ number, name }) => ` [${name} ${number}]`
                        //         )}`,
                        //         heading: HeadingLevel.HEADING_6,
                        //         alignment: AlignmentType.LEFT,
                        //     })
                        // )
                        const primaryComponents = formRef.getFieldValue<
                            MachineForm['primaryComponents']
                        >(['primaryComponents'])
                        const foundComponent = primaryComponents.filter(
                            (component) => {
                                return element.uid === component.id
                            }
                        )
                        if (foundComponent.length) {
                            counter++
                            detailedDescriptionArr.push(
                                new Paragraph({
                                    heading: HeadingLevel.HEADING_6,
                                    alignment: AlignmentType.LEFT,
                                    ...(isDownload
                                        ? {
                                              numbering: {
                                                  reference: 'my-reference',
                                                  level: 0,
                                              },
                                          }
                                        : {}),
                                    children: [
                                        ...(!isDownload
                                            ? [
                                                  new TextRun({
                                                      text: `[${counter}]    `,
                                                      color: '000000',
                                                      size: 16,
                                                  }),
                                              ]
                                            : []),
                                        new TextRun({
                                            text: `In some embodiments of the ${category} ${normalizedClaims[0].number}, the ${category} ${normalizedClaims[0].number} comprises ${foundComponent[0].indefiniteArticle} ${element.name} ${element.number}.`,
                                            color: '000000',
                                            size: 16,
                                        }),
                                    ],
                                })
                            )
                        } else if (
                            typeof element.uid === 'string' &&
                            element.uid.startsWith('add_')
                        ) {
                            // Additional component (e.g. "roof") — look it up in
                            // primaryAdditionalComponents using the id without the add_ prefix
                            const additionalComponents =
                                formRef.getFieldValue<
                                    MachineForm['primaryAdditionalComponents']
                                >(['primaryAdditionalComponents']) ?? []
                            const addId = element.uid.substring(4)
                            const foundAddComp = additionalComponents.find(
                                (comp) => comp?.id === addId
                            )
                            if (foundAddComp) {
                                counter++
                                detailedDescriptionArr.push(
                                    new Paragraph({
                                        heading: HeadingLevel.HEADING_6,
                                        alignment: AlignmentType.LEFT,
                                        ...(isDownload
                                            ? {
                                                  numbering: {
                                                      reference: 'my-reference',
                                                      level: 0,
                                                  },
                                              }
                                            : {}),
                                        children: [
                                            ...(!isDownload
                                                ? [
                                                      new TextRun({
                                                          text: `[${counter}]    `,
                                                          color: '000000',
                                                          size: 16,
                                                      }),
                                                  ]
                                                : []),
                                            new TextRun({
                                                text: `In some embodiments of the ${category} ${normalizedClaims[0].number}, the ${category} ${normalizedClaims[0].number} comprises ${foundAddComp.indefiniteArticle} ${element.name} ${element.number}.`,
                                                color: '000000',
                                                size: 16,
                                            }),
                                        ],
                                    })
                                )
                            }
                        }
                        const subComponents = formRef.getFieldValue([
                            MachineNames.primaryComponentSubComponentsList,
                            element.uid as string,
                        ]) as ValueOf<
                            MachineForm['primaryComponentSubComponentsList']
                        >
                        if (subComponents) {
                            subComponents.forEach((component) => {
                                const filteredSubComponents =
                                    normalizedFigure.filter((claimElement) => {
                                        return claimElement.uid === component.id
                                    })
                                if (filteredSubComponents.length > 0) {
                                    counter++
                                    detailedDescriptionArr.push(
                                        new Paragraph({
                                            heading: HeadingLevel.HEADING_6,
                                            alignment: AlignmentType.LEFT,
                                            ...(isDownload
                                                ? {
                                                      numbering: {
                                                          reference:
                                                              'my-reference',
                                                          level: 0,
                                                      },
                                                  }
                                                : {}),
                                            children: [
                                                ...(!isDownload
                                                    ? [
                                                          new TextRun({
                                                              text: `[${counter}]    `,
                                                              color: '000000',
                                                              size: 16,
                                                          }),
                                                      ]
                                                    : []),
                                                new TextRun({
                                                    text: `In some embodiments of the ${category} ${normalizedClaims[0].number}, wherein the ${element.name} ${element.number} comprises ${component.indefiniteArticle} ${filteredSubComponents[0].name} ${filteredSubComponents[0].number}.`,
                                                    color: '000000',
                                                    size: 16,
                                                }),
                                            ],
                                        })
                                    )
                                }
                            })
                        }
                        const characteristicComponents = element.uid
                            ? formRef.getFieldValue<any>([
                                  'characteristic',
                                  element.uid as string,
                              ])
                            : undefined
                        if (characteristicComponents) {
                            if (characteristicComponents.density) {
                                counter++
                                detailedDescriptionArr.push(
                                    new Paragraph({
                                        heading: HeadingLevel.HEADING_6,
                                        alignment: AlignmentType.LEFT,
                                        ...(isDownload
                                            ? {
                                                  numbering: {
                                                      reference: 'my-reference',
                                                      level: 0,
                                                  },
                                              }
                                            : {}),
                                        children: [
                                            ...(!isDownload
                                                ? [
                                                      new TextRun({
                                                          text: `[${counter}]    `,
                                                          color: '000000',
                                                          size: 16,
                                                      }),
                                                  ]
                                                : []),
                                            new TextRun({
                                                text: `In some embodiments of the ${category} ${
                                                    normalizedClaims[0].number
                                                }, the ${element.name} ${
                                                    element.number
                                                } has a density of ${
                                                    characteristicComponents
                                                        .density.from || '__'
                                                } to ${
                                                    characteristicComponents
                                                        .density.to || '__'
                                                } kg/m^3`,
                                                color: '000000',
                                                size: 16,
                                            }),
                                        ],
                                    })
                                )
                            }
                            if (characteristicComponents.weight) {
                                counter++
                                detailedDescriptionArr.push(
                                    new Paragraph({
                                        heading: HeadingLevel.HEADING_6,
                                        alignment: AlignmentType.LEFT,
                                        ...(isDownload
                                            ? {
                                                  numbering: {
                                                      reference: 'my-reference',
                                                      level: 0,
                                                  },
                                              }
                                            : {}),
                                        children: [
                                            ...(!isDownload
                                                ? [
                                                      new TextRun({
                                                          text: `[${counter}]    `,
                                                          color: '000000',
                                                          size: 16,
                                                      }),
                                                  ]
                                                : []),
                                            new TextRun({
                                                text: `In some embodiments of the ${category} ${
                                                    normalizedClaims[0].number
                                                }, the ${element.name} ${
                                                    element.number
                                                } has a weight of ${
                                                    characteristicComponents
                                                        .weight.from || '__'
                                                } to ${
                                                    characteristicComponents
                                                        .weight.to || '__'
                                                } kg.`,
                                                color: '000000',
                                                size: 16,
                                            }),
                                        ],
                                    })
                                )
                            }
                            if (characteristicComponents.length) {
                                counter++
                                detailedDescriptionArr.push(
                                    new Paragraph({
                                        heading: HeadingLevel.HEADING_6,
                                        alignment: AlignmentType.LEFT,
                                        ...(isDownload
                                            ? {
                                                  numbering: {
                                                      reference: 'my-reference',
                                                      level: 0,
                                                  },
                                              }
                                            : {}),
                                        children: [
                                            ...(!isDownload
                                                ? [
                                                      new TextRun({
                                                          text: `[${counter}]    `,
                                                          color: '000000',
                                                          size: 16,
                                                      }),
                                                  ]
                                                : []),
                                            new TextRun({
                                                text: `In some embodiments of the ${category} ${
                                                    normalizedClaims[0].number
                                                }, the ${element.name} ${
                                                    element.number
                                                } has a weight of ${
                                                    characteristicComponents
                                                        .length.from || '__'
                                                } to ${
                                                    characteristicComponents
                                                        .length.to || '__'
                                                } m.`,
                                                color: '000000',
                                                size: 16,
                                            }),
                                        ],
                                    })
                                )
                            }
                            if (characteristicComponents.thickness) {
                                counter++
                                detailedDescriptionArr.push(
                                    new Paragraph({
                                        heading: HeadingLevel.HEADING_6,
                                        alignment: AlignmentType.LEFT,
                                        ...(isDownload
                                            ? {
                                                  numbering: {
                                                      reference: 'my-reference',
                                                      level: 0,
                                                  },
                                              }
                                            : {}),
                                        children: [
                                            ...(!isDownload
                                                ? [
                                                      new TextRun({
                                                          text: `[${counter}]    `,
                                                          color: '000000',
                                                          size: 16,
                                                      }),
                                                  ]
                                                : []),
                                            new TextRun({
                                                text: `In some embodiments of the ${category} ${
                                                    normalizedClaims[0].number
                                                }, the ${element.name} ${
                                                    element.number
                                                } has a thickness of ${
                                                    characteristicComponents
                                                        .thickness.from || '__'
                                                } to ${
                                                    characteristicComponents
                                                        .thickness.to || '__'
                                                } m.`,
                                                color: '000000',
                                                size: 16,
                                            }),
                                        ],
                                    })
                                )
                            }
                            if (characteristicComponents.defined) {
                                counter++
                                detailedDescriptionArr.push(
                                    new Paragraph({
                                        heading: HeadingLevel.HEADING_6,
                                        alignment: AlignmentType.LEFT,
                                        ...(isDownload
                                            ? {
                                                  numbering: {
                                                      reference: 'my-reference',
                                                      level: 0,
                                                  },
                                              }
                                            : {}),
                                        children: [
                                            ...(!isDownload
                                                ? [
                                                      new TextRun({
                                                          text: `[${counter}]    `,
                                                          color: '000000',
                                                          size: 16,
                                                      }),
                                                  ]
                                                : []),
                                            new TextRun({
                                                text: `In some embodiments of the ${category} ${
                                                    normalizedClaims[0].number
                                                }, the ${element.name} ${
                                                    element.number
                                                } ${
                                                    characteristicComponents
                                                        .defined.description ||
                                                    '__'
                                                } m.`,
                                                color: '000000',
                                                size: 16,
                                            }),
                                        ],
                                    })
                                )
                            }
                        }

                        const subcharacteristicComponent =
                            formRef.getFieldValue<
                                ValueOf<MachineValues['subCharacteristic']>
                            >(['subCharacteristic', element.uid as string])
                        if (subcharacteristicComponent) {
                            if (subcharacteristicComponent.density) {
                                counter++
                                detailedDescriptionArr.push(
                                    new Paragraph({
                                        heading: HeadingLevel.HEADING_6,
                                        alignment: AlignmentType.LEFT,
                                        ...(isDownload
                                            ? {
                                                  numbering: {
                                                      reference: 'my-reference',
                                                      level: 0,
                                                  },
                                              }
                                            : {}),
                                        children: [
                                            ...(!isDownload
                                                ? [
                                                      new TextRun({
                                                          text: `[${counter}]    `,
                                                          color: '000000',
                                                          size: 16,
                                                      }),
                                                  ]
                                                : []),
                                            new TextRun({
                                                text: `In some embodiments of the ${category} ${
                                                    normalizedClaims[0].number
                                                }, the ${element.name} ${
                                                    element.number
                                                } has a density of ${
                                                    subcharacteristicComponent
                                                        .density.from || '__'
                                                } to ${
                                                    subcharacteristicComponent
                                                        .density.to || '__'
                                                } kg/m^3.`,
                                                color: '000000',
                                                size: 16,
                                            }),
                                        ],
                                    })
                                )
                            }
                            if (subcharacteristicComponent.weight) {
                                counter++
                                detailedDescriptionArr.push(
                                    new Paragraph({
                                        heading: HeadingLevel.HEADING_6,
                                        alignment: AlignmentType.LEFT,
                                        ...(isDownload
                                            ? {
                                                  numbering: {
                                                      reference: 'my-reference',
                                                      level: 0,
                                                  },
                                              }
                                            : {}),
                                        children: [
                                            ...(!isDownload
                                                ? [
                                                      new TextRun({
                                                          text: `[${counter}]    `,
                                                          color: '000000',
                                                          size: 16,
                                                      }),
                                                  ]
                                                : []),
                                            new TextRun({
                                                text: `In some embodiments of the ${category} ${
                                                    normalizedClaims[0].number
                                                }, the ${element.name} ${
                                                    element.number
                                                } has a weight of ${
                                                    subcharacteristicComponent
                                                        .weight.from || '__'
                                                } to ${
                                                    subcharacteristicComponent
                                                        .weight.to || '__'
                                                } kg.`,
                                                color: '000000',
                                                size: 16,
                                            }),
                                        ],
                                    })
                                )
                            }
                            if (subcharacteristicComponent.length) {
                                counter++
                                detailedDescriptionArr.push(
                                    new Paragraph({
                                        heading: HeadingLevel.HEADING_6,
                                        alignment: AlignmentType.LEFT,
                                        ...(isDownload
                                            ? {
                                                  numbering: {
                                                      reference: 'my-reference',
                                                      level: 0,
                                                  },
                                              }
                                            : {}),
                                        children: [
                                            ...(!isDownload
                                                ? [
                                                      new TextRun({
                                                          text: `[${counter}]    `,
                                                          color: '000000',
                                                          size: 16,
                                                      }),
                                                  ]
                                                : []),
                                            new TextRun({
                                                text: `In some embodiments of the ${category} ${
                                                    normalizedClaims[0].number
                                                }, the ${element.name} ${
                                                    element.number
                                                } has a weight of ${
                                                    subcharacteristicComponent
                                                        .length.from || '__'
                                                } to ${
                                                    subcharacteristicComponent
                                                        .length.to || '__'
                                                } m.`,
                                                color: '000000',
                                                size: 16,
                                            }),
                                        ],
                                    })
                                )
                            }
                            if (subcharacteristicComponent.thickness) {
                                counter++
                                detailedDescriptionArr.push(
                                    new Paragraph({
                                        heading: HeadingLevel.HEADING_6,
                                        alignment: AlignmentType.LEFT,
                                        ...(isDownload
                                            ? {
                                                  numbering: {
                                                      reference: 'my-reference',
                                                      level: 0,
                                                  },
                                              }
                                            : {}),
                                        children: [
                                            ...(!isDownload
                                                ? [
                                                      new TextRun({
                                                          text: `[${counter}]    `,
                                                          color: '000000',
                                                          size: 16,
                                                      }),
                                                  ]
                                                : []),
                                            new TextRun({
                                                text: `In some embodiments of the ${category} ${
                                                    normalizedClaims[0].number
                                                }, the ${element.name} ${
                                                    element.number
                                                } has a thickness of ${
                                                    subcharacteristicComponent
                                                        .thickness.from || '__'
                                                } to ${
                                                    subcharacteristicComponent
                                                        .thickness.to || '__'
                                                } m.`,
                                                color: '000000',
                                                size: 16,
                                            }),
                                        ],
                                    })
                                )
                            }
                            if (subcharacteristicComponent.defined) {
                                counter++
                                detailedDescriptionArr.push(
                                    new Paragraph({
                                        heading: HeadingLevel.HEADING_6,
                                        alignment: AlignmentType.LEFT,
                                        ...(isDownload
                                            ? {
                                                  numbering: {
                                                      reference: 'my-reference',
                                                      level: 0,
                                                  },
                                              }
                                            : {}),
                                        children: [
                                            ...(!isDownload
                                                ? [
                                                      new TextRun({
                                                          text: `[${counter}]    `,
                                                          color: '000000',
                                                          size: 16,
                                                      }),
                                                  ]
                                                : []),
                                            new TextRun({
                                                text: `In some embodiments of the ${category} ${
                                                    normalizedClaims[0].number
                                                }, the ${element.name} ${
                                                    element.number
                                                } ${
                                                    subcharacteristicComponent
                                                        .defined.description ||
                                                    '__'
                                                } m.`,
                                                color: '000000',
                                                size: 16,
                                            }),
                                        ],
                                    })
                                )
                            }
                        }

                        const additionalCharacteristic =
                            formRef.getFieldValue<AdditionalCharacteristics>([
                                'additionalCharacteristic',
                                element.uid as string,
                            ])
                        if (additionalCharacteristic) {
                            if (additionalCharacteristic.thermalConductivity) {
                                counter++
                                detailedDescriptionArr.push(
                                    new Paragraph({
                                        heading: HeadingLevel.HEADING_6,
                                        alignment: AlignmentType.LEFT,
                                        ...(isDownload
                                            ? {
                                                  numbering: {
                                                      reference: 'my-reference',
                                                      level: 0,
                                                  },
                                              }
                                            : {}),
                                        children: [
                                            ...(!isDownload
                                                ? [
                                                      new TextRun({
                                                          text: `[${counter}]    `,
                                                          color: '000000',
                                                          size: 16,
                                                      }),
                                                  ]
                                                : []),
                                            new TextRun({
                                                text: `In some embodiments of the ${category} ${
                                                    normalizedClaims[0].number
                                                }, the ${element.name} ${
                                                    element.number
                                                } has a thermal conductivity of ${
                                                    (
                                                        additionalCharacteristic.thermalConductivity as Range
                                                    ).from || '__'
                                                } to ${
                                                    (
                                                        additionalCharacteristic.thermalConductivity as Range
                                                    ).to || '__'
                                                } W/(cm °C).`,
                                                color: '000000',
                                                size: 16,
                                            }),
                                        ],
                                    })
                                )
                            }
                            if (additionalCharacteristic.meltingPoint) {
                                counter++
                                detailedDescriptionArr.push(
                                    new Paragraph({
                                        heading: HeadingLevel.HEADING_6,
                                        alignment: AlignmentType.LEFT,
                                        ...(isDownload
                                            ? {
                                                  numbering: {
                                                      reference: 'my-reference',
                                                      level: 0,
                                                  },
                                              }
                                            : {}),
                                        children: [
                                            ...(!isDownload
                                                ? [
                                                      new TextRun({
                                                          text: `[${counter}]    `,
                                                          color: '000000',
                                                          size: 16,
                                                      }),
                                                  ]
                                                : []),
                                            new TextRun({
                                                text: `In some embodiments of the ${category} ${
                                                    normalizedClaims[0].number
                                                }, the ${element.name} ${
                                                    element.number
                                                } has a melting point of ${
                                                    (
                                                        additionalCharacteristic.meltingPoint as Range
                                                    ).from || '__'
                                                } to ${
                                                    (
                                                        additionalCharacteristic.meltingPoint as Range
                                                    ).to || '__'
                                                } °C.`,
                                                color: '000000',
                                                size: 16,
                                            }),
                                        ],
                                    })
                                )
                            }
                            if (additionalCharacteristic.specificHeat) {
                                counter++
                                detailedDescriptionArr.push(
                                    new Paragraph({
                                        heading: HeadingLevel.HEADING_6,
                                        alignment: AlignmentType.LEFT,
                                        ...(isDownload
                                            ? {
                                                  numbering: {
                                                      reference: 'my-reference',
                                                      level: 0,
                                                  },
                                              }
                                            : {}),
                                        children: [
                                            ...(!isDownload
                                                ? [
                                                      new TextRun({
                                                          text: `[${counter}]    `,
                                                          color: '000000',
                                                          size: 16,
                                                      }),
                                                  ]
                                                : []),
                                            new TextRun({
                                                text: `In some embodiments of the ${category} ${
                                                    normalizedClaims[0].number
                                                }, the ${element.name} ${
                                                    element.number
                                                } has a specific heat of ${
                                                    (
                                                        additionalCharacteristic.specificHeat as Range
                                                    ).from || '__'
                                                } to ${
                                                    (
                                                        additionalCharacteristic.specificHeat as Range
                                                    ).to || '__'
                                                } J/kg°C.`,
                                                color: '000000',
                                                size: 16,
                                            }),
                                        ],
                                    })
                                )
                            }
                            if (additionalCharacteristic.defined) {
                                counter++
                                detailedDescriptionArr.push(
                                    new Paragraph({
                                        heading: HeadingLevel.HEADING_6,
                                        alignment: AlignmentType.LEFT,
                                        ...(isDownload
                                            ? {
                                                  numbering: {
                                                      reference: 'my-reference',
                                                      level: 0,
                                                  },
                                              }
                                            : {}),
                                        children: [
                                            ...(!isDownload
                                                ? [
                                                      new TextRun({
                                                          text: `[${counter}]    `,
                                                          color: '000000',
                                                          size: 16,
                                                      }),
                                                  ]
                                                : []),
                                            new TextRun({
                                                text: `In some embodiments of the ${category} ${
                                                    normalizedClaims[0].number
                                                }, the ${element.name} ${
                                                    element.number
                                                } ${
                                                    additionalCharacteristic
                                                        .defined.description ||
                                                    '__'
                                                }.`,
                                                color: '000000',
                                                size: 16,
                                            }),
                                        ],
                                    })
                                )
                            }
                        }

                        const subadditionalCharacteristic =
                            formRef.getFieldValue<
                                MachineValues['subAdditionalCharacteristic']
                            >([
                                'subAdditionalCharacteristic',
                                element.uid as string,
                            ])

                        if (subadditionalCharacteristic) {
                            if (
                                subadditionalCharacteristic.thermalConductivity
                            ) {
                                counter++
                                detailedDescriptionArr.push(
                                    new Paragraph({
                                        heading: HeadingLevel.HEADING_6,
                                        alignment: AlignmentType.LEFT,
                                        ...(isDownload
                                            ? {
                                                  numbering: {
                                                      reference: 'my-reference',
                                                      level: 0,
                                                  },
                                              }
                                            : {}),
                                        children: [
                                            ...(!isDownload
                                                ? [
                                                      new TextRun({
                                                          text: `[${counter}]    `,
                                                          color: '000000',
                                                          size: 16,
                                                      }),
                                                  ]
                                                : []),
                                            new TextRun({
                                                text: `In some embodiments of the ${category} ${
                                                    normalizedClaims[0].number
                                                }, the ${element.name} ${
                                                    element.number
                                                } has a thermal conductivity of ${
                                                    (
                                                        subadditionalCharacteristic.thermalConductivity as Range
                                                    ).from || '__'
                                                } to ${
                                                    (
                                                        subadditionalCharacteristic.thermalConductivity as Range
                                                    ).to || '__'
                                                } W/(cm °C).`,
                                                color: '000000',
                                                size: 16,
                                            }),
                                        ],
                                    })
                                )
                            }
                            if (subadditionalCharacteristic.meltingPoint) {
                                counter++
                                detailedDescriptionArr.push(
                                    new Paragraph({
                                        heading: HeadingLevel.HEADING_6,
                                        alignment: AlignmentType.LEFT,
                                        ...(isDownload
                                            ? {
                                                  numbering: {
                                                      reference: 'my-reference',
                                                      level: 0,
                                                  },
                                              }
                                            : {}),
                                        children: [
                                            ...(!isDownload
                                                ? [
                                                      new TextRun({
                                                          text: `[${counter}]    `,
                                                          color: '000000',
                                                          size: 16,
                                                      }),
                                                  ]
                                                : []),
                                            new TextRun({
                                                text: `In some embodiments of the ${category} ${
                                                    normalizedClaims[0].number
                                                }, the ${element.name} ${
                                                    element.number
                                                } has a melting point of ${
                                                    (
                                                        subadditionalCharacteristic.meltingPoint as Range
                                                    ).from || '__'
                                                } to ${
                                                    (
                                                        subadditionalCharacteristic.meltingPoint as Range
                                                    ).to || '__'
                                                } °C.`,
                                                color: '000000',
                                                size: 16,
                                            }),
                                        ],
                                    })
                                )
                            }
                            if (subadditionalCharacteristic.specificHeat) {
                                counter++
                                detailedDescriptionArr.push(
                                    new Paragraph({
                                        heading: HeadingLevel.HEADING_6,
                                        alignment: AlignmentType.LEFT,
                                        ...(isDownload
                                            ? {
                                                  numbering: {
                                                      reference: 'my-reference',
                                                      level: 0,
                                                  },
                                              }
                                            : {}),
                                        children: [
                                            ...(!isDownload
                                                ? [
                                                      new TextRun({
                                                          text: `[${counter}]    `,
                                                          color: '000000',
                                                          size: 16,
                                                      }),
                                                  ]
                                                : []),
                                            new TextRun({
                                                text: `In some embodiments of the ${category} ${
                                                    normalizedClaims[0].number
                                                }, the ${element.name} ${
                                                    element.number
                                                } has a specific heat of ${
                                                    (
                                                        subadditionalCharacteristic.specificHeat as Range
                                                    ).from || '__'
                                                } to ${
                                                    (
                                                        subadditionalCharacteristic.specificHeat as Range
                                                    ).to || '__'
                                                } J/kg°C.`,
                                                color: '000000',
                                                size: 16,
                                            }),
                                        ],
                                    })
                                )
                            }
                            if (subadditionalCharacteristic.defined) {
                                counter++
                                detailedDescriptionArr.push(
                                    new Paragraph({
                                        heading: HeadingLevel.HEADING_6,
                                        alignment: AlignmentType.LEFT,
                                        ...(isDownload
                                            ? {
                                                  numbering: {
                                                      reference: 'my-reference',
                                                      level: 0,
                                                  },
                                              }
                                            : {}),
                                        children: [
                                            ...(!isDownload
                                                ? [
                                                      new TextRun({
                                                          text: `[${counter}]    `,
                                                          color: '000000',
                                                          size: 16,
                                                      }),
                                                  ]
                                                : []),
                                            new TextRun({
                                                text: `In some embodiments of the ${category} ${
                                                    normalizedClaims[0].number
                                                }, the ${element.name} ${
                                                    element.number
                                                } ${
                                                    subadditionalCharacteristic
                                                        .defined.description ||
                                                    '__'
                                                }.`,
                                                color: '000000',
                                                size: 16,
                                            }),
                                        ],
                                    })
                                )
                            }
                        }

                        const formatUserDefined = formRef.getFieldValue<
                            ValueOf<MachineForm['formatUserDefined']>
                        >(['formatUserDefined', element.uid as string])
                        if (formatUserDefined) {
                            if (formatUserDefined.from) {
                                counter++
                                detailedDescriptionArr.push(
                                    new Paragraph({
                                        heading: HeadingLevel.HEADING_6,
                                        alignment: AlignmentType.LEFT,
                                        ...(isDownload
                                            ? {
                                                  numbering: {
                                                      reference: 'my-reference',
                                                      level: 0,
                                                  },
                                              }
                                            : {}),
                                        children: [
                                            ...(!isDownload
                                                ? [
                                                      new TextRun({
                                                          text: `[${counter}]    `,
                                                          color: '000000',
                                                          size: 16,
                                                      }),
                                                  ]
                                                : []),
                                            new TextRun({
                                                text: `In some embodiments of the ${category} ${
                                                    normalizedClaims[0].number
                                                }, the ${element.name} ${
                                                    element.number
                                                } has a ${
                                                    (
                                                        formatUserDefined.from as UserDefinedFormat
                                                    ).name || '__'
                                                } ${
                                                    (
                                                        formatUserDefined.from as UserDefinedFormat
                                                    ).maximum || '__'
                                                }  ${
                                                    (
                                                        formatUserDefined.from as UserDefinedFormat
                                                    ).units || '__'
                                                }`,
                                                color: '000000',
                                                size: 16,
                                            }),
                                        ],
                                    })
                                )
                            }
                            if (formatUserDefined.equal) {
                                counter++
                                detailedDescriptionArr.push(
                                    new Paragraph({
                                        heading: HeadingLevel.HEADING_6,
                                        alignment: AlignmentType.LEFT,
                                        ...(isDownload
                                            ? {
                                                  numbering: {
                                                      reference: 'my-reference',
                                                      level: 0,
                                                  },
                                              }
                                            : {}),
                                        children: [
                                            ...(!isDownload
                                                ? [
                                                      new TextRun({
                                                          text: `[${counter}]    `,
                                                          color: '000000',
                                                          size: 16,
                                                      }),
                                                  ]
                                                : []),
                                            new TextRun({
                                                text: `In some embodiments of the ${category} ${
                                                    normalizedClaims[0].number
                                                }, the ${element.name} ${
                                                    element.number
                                                } has a ${
                                                    (
                                                        formatUserDefined.equal as UserDefinedFormat
                                                    ).name || '__'
                                                } ${
                                                    (
                                                        formatUserDefined.equal as UserDefinedFormat
                                                    ).maximum || '__'
                                                }  ${
                                                    (
                                                        formatUserDefined.equal as UserDefinedFormat
                                                    ).units || '__'
                                                }`,
                                                color: '000000',
                                                size: 16,
                                            }),
                                        ],
                                    })
                                )
                            }
                            if (formatUserDefined.defined) {
                                counter++
                                detailedDescriptionArr.push(
                                    new Paragraph({
                                        heading: HeadingLevel.HEADING_6,
                                        alignment: AlignmentType.LEFT,
                                        ...(isDownload
                                            ? {
                                                  numbering: {
                                                      reference: 'my-reference',
                                                      level: 0,
                                                  },
                                              }
                                            : {}),
                                        children: [
                                            ...(!isDownload
                                                ? [
                                                      new TextRun({
                                                          text: `[${counter}]    `,
                                                          color: '000000',
                                                          size: 16,
                                                      }),
                                                  ]
                                                : []),
                                            new TextRun({
                                                text: `In some embodiments of the ${category} ${
                                                    normalizedClaims[0].number
                                                }, the ${element.name} ${
                                                    element.number
                                                } ${
                                                    formatUserDefined.defined
                                                        .description || '__'
                                                }.`,
                                                color: '000000',
                                                size: 16,
                                            }),
                                        ],
                                    })
                                )
                            }
                        }

                        const subformatUserDefined = formRef.getFieldValue<
                            ValueOf<MachineForm['subFormatUserDefined']>
                        >(['subFormatUserDefined', element.uid as string])

                        if (subformatUserDefined) {
                            if (subformatUserDefined.from) {
                                counter++
                                detailedDescriptionArr.push(
                                    new Paragraph({
                                        heading: HeadingLevel.HEADING_6,
                                        alignment: AlignmentType.LEFT,
                                        ...(isDownload
                                            ? {
                                                  numbering: {
                                                      reference: 'my-reference',
                                                      level: 0,
                                                  },
                                              }
                                            : {}),
                                        children: [
                                            ...(!isDownload
                                                ? [
                                                      new TextRun({
                                                          text: `[${counter}]    `,
                                                          color: '000000',
                                                          size: 16,
                                                      }),
                                                  ]
                                                : []),
                                            new TextRun({
                                                text: `In some embodiments of the ${category} ${
                                                    normalizedClaims[0].number
                                                }, the ${element.name} ${
                                                    element.number
                                                } has a ${
                                                    (
                                                        subformatUserDefined.from as UserDefinedFormat
                                                    ).name || '__'
                                                } ${
                                                    (
                                                        subformatUserDefined.from as UserDefinedFormat
                                                    ).maximum || '__'
                                                }  ${
                                                    (
                                                        subformatUserDefined.from as UserDefinedFormat
                                                    ).units || '__'
                                                }`,
                                                color: '000000',
                                                size: 16,
                                            }),
                                        ],
                                    })
                                )
                            }
                            if (subformatUserDefined.equal) {
                                counter++
                                detailedDescriptionArr.push(
                                    new Paragraph({
                                        heading: HeadingLevel.HEADING_6,
                                        alignment: AlignmentType.LEFT,
                                        ...(isDownload
                                            ? {
                                                  numbering: {
                                                      reference: 'my-reference',
                                                      level: 0,
                                                  },
                                              }
                                            : {}),
                                        children: [
                                            ...(!isDownload
                                                ? [
                                                      new TextRun({
                                                          text: `[${counter}]    `,
                                                          color: '000000',
                                                          size: 16,
                                                      }),
                                                  ]
                                                : []),
                                            new TextRun({
                                                text: `In some embodiments of the ${category} ${
                                                    normalizedClaims[0].number
                                                }, the ${element.name} ${
                                                    element.number
                                                } has a ${
                                                    (
                                                        subformatUserDefined.equal as UserDefinedFormat
                                                    ).name || '__'
                                                } ${
                                                    (
                                                        subformatUserDefined.equal as UserDefinedFormat
                                                    ).maximum || '__'
                                                }  ${
                                                    (
                                                        formatUserDefined.equal as UserDefinedFormat
                                                    ).units || '__'
                                                }`,
                                                color: '000000',
                                                size: 16,
                                            }),
                                        ],
                                    })
                                )
                            }
                            if (subformatUserDefined.defined) {
                                counter++
                                detailedDescriptionArr.push(
                                    new Paragraph({
                                        heading: HeadingLevel.HEADING_6,
                                        alignment: AlignmentType.LEFT,
                                        ...(isDownload
                                            ? {
                                                  numbering: {
                                                      reference: 'my-reference',
                                                      level: 0,
                                                  },
                                              }
                                            : {}),
                                        children: [
                                            ...(!isDownload
                                                ? [
                                                      new TextRun({
                                                          text: `[${counter}]    `,
                                                          color: '000000',
                                                          size: 16,
                                                      }),
                                                  ]
                                                : []),
                                            new TextRun({
                                                text: `In some embodiments of the ${category} ${
                                                    normalizedClaims[0].number
                                                }, the ${element.name} ${
                                                    element.number
                                                } ${
                                                    subformatUserDefined.defined
                                                        .description || '__'
                                                }.`,
                                                color: '000000',
                                                size: 16,
                                            }),
                                        ],
                                    })
                                )
                            }
                        }
                    })
                    const importantComponentsConnections =
                        formRef.getFieldValue<
                            MachineForm['importantComponentsConnections']
                        >(['importantComponentsConnections'])
                    if (importantComponentsConnections != null) {
                        Object.entries(importantComponentsConnections).forEach(
                            (entry: any) => {
                                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                                const [key, val] = entry
                                let finalText: string | undefined
                                const notSet = val?.notSet
                                const firstComponent = lowerfirst(
                                    extractComponentName(
                                        val?.['1stComponent'],
                                        formRef
                                    )
                                )
                                const secondComponent = lowerfirst(
                                    extractComponentName(
                                        val?.['2ndComponent'],
                                        formRef
                                    )
                                )
                                let firstComponentWithReferenceNo = ''
                                let isFoundFirstComponentReferenceNo = true
                                const foundFirstComponent =
                                    normalizedFigure.filter((claimElement) => {
                                        return (
                                            claimElement.name === firstComponent
                                        )
                                    })
                                if (foundFirstComponent.length) {
                                    firstComponentWithReferenceNo = `${firstComponent} ${foundFirstComponent[0].number}`
                                } else if (
                                    foundFirstComponent.length < 1 &&
                                    firstComponent.includes('of the')
                                ) {
                                    firstComponentWithReferenceNo =
                                        firstComponent
                                    let foundElementCount = 0
                                    normalizedFigure.forEach((claimElement) => {
                                        if (
                                            firstComponent.includes(
                                                claimElement.name
                                            )
                                        ) {
                                            firstComponentWithReferenceNo =
                                                firstComponentWithReferenceNo.replace(
                                                    claimElement.name,
                                                    `${claimElement.name} ${claimElement.number}`
                                                )
                                            foundElementCount += 1
                                        }
                                    })
                                    if (foundElementCount !== 2) {
                                        isFoundFirstComponentReferenceNo = false
                                    }
                                } else {
                                    isFoundFirstComponentReferenceNo = false
                                }
                                if (isFoundFirstComponentReferenceNo) {
                                    let secondComponentWithReferenceNo = ''
                                    let isFoundSecondComponentReferenceNo = true
                                    const foundSecondComponent =
                                        normalizedFigure.filter(
                                            (claimElement) => {
                                                return (
                                                    claimElement.name ===
                                                    secondComponent
                                                )
                                            }
                                        )
                                    if (foundSecondComponent.length) {
                                        secondComponentWithReferenceNo = `${secondComponent} ${foundSecondComponent[0].number}`
                                    } else if (
                                        foundSecondComponent.length < 1 &&
                                        secondComponent.includes('of the')
                                    ) {
                                        secondComponentWithReferenceNo =
                                            secondComponent
                                        let foundElementCount = 0
                                        normalizedFigure.forEach(
                                            (claimElement) => {
                                                if (
                                                    secondComponent.includes(
                                                        claimElement.name
                                                    )
                                                ) {
                                                    secondComponentWithReferenceNo =
                                                        secondComponentWithReferenceNo.replace(
                                                            claimElement.name,
                                                            `${claimElement.name} ${claimElement.number}`
                                                        )
                                                    foundElementCount += 1
                                                }
                                            }
                                        )
                                        if (foundElementCount !== 2) {
                                            isFoundSecondComponentReferenceNo =
                                                false
                                        }
                                    } else {
                                        isFoundSecondComponentReferenceNo =
                                            false
                                    }
                                    if (isFoundSecondComponentReferenceNo) {
                                        if (notSet) {
                                            const firstPart = val?.['1stPart']
                                            const secondPart = val?.['2ndPart']
                                            const thirdPart = val?.['3rdPart']
                                            if (
                                                firstPart &&
                                                firstComponentWithReferenceNo &&
                                                secondPart &&
                                                secondComponentWithReferenceNo &&
                                                thirdPart
                                            ) {
                                                finalText = `In some embodiments of the ${category} ${
                                                    normalizedClaims[0].number
                                                }, ${firstPart} the ${firstComponentWithReferenceNo} ${secondPart} the ${secondComponentWithReferenceNo} ${
                                                    thirdPart || '__'
                                                }`
                                            }
                                        } else {
                                            const foundSubConnectionType =
                                                connectionRelation[
                                                    val?.connectionType
                                                ]?.find((connection: any) => {
                                                    return (
                                                        connection.value ===
                                                        val?.subConnectionType
                                                    )
                                                })
                                            const connectionType =
                                                foundSubConnectionType
                                                    ? val?.subConnectionType ||
                                                      val?.connectionType
                                                    : val?.connectionType
                                            if (
                                                firstComponentWithReferenceNo &&
                                                connectionType &&
                                                secondComponentWithReferenceNo
                                            ) {
                                                finalText = `In some embodiments of the ${category} ${
                                                    normalizedClaims[0].number
                                                }, the ${firstComponentWithReferenceNo} is ${connectionType.toLowerCase()} the ${secondComponentWithReferenceNo}`
                                            }
                                        }
                                        if (finalText) {
                                            counter++
                                            detailedDescriptionArr.push(
                                                new Paragraph({
                                                    heading:
                                                        HeadingLevel.HEADING_6,
                                                    alignment:
                                                        AlignmentType.LEFT,

                                                    ...(isDownload
                                                        ? {
                                                              numbering: {
                                                                  reference:
                                                                      'my-reference',
                                                                  level: 0,
                                                              },
                                                          }
                                                        : {}),
                                                    children: [
                                                        ...(!isDownload
                                                            ? [
                                                                  new TextRun({
                                                                      text: `[${counter}]    `,
                                                                      color: '000000',
                                                                      size: 16,
                                                                  }),
                                                              ]
                                                            : []),
                                                        new TextRun({
                                                            text: `${finalText}.`,
                                                            color: '000000',
                                                            size: 16,
                                                        }),
                                                    ],
                                                })
                                            )
                                        }
                                    }
                                }
                            }
                        )
                    }
                    const importantComponentsArrangements =
                        formRef.getFieldValue<
                            MachineForm['importantComponentsArrangements']
                        >(['importantComponentsArrangements'])
                    if (importantComponentsArrangements != null) {
                        Object.entries(importantComponentsArrangements).forEach(
                            (entry: any) => {
                                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                                const [key, val] = entry
                                let finalText: string | undefined
                                const notSet = val?.notSet
                                const firstComponent = lowerfirst(
                                    extractComponentName(
                                        val?.['1stComponent'],
                                        formRef
                                    )
                                )
                                const secondComponent = lowerfirst(
                                    extractComponentName(
                                        val?.['2ndComponent'],
                                        formRef
                                    )
                                )
                                let firstComponentWithReferenceNo = ''
                                let isFoundFirstComponentReferenceNo = true
                                const foundFirstComponent =
                                    normalizedFigure.filter((claimElement) => {
                                        return (
                                            claimElement.name === firstComponent
                                        )
                                    })
                                if (foundFirstComponent.length) {
                                    firstComponentWithReferenceNo = `${firstComponent} ${foundFirstComponent[0].number}`
                                } else if (
                                    foundFirstComponent.length < 1 &&
                                    firstComponent.includes('of the')
                                ) {
                                    firstComponentWithReferenceNo =
                                        firstComponent
                                    let foundElementCount = 0
                                    normalizedFigure.forEach((claimElement) => {
                                        if (
                                            firstComponent.includes(
                                                claimElement.name
                                            )
                                        ) {
                                            firstComponentWithReferenceNo =
                                                firstComponentWithReferenceNo.replace(
                                                    claimElement.name,
                                                    `${claimElement.name} ${claimElement.number}`
                                                )
                                            foundElementCount += 1
                                        }
                                    })
                                    if (foundElementCount !== 2) {
                                        isFoundFirstComponentReferenceNo = false
                                    }
                                } else {
                                    isFoundFirstComponentReferenceNo = false
                                }
                                if (isFoundFirstComponentReferenceNo) {
                                    let secondComponentWithReferenceNo = ''
                                    let isFoundSecondComponentReferenceNo = true
                                    const foundSecondComponent =
                                        normalizedFigure.filter(
                                            (claimElement) => {
                                                return (
                                                    claimElement.name ===
                                                    secondComponent
                                                )
                                            }
                                        )
                                    if (foundSecondComponent.length) {
                                        secondComponentWithReferenceNo = `${secondComponent} ${foundSecondComponent[0].number}`
                                    } else if (
                                        foundSecondComponent.length < 1 &&
                                        secondComponent.includes('of the')
                                    ) {
                                        secondComponentWithReferenceNo =
                                            secondComponent
                                        let foundElementCount = 0
                                        normalizedFigure.forEach(
                                            (claimElement) => {
                                                if (
                                                    secondComponent.includes(
                                                        claimElement.name
                                                    )
                                                ) {
                                                    secondComponentWithReferenceNo =
                                                        secondComponentWithReferenceNo.replace(
                                                            claimElement.name,
                                                            `${claimElement.name} ${claimElement.number}`
                                                        )
                                                    foundElementCount += 1
                                                }
                                            }
                                        )
                                        if (foundElementCount !== 2) {
                                            isFoundSecondComponentReferenceNo =
                                                false
                                        }
                                    } else {
                                        isFoundSecondComponentReferenceNo =
                                            false
                                    }
                                    if (isFoundSecondComponentReferenceNo) {
                                        if (notSet) {
                                            const firstPart = val?.['1stPart']
                                            const secondPart = val?.['2ndPart']
                                            const thirdPart = val?.['3rdPart']
                                            if (
                                                firstComponentWithReferenceNo &&
                                                secondPart &&
                                                secondComponentWithReferenceNo
                                            ) {
                                                finalText = `In some embodiments of the ${category} ${
                                                    normalizedClaims[0].number
                                                }, ${
                                                    firstPart
                                                        ? `${firstPart} the `
                                                        : ''
                                                }${firstComponentWithReferenceNo} ${secondPart} the ${secondComponentWithReferenceNo} ${
                                                    thirdPart || '__'
                                                }`
                                            }
                                        } else {
                                            const arrangementType =
                                                val?.arrangementType
                                            if (
                                                firstComponentWithReferenceNo &&
                                                arrangementType &&
                                                secondComponentWithReferenceNo
                                            ) {
                                                finalText = `In some embodiments of the ${category} ${
                                                    normalizedClaims[0].number
                                                }, the ${firstComponentWithReferenceNo} is ${arrangementType.toLowerCase()} the ${secondComponentWithReferenceNo}`
                                            }
                                        }
                                        if (finalText) {
                                            counter++
                                            detailedDescriptionArr.push(
                                                new Paragraph({
                                                    heading:
                                                        HeadingLevel.HEADING_6,
                                                    alignment:
                                                        AlignmentType.LEFT,
                                                    ...(isDownload
                                                        ? {
                                                              numbering: {
                                                                  reference:
                                                                      'my-reference',
                                                                  level: 0,
                                                              },
                                                          }
                                                        : {}),
                                                    children: [
                                                        ...(!isDownload
                                                            ? [
                                                                  new TextRun({
                                                                      text: `[${counter}]    `,
                                                                      color: '000000',
                                                                      size: 16,
                                                                  }),
                                                              ]
                                                            : []),
                                                        new TextRun({
                                                            text: `${finalText}.`,
                                                            color: '000000',
                                                            size: 16,
                                                        }),
                                                    ],
                                                })
                                            )
                                        }
                                    }
                                }
                            }
                        )
                    }
                    const functionalConnections = formRef.getFieldValue<
                        MachineForm['functionalConnections']
                    >(['functionalConnections'])
                    if (functionalConnections != null) {
                        Object.entries(functionalConnections).forEach(
                            (entry: any) => {
                                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                                const [key, val] = entry
                                let finalText: string | undefined
                                const notSet = val?.notSet
                                const firstComponent = lowerfirst(
                                    extractComponentName(
                                        val?.['1stComponent'],
                                        formRef
                                    )
                                )
                                const secondComponent = lowerfirst(
                                    extractComponentName(
                                        val?.['2ndComponent'],
                                        formRef
                                    )
                                )
                                let firstComponentWithReferenceNo = ''
                                let isFoundFirstComponentReferenceNo = true
                                const foundFirstComponent =
                                    normalizedFigure.filter((claimElement) => {
                                        return (
                                            claimElement.name === firstComponent
                                        )
                                    })
                                if (foundFirstComponent.length) {
                                    firstComponentWithReferenceNo = `${firstComponent} ${foundFirstComponent[0].number}`
                                } else if (
                                    foundFirstComponent.length < 1 &&
                                    firstComponent.includes('of the')
                                ) {
                                    firstComponentWithReferenceNo =
                                        firstComponent
                                    let foundElementCount = 0
                                    normalizedFigure.forEach((claimElement) => {
                                        if (
                                            firstComponent.includes(
                                                claimElement.name
                                            )
                                        ) {
                                            firstComponentWithReferenceNo =
                                                firstComponentWithReferenceNo.replace(
                                                    claimElement.name,
                                                    `${claimElement.name} ${claimElement.number}`
                                                )
                                            foundElementCount += 1
                                        }
                                    })
                                    if (foundElementCount !== 2) {
                                        isFoundFirstComponentReferenceNo = false
                                    }
                                } else {
                                    isFoundFirstComponentReferenceNo = false
                                }
                                if (isFoundFirstComponentReferenceNo) {
                                    let secondComponentWithReferenceNo = ''
                                    let isFoundSecondComponentReferenceNo = true
                                    const foundSecondComponent =
                                        normalizedFigure.filter(
                                            (claimElement) => {
                                                return (
                                                    claimElement.name ===
                                                    secondComponent
                                                )
                                            }
                                        )
                                    if (foundSecondComponent.length) {
                                        secondComponentWithReferenceNo = `${secondComponent} ${foundSecondComponent[0].number}`
                                    } else if (
                                        foundSecondComponent.length < 1 &&
                                        secondComponent.includes('of the')
                                    ) {
                                        secondComponentWithReferenceNo =
                                            secondComponent
                                        let foundElementCount = 0
                                        normalizedFigure.forEach(
                                            (claimElement) => {
                                                if (
                                                    secondComponent.includes(
                                                        claimElement.name
                                                    )
                                                ) {
                                                    secondComponentWithReferenceNo =
                                                        secondComponentWithReferenceNo.replace(
                                                            claimElement.name,
                                                            `${claimElement.name} ${claimElement.number}`
                                                        )
                                                    foundElementCount += 1
                                                }
                                            }
                                        )
                                        if (foundElementCount !== 2) {
                                            isFoundSecondComponentReferenceNo =
                                                false
                                        }
                                    } else {
                                        isFoundSecondComponentReferenceNo =
                                            false
                                    }
                                    if (isFoundSecondComponentReferenceNo) {
                                        if (notSet) {
                                            const firstPart = val?.['1stPart']
                                            const secondPart = val?.['2ndPart']
                                            const thirdPart = val?.['3rdPart']
                                            if (
                                                firstComponentWithReferenceNo &&
                                                secondPart &&
                                                secondComponentWithReferenceNo
                                            ) {
                                                finalText = `In some embodiments of the ${category} ${
                                                    normalizedClaims[0].number
                                                }, ${
                                                    firstPart
                                                        ? `${firstPart} the `
                                                        : ''
                                                }${firstComponentWithReferenceNo} ${secondPart} the ${secondComponentWithReferenceNo} ${
                                                    thirdPart || '__'
                                                }`
                                            }
                                        } else if (
                                            firstComponentWithReferenceNo &&
                                            secondComponentWithReferenceNo
                                        ) {
                                            finalText = `In some embodiments of the ${category} ${normalizedClaims[0].number}, the ${firstComponentWithReferenceNo} is functionally connected with the ${secondComponentWithReferenceNo}`
                                        }
                                        if (finalText) {
                                            counter++
                                            detailedDescriptionArr.push(
                                                new Paragraph({
                                                    heading:
                                                        HeadingLevel.HEADING_6,
                                                    alignment:
                                                        AlignmentType.LEFT,
                                                    ...(isDownload
                                                        ? {
                                                              numbering: {
                                                                  reference:
                                                                      'my-reference',
                                                                  level: 0,
                                                              },
                                                          }
                                                        : {}),
                                                    children: [
                                                        ...(!isDownload
                                                            ? [
                                                                  new TextRun({
                                                                      text: `[${counter}]    `,
                                                                      color: '000000',
                                                                      size: 16,
                                                                  }),
                                                              ]
                                                            : []),
                                                        new TextRun({
                                                            text: `${finalText}.`,
                                                            color: '000000',
                                                            size: 16,
                                                        }),
                                                    ],
                                                })
                                            )
                                        }
                                    }
                                }
                            }
                        )
                    }
                    const dependantComponentsConnections =
                        formRef.getFieldValue<
                            MachineForm['dependantComponentsConnections']
                        >(['dependantComponentsConnections'])
                    if (dependantComponentsConnections != null) {
                        Object.entries(dependantComponentsConnections).forEach(
                            (entry: any) => {
                                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                                const [key, val] = entry
                                let finalText: string | undefined
                                const notSet = val?.notSet
                                const firstComponent = lowerfirst(
                                    extractComponentName(
                                        val?.['1stComponent'],
                                        formRef
                                    )
                                )
                                const secondComponent = lowerfirst(
                                    extractComponentName(
                                        val?.['2ndComponent'],
                                        formRef
                                    )
                                )
                                let firstComponentWithReferenceNo = ''
                                let isFoundFirstComponentReferenceNo = true
                                const foundFirstComponent =
                                    normalizedFigure.filter((claimElement) => {
                                        return (
                                            claimElement.name === firstComponent
                                        )
                                    })
                                if (foundFirstComponent.length) {
                                    firstComponentWithReferenceNo = `${firstComponent} ${foundFirstComponent[0].number}`
                                } else if (
                                    foundFirstComponent.length < 1 &&
                                    firstComponent.includes('of the')
                                ) {
                                    firstComponentWithReferenceNo =
                                        firstComponent
                                    let foundElementCount = 0
                                    normalizedFigure.forEach((claimElement) => {
                                        if (
                                            firstComponent.includes(
                                                claimElement.name
                                            )
                                        ) {
                                            firstComponentWithReferenceNo =
                                                firstComponentWithReferenceNo.replace(
                                                    claimElement.name,
                                                    `${claimElement.name} ${claimElement.number}`
                                                )
                                            foundElementCount += 1
                                        }
                                    })
                                    if (foundElementCount !== 2) {
                                        isFoundFirstComponentReferenceNo = false
                                    }
                                } else {
                                    isFoundFirstComponentReferenceNo = false
                                }
                                if (isFoundFirstComponentReferenceNo) {
                                    let secondComponentWithReferenceNo = ''
                                    let isFoundSecondComponentReferenceNo = true
                                    const foundSecondComponent =
                                        normalizedFigure.filter(
                                            (claimElement) => {
                                                return (
                                                    claimElement.name ===
                                                    secondComponent
                                                )
                                            }
                                        )
                                    if (foundSecondComponent.length) {
                                        secondComponentWithReferenceNo = `${secondComponent} ${foundSecondComponent[0].number}`
                                    } else if (
                                        foundSecondComponent.length < 1 &&
                                        secondComponent.includes('of the')
                                    ) {
                                        secondComponentWithReferenceNo =
                                            secondComponent
                                        let foundElementCount = 0
                                        normalizedFigure.forEach(
                                            (claimElement) => {
                                                if (
                                                    secondComponent.includes(
                                                        claimElement.name
                                                    )
                                                ) {
                                                    secondComponentWithReferenceNo =
                                                        secondComponentWithReferenceNo.replace(
                                                            claimElement.name,
                                                            `${claimElement.name} ${claimElement.number}`
                                                        )
                                                    foundElementCount += 1
                                                }
                                            }
                                        )
                                        if (foundElementCount !== 2) {
                                            isFoundSecondComponentReferenceNo =
                                                false
                                        }
                                    } else {
                                        isFoundSecondComponentReferenceNo =
                                            false
                                    }
                                    if (isFoundSecondComponentReferenceNo) {
                                        if (notSet) {
                                            const firstPart = val?.['1stPart']
                                            const secondPart = val?.['2ndPart']
                                            const thirdPart = val?.['3rdPart']
                                            if (
                                                firstPart &&
                                                firstComponentWithReferenceNo &&
                                                secondPart &&
                                                secondComponentWithReferenceNo &&
                                                thirdPart
                                            ) {
                                                finalText = `In some embodiments of the ${category} ${
                                                    normalizedClaims[0].number
                                                }, ${firstPart} the ${firstComponentWithReferenceNo} ${secondPart} the ${secondComponentWithReferenceNo} ${
                                                    thirdPart || '__'
                                                }`
                                            }
                                        } else {
                                            const connectionType =
                                                val?.connectionType?.toLowerCase()
                                            if (
                                                firstComponentWithReferenceNo &&
                                                connectionType &&
                                                secondComponentWithReferenceNo
                                            ) {
                                                finalText = `In some embodiments of the ${category} ${normalizedClaims[0].number}, the ${firstComponentWithReferenceNo} is ${connectionType} the ${secondComponentWithReferenceNo}`
                                            }
                                        }
                                        if (finalText) {
                                            counter++
                                            detailedDescriptionArr.push(
                                                new Paragraph({
                                                    heading:
                                                        HeadingLevel.HEADING_6,
                                                    alignment:
                                                        AlignmentType.LEFT,
                                                    ...(isDownload
                                                        ? {
                                                              numbering: {
                                                                  reference:
                                                                      'my-reference',
                                                                  level: 0,
                                                              },
                                                          }
                                                        : {}),
                                                    children: [
                                                        ...(!isDownload
                                                            ? [
                                                                  new TextRun({
                                                                      text: `[${counter}]    `,
                                                                      color: '000000',
                                                                      size: 16,
                                                                  }),
                                                              ]
                                                            : []),
                                                        new TextRun({
                                                            text: `${finalText}.`,
                                                            color: '000000',
                                                            size: 16,
                                                        }),
                                                    ],
                                                })
                                            )
                                        }
                                    }
                                }
                            }
                        )
                    }
                })
            }
            const isCheckedBoilerPlate = formRef.getFieldValue([
                'detailedDescription',
                'checked',
            ])

            // moved the "detailed description text para here
            if (describeDisclosure) {
                counter++
                detailedDescriptionArr.push(
                    new Paragraph({
                        heading: HeadingLevel.HEADING_6,
                        alignment: AlignmentType.LEFT,
                        ...(isDownload
                            ? {
                                  numbering: {
                                      reference: 'my-reference',
                                      level: 0,
                                  },
                              }
                            : {}),
                        children: [
                            ...(!isDownload
                                ? [
                                      new TextRun({
                                          text: `[${counter}]    `,
                                          color: '000000',
                                          size: 16,
                                      }),
                                  ]
                                : []),
                            new TextRun({
                                text: `Some embodiments of the ${category} described in this disclosure ${
                                    describeDisclosure || '__'
                                }`,
                                color: '000000',
                                size: 16,
                            }),
                        ],
                    })
                )
            }

            if (isCheckedBoilerPlate) {
                // ix-82 fix new:
                const described = formRef.getFieldValue([
                    'detailedDescription',
                    'described',
                ])

                // 2. MOVED UP: Push the "For example" paragraph BEFORE "Additional features"
                if (described) {
                    counter++
                    detailedDescriptionArr.push(
                        new Paragraph({
                            heading: HeadingLevel.HEADING_6,
                            alignment: AlignmentType.LEFT,
                            ...(isDownload
                                ? {
                                      numbering: {
                                          reference: 'my-reference',
                                          level: 0,
                                      },
                                  }
                                : {}),
                            children: [
                                ...(!isDownload
                                    ? [
                                          new TextRun({
                                              text: `[${counter}]    `,
                                              color: '000000',
                                              size: 16,
                                          }),
                                      ]
                                    : []),
                                new TextRun({
                                    text: `For example, ${described}.`,
                                    color: '000000',
                                    size: 16,
                                }),
                            ],
                        })
                    )
                }

                counter++
                detailedDescriptionArr.push(
                    new Paragraph({
                        heading: HeadingLevel.HEADING_6,
                        alignment: AlignmentType.LEFT,
                        ...(isDownload
                            ? {
                                  numbering: {
                                      reference: 'my-reference',
                                      level: 0,
                                  },
                              }
                            : {}),
                        children: [
                            ...(!isDownload
                                ? [
                                      new TextRun({
                                          text: `[${counter}]    `,
                                          color: '000000',
                                          size: 16,
                                      }),
                                  ]
                                : []),
                            new TextRun({
                                text: `Additional features and options for a ${category}  of this disclosure are illustrated throughout the Figures.  Nonetheless, the embodiments illustrated in the Figures are only illustrative, not limiting.  Using this disclosure for reference, a skilled person would understand that many different modifications of the expressly disclosed embodiments are possible.  For example, any ${category} feature described in this disclosure can be omitted from or added to any of the ${category}s described in this disclosure to create additional ${category}.  Additionally, the order of a feature in any ${category} can be swapped with another feature or otherwise reordered to create additional embodiments.`,
                                color: '000000',
                                size: 16,
                            }),
                        ],
                    })
                )

                //  FIX: Separated the "Moreover" paragraph from the "Example" text.
                //  This paragraph now always appears cleanly without the example attached.
                counter++
                detailedDescriptionArr.push(
                    new Paragraph({
                        heading: HeadingLevel.HEADING_6,
                        alignment: AlignmentType.LEFT,
                        ...(isDownload
                            ? {
                                  numbering: {
                                      reference: 'my-reference',
                                      level: 0,
                                  },
                              }
                            : {}),
                        children: [
                            ...(!isDownload
                                ? [
                                      new TextRun({
                                          text: `[${counter}]    `,
                                          color: '000000',
                                          size: 16,
                                      }),
                                  ]
                                : []),
                            new TextRun({
                                text: `Moreover, any feature or combination of features of a ${category} described in this disclosure can be repeated one, two, three or any number of times.  Since any ${category} can involve performing a second feature or features similar to a first feature previously described with reference to the ${category}, every element described with reference to the first ${category}, can be considered a first element of the kind described.`,
                                color: '000000',
                                size: 16,
                            }),
                        ],
                    })
                )

                //  FIX: The Example text is now a SEPARATE paragraph.
                //  FIX: It is moved to be immediately after the "Moreover" paragraph.
                //  FIX: It is optional (if 'described' is empty, this paragraph is skipped).

                counter++
                detailedDescriptionArr.push(
                    new Paragraph({
                        heading: HeadingLevel.HEADING_6,
                        alignment: AlignmentType.LEFT,
                        ...(isDownload
                            ? {
                                  numbering: {
                                      reference: 'my-reference',
                                      level: 0,
                                  },
                              }
                            : {}),
                        children: [
                            ...(!isDownload
                                ? [
                                      new TextRun({
                                          text: `[${counter}]    `,
                                          color: '000000',
                                          size: 16,
                                      }),
                                  ]
                                : []),
                            new TextRun({
                                text: `Additionally, because the feature or a selection of features can be included any number of times in any order, subsequent recitation of a feature that has previously been recited can be given a name indicating that the feature is one of several features, for example, the name can include “subsequent,” “additional,” “second,” “third,” “fourth,” “fifth,” “sixth” or any other appropriate designation relating to number or order or both number and order.`,
                                color: '000000',
                                size: 16,
                            }),
                        ],
                    })
                )
            }
        }
        if (question === 'abstractOfTheDisclosure') {
            if (
                isMachineForm(formRef, question) &&
                isCompositionForm(formRef, question)
            ) {
                summaryArr = []
                const summary = formRef.getFieldValue<
                    FormValues['summaryOfSelectedEmbodimentsOfTheInvention']
                >('summaryOfSelectedEmbodimentsOfTheInvention')
                // const test = formRef.getFieldValue<
                //     MachineValues['subSummaryOfSelectedEmbodimentsOfTheInvention']
                // >('subSummaryOfSelectedEmbodimentsOfTheInvention')
                const primaryComponents = formRef.getFieldValue<
                    MachineForm['primaryComponents']
                >(MachineNames.primaryComponents)
                const additionalComponents = formRef.getFieldValue<
                    MachineForm['primaryAdditionalComponents']
                >(MachineNames.primaryAdditionalComponents)
                const list =
                    formRef.getFieldValue<MachineForm['mainSubcomponents']>(
                        MachineNames.mainSubcomponents
                    ) ?? []
                summaryArr.push(
                    new Paragraph({
                        heading: HeadingLevel.HEADING_1,
                        alignment: AlignmentType.CENTER,
                        spacing: {
                            before: 250,
                            after: 250,
                        },
                        children: [
                            new TextRun({
                                text: `SUMMARY OF SELECTED EMBODIMENTS OF THE INVENTION`,
                                color: '000000',
                                size: 16,
                                // bold: true,
                                underline: {
                                    type: UnderlineType.SINGLE,
                                    color: '000000',
                                },
                            }),
                        ],
                    })
                )

                counter++
                let abstractPrimaryComponents: any[] = []
                let abstractAdditionalComponents: any[] = []
                const abstractComponents = []
                const abstractSubComponents: any[] = []
                const abstractFeatures: any[] = []
                let summaryText = ''
                if (summary && summary.length) {
                    if (primaryComponents) {
                        abstractPrimaryComponents = primaryComponents.filter(
                            (item) => {
                                if (summary && item !== undefined) {
                                    return summary.includes(item.id ?? '')
                                }

                                return false
                            }
                        )
                        abstractPrimaryComponents.forEach((comp) => {
                            abstractComponents.push(
                                comp?.indefiniteArticle === ' '
                                    ? `${comp?.component}`
                                    : `${comp?.indefiniteArticle} ${comp?.component}`
                            )
                        })
                    }
                    if (additionalComponents) {
                        abstractAdditionalComponents =
                            additionalComponents.filter((item) => {
                                if (summary && item !== undefined) {
                                    return summary.includes(`add_${item.id}`)
                                }

                                return false
                            })
                        abstractAdditionalComponents.forEach((comp) => {
                            abstractComponents.push(
                                comp.indefiniteArticle === ' '
                                    ? `${comp.component}`
                                    : `${comp.indefiniteArticle} ${comp.component}`
                            )
                        })
                    }
                    list.forEach((mainComponent) => {
                        const componentData = primaryComponents.find(
                            (x) => x?.id === mainComponent
                        )

                        if (componentData) {
                            const subComponents = formRef.getFieldValue([
                                MachineNames.primaryComponentSubComponentsList,
                                mainComponent,
                            ]) as ValueOf<
                                MachineForm['primaryComponentSubComponentsList']
                            >
                            if (subComponents) {
                                const primaryComponentData =
                                    abstractPrimaryComponents.find(
                                        (x) => x?.id === mainComponent
                                    )
                                if (primaryComponentData) {
                                    const parentClaimSubComponents =
                                        subComponents.filter((item) => {
                                            if (summary && item !== undefined) {
                                                return summary.includes(
                                                    `${componentData.id}+=>${item.id}`
                                                )
                                            }

                                            return false
                                        })
                                    if (parentClaimSubComponents.length > 0) {
                                        abstractSubComponents.push(
                                            `The ${
                                                primaryComponentData?.component
                                            } comprises ${parentClaimSubComponents
                                                .map((subComp: any) =>
                                                    subComp.indefiniteArticle ===
                                                    ' '
                                                        ? `${subComp.component}`
                                                        : `${subComp.indefiniteArticle} ${subComp.component}`
                                                )
                                                ?.join(', ')
                                                .replace(
                                                    /,(?=[^,]*$)/,
                                                    ' and'
                                                )}.`
                                        )
                                    }
                                }
                            }
                        }
                    })
                    const characteristicComponents = formRef.getFieldValue(
                        MachineNames.componentsForCharacteristics
                    ) as MachineValues['componentsForCharacteristics']
                    characteristicComponents.forEach((component: string) => {
                        const parentClaimPrimaryComponent =
                            abstractPrimaryComponents.find(
                                (x) => x?.id === component
                            )
                        if (parentClaimPrimaryComponent) {
                            const characteristic = formRef.getFieldValue([
                                MachineNames.characteristic,
                                component,
                            ]) as ValueOf<MachineValues['characteristic']>
                            const additionalCharacteristic =
                                formRef.getFieldValue<
                                    ValueOf<
                                        MachineValues['additionalCharacteristic']
                                    >
                                >(['additionalCharacteristic', component])
                            const formatUserDefined = formRef.getFieldValue<
                                ValueOf<MachineValues['formatUserDefined']>
                            >(['formatUserDefined', component])
                            if (characteristic) {
                                Object.entries(characteristic).forEach(
                                    (entity) => {
                                        const [entityKey, val] = entity
                                        const key =
                                            entityKey as keyof typeof UNITS
                                        if (val) {
                                            if (
                                                summary &&
                                                summary.includes(
                                                    `characteristic_${component}.${key}`
                                                )
                                            ) {
                                                if (
                                                    typeof val === 'object' &&
                                                    val !== null
                                                ) {
                                                    if (key === 'defined') {
                                                        abstractFeatures.push(
                                                            `The ${
                                                                'description' in
                                                                val
                                                                    ? val.description
                                                                    : '__'
                                                            }.`
                                                        )
                                                    } else {
                                                        abstractFeatures.push(
                                                            `The ${parentClaimPrimaryComponent.component!} has a ${key} from ${
                                                                'from' in val
                                                                    ? val.from
                                                                    : '__'
                                                            } to ${
                                                                'to' in val
                                                                    ? val.to
                                                                    : '__'
                                                            } ${UNITS[key]}.`
                                                        )
                                                    }
                                                }
                                            }
                                        }
                                    }
                                )
                            }
                            if (additionalCharacteristic) {
                                Object.entries(
                                    additionalCharacteristic
                                ).forEach((entity) => {
                                    const [entityKey, val] = entity
                                    const key = entityKey as keyof typeof UNITS
                                    if (val) {
                                        if (
                                            summary &&
                                            summary.includes(
                                                `additionalCharacteristic_${component}.${key}`
                                            )
                                        ) {
                                            if (
                                                typeof val === 'object' &&
                                                val !== null
                                            ) {
                                                if ('description' in val) {
                                                    if (key === 'defined') {
                                                        abstractFeatures.push(
                                                            `The ${
                                                                'description' in
                                                                val
                                                                    ? val.description
                                                                    : '__'
                                                            }.`
                                                        )
                                                    }
                                                } else {
                                                    if (
                                                        key ===
                                                        'thermalConductivity'
                                                    ) {
                                                        abstractFeatures.push(
                                                            `The ${
                                                                parentClaimPrimaryComponent.component
                                                            } has a thermal conductivity from ${
                                                                val.from || '__'
                                                            } to ${
                                                                val.to || '__'
                                                            } ${UNITS[key]}.`
                                                        )
                                                    }
                                                    if (
                                                        key === 'meltingPoint'
                                                    ) {
                                                        abstractFeatures.push(
                                                            `The ${
                                                                parentClaimPrimaryComponent.component
                                                            } has a melting point from ${
                                                                val.from || '__'
                                                            } to ${
                                                                val.to || '__'
                                                            } ${UNITS[key]}.`
                                                        )
                                                    }
                                                    if (
                                                        key === 'specificHeat'
                                                    ) {
                                                        abstractFeatures.push(
                                                            `The ${
                                                                parentClaimPrimaryComponent.component
                                                            } has a specific heat from ${
                                                                val.from || '__'
                                                            } to ${
                                                                val.to || '__'
                                                            } ${UNITS[key]}.`
                                                        )
                                                    }
                                                }
                                            }
                                        }
                                    }
                                })
                            }
                            if (formatUserDefined) {
                                Object.entries(formatUserDefined).forEach(
                                    (entity: any) => {
                                        const [entityKey, val] = entity
                                        const key = entityKey
                                        if (val) {
                                            if (
                                                summary &&
                                                summary.includes(
                                                    `formatUserDefined_${component}.${key}`
                                                )
                                            ) {
                                                if (
                                                    typeof val === 'object' &&
                                                    val !== null
                                                ) {
                                                    if (key === 'from') {
                                                        abstractFeatures.push(
                                                            `The ${parentClaimPrimaryComponent.component!} has a ${
                                                                val.name || '__'
                                                            } from ${
                                                                val.minimum ||
                                                                '__'
                                                            } to ${
                                                                val.maximum ||
                                                                '__'
                                                            } ${
                                                                val.units || ''
                                                            }.`
                                                        )
                                                    }
                                                    if (key === 'equal') {
                                                        abstractFeatures.push(
                                                            `The ${
                                                                val.name || '__'
                                                            } of the ${parentClaimPrimaryComponent.component!} is equal to from ${
                                                                val.minimum ||
                                                                '__'
                                                            } to ${
                                                                val.maximum ||
                                                                '__'
                                                            } ${
                                                                val.units || ''
                                                            }.`
                                                        )
                                                    }
                                                    if (key === 'defined') {
                                                        abstractFeatures.push(
                                                            `The ${
                                                                val.description ||
                                                                '___'
                                                            }.`
                                                        )
                                                    }
                                                }
                                            }
                                        }
                                    }
                                )
                            }
                        }
                    })

                    if (abstractAdditionalComponents) {
                        const additionalComponentsForCharacteristics =
                            formRef.getFieldValue<
                                MachineValues['additionalComponentsForCharacteristics']
                            >('additionalComponentsForCharacteristics')

                        additionalComponentsForCharacteristics?.forEach(
                            (component: string) => {
                                const parentClaimAdditionalComponent =
                                    abstractAdditionalComponents?.find(
                                        (x) => x?.id === component
                                    )
                                if (parentClaimAdditionalComponent) {
                                    const addCompCharacteristic =
                                        formRef.getFieldValue([
                                            MachineNames.addCompCharacteristic,
                                            component,
                                        ]) as ValueOf<
                                            MachineValues['addCompCharacteristic']
                                        >
                                    const addCompAdditionalCharacteristic =
                                        formRef.getFieldValue<
                                            ValueOf<
                                                MachineValues['addCompAdditionalCharacteristic']
                                            >
                                        >([
                                            'addCompAdditionalCharacteristic',
                                            component,
                                        ])
                                    const addCompFormatUserDefined =
                                        formRef.getFieldValue<
                                            ValueOf<
                                                MachineValues['addCompFormatUserDefined']
                                            >
                                        >([
                                            'addCompFormatUserDefined',
                                            component,
                                        ])
                                    if (addCompCharacteristic) {
                                        Object.entries(
                                            addCompCharacteristic
                                        ).forEach((entity) => {
                                            const [entityKey, val] = entity
                                            const key =
                                                entityKey as keyof typeof UNITS
                                            if (val) {
                                                if (
                                                    summary &&
                                                    summary.includes(
                                                        `addCompCharacteristic_${component}.${key}`
                                                    )
                                                ) {
                                                    if (
                                                        typeof val ===
                                                            'object' &&
                                                        val !== null
                                                    ) {
                                                        if (key === 'defined') {
                                                            abstractFeatures.push(
                                                                `The ${
                                                                    'description' in
                                                                    val
                                                                        ? val.description
                                                                        : '__'
                                                                }.`
                                                            )
                                                        } else {
                                                            abstractFeatures.push(
                                                                `The ${parentClaimAdditionalComponent.component!} has a ${key} from ${
                                                                    'from' in
                                                                    val
                                                                        ? val.from
                                                                        : '__'
                                                                } to ${
                                                                    'to' in val
                                                                        ? val.to
                                                                        : '__'
                                                                } ${
                                                                    UNITS[key]
                                                                }.`
                                                            )
                                                        }
                                                    }
                                                }
                                            }
                                        })
                                    }
                                    if (addCompAdditionalCharacteristic) {
                                        Object.entries(
                                            addCompAdditionalCharacteristic
                                        ).forEach((entity) => {
                                            const [entityKey, val] = entity
                                            const key =
                                                entityKey as keyof typeof UNITS
                                            if (val) {
                                                if (
                                                    summary &&
                                                    summary.includes(
                                                        `addCompAdditionalCharacteristic_${component}.${key}`
                                                    )
                                                ) {
                                                    if (
                                                        typeof val ===
                                                            'object' &&
                                                        val !== null
                                                    ) {
                                                        if (
                                                            'description' in val
                                                        ) {
                                                            if (
                                                                key ===
                                                                'defined'
                                                            ) {
                                                                abstractFeatures.push(
                                                                    `The ${
                                                                        'description' in
                                                                        val
                                                                            ? val.description
                                                                            : '__'
                                                                    }.`
                                                                )
                                                            }
                                                        } else {
                                                            if (
                                                                key ===
                                                                'thermalConductivity'
                                                            ) {
                                                                abstractFeatures.push(
                                                                    `The ${
                                                                        parentClaimAdditionalComponent.component
                                                                    } has a thermal conductivity from ${
                                                                        val.from ||
                                                                        '__'
                                                                    } to ${
                                                                        val.to ||
                                                                        '__'
                                                                    } ${
                                                                        UNITS[
                                                                            key
                                                                        ]
                                                                    }.`
                                                                )
                                                            }
                                                            if (
                                                                key ===
                                                                'meltingPoint'
                                                            ) {
                                                                abstractFeatures.push(
                                                                    `The ${
                                                                        parentClaimAdditionalComponent.component
                                                                    } has a melting point from ${
                                                                        val.from ||
                                                                        '__'
                                                                    } to ${
                                                                        val.to ||
                                                                        '__'
                                                                    } ${
                                                                        UNITS[
                                                                            key
                                                                        ]
                                                                    }.`
                                                                )
                                                            }
                                                            if (
                                                                key ===
                                                                'specificHeat'
                                                            ) {
                                                                abstractFeatures.push(
                                                                    `The ${
                                                                        parentClaimAdditionalComponent.component
                                                                    } has a specific heat from ${
                                                                        val.from ||
                                                                        '__'
                                                                    } to ${
                                                                        val.to ||
                                                                        '__'
                                                                    } ${
                                                                        UNITS[
                                                                            key
                                                                        ]
                                                                    }.`
                                                                )
                                                            }
                                                        }
                                                    }
                                                }
                                            }
                                        })
                                    }
                                    if (addCompFormatUserDefined) {
                                        Object.entries(
                                            addCompFormatUserDefined
                                        ).forEach((entity: any) => {
                                            const [entityKey, val] = entity
                                            const key = entityKey
                                            if (val) {
                                                if (
                                                    summary &&
                                                    summary.includes(
                                                        `addCompFormatUserDefined_${component}.${key}`
                                                    )
                                                ) {
                                                    if (
                                                        typeof val ===
                                                            'object' &&
                                                        val !== null
                                                    ) {
                                                        if (key === 'from') {
                                                            abstractFeatures.push(
                                                                `The ${parentClaimAdditionalComponent.component!} has a ${
                                                                    val.name ||
                                                                    '__'
                                                                } from ${
                                                                    val.minimum ||
                                                                    '__'
                                                                } to ${
                                                                    val.maximum ||
                                                                    '__'
                                                                } ${
                                                                    val.units ||
                                                                    ''
                                                                }.`
                                                            )
                                                        }
                                                        if (key === 'equal') {
                                                            abstractFeatures.push(
                                                                `The ${
                                                                    val.name ||
                                                                    '__'
                                                                } of the ${parentClaimAdditionalComponent.component!} is equal to from ${
                                                                    val.minimum ||
                                                                    '__'
                                                                } to ${
                                                                    val.maximum ||
                                                                    '__'
                                                                } ${
                                                                    val.units ||
                                                                    ''
                                                                }.`
                                                            )
                                                        }
                                                        if (key === 'defined') {
                                                            abstractFeatures.push(
                                                                `The ${
                                                                    val.description ||
                                                                    '___'
                                                                }.`
                                                            )
                                                        }
                                                    }
                                                }
                                            }
                                        })
                                    }
                                }
                            }
                        )
                    }

                    const importantComponentsConnections =
                        formRef.getFieldValue<
                            MachineValues['importantComponentsConnections']
                        >(['importantComponentsConnections'])
                    if (importantComponentsConnections != null) {
                        Object.entries(importantComponentsConnections).forEach(
                            (entry: any) => {
                                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                                const [key, val] = entry
                                let finalText: string | undefined
                                const notSet = val?.notSet
                                if (
                                    summary &&
                                    summary.includes(
                                        `importantComponentsConnections_${val.id}`
                                    )
                                ) {
                                    const firstComponent =
                                        val &&
                                        summary &&
                                        summary.includes(val['1stComponent']) &&
                                        extractComponentName(
                                            val['1stComponent'],
                                            formRef
                                        )
                                    const secondComponent =
                                        val &&
                                        summary &&
                                        summary.includes(val['2ndComponent']) &&
                                        extractComponentName(
                                            val['2ndComponent'],
                                            formRef
                                        )
                                    if (notSet) {
                                        const firstPart = val?.['1stPart']
                                        const secondPart = val?.['2ndPart']
                                        const thirdPart = val?.['3rdPart']
                                        if (
                                            firstPart &&
                                            firstComponent &&
                                            secondPart &&
                                            secondComponent &&
                                            thirdPart
                                        ) {
                                            finalText = `${firstPart} the ${firstComponent} ${secondPart} the ${secondComponent} ${
                                                thirdPart || '__'
                                            }.`
                                        }
                                    } else {
                                        const foundSubConnectionType =
                                            connectionRelation[
                                                val?.connectionType
                                            ]?.find((connection: any) => {
                                                return (
                                                    connection.value ===
                                                    val?.subConnectionType
                                                )
                                            })
                                        const connectionType =
                                            foundSubConnectionType
                                                ? val?.subConnectionType ||
                                                  val?.connectionType
                                                : val?.connectionType
                                        if (
                                            firstComponent &&
                                            connectionType &&
                                            secondComponent
                                        ) {
                                            finalText = `The ${firstComponent} is ${connectionType.toLowerCase()} the ${secondComponent}.`
                                        }
                                    }
                                    if (finalText) {
                                        abstractFeatures.push(finalText)
                                    }
                                }
                            }
                        )
                    }
                    const importantComponentsArrangements =
                        formRef.getFieldValue<
                            MachineValues['importantComponentsArrangements']
                        >(['importantComponentsArrangements'])
                    if (importantComponentsArrangements != null) {
                        Object.entries(importantComponentsArrangements).forEach(
                            (entry: any) => {
                                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                                const [key, val] = entry
                                let finalText: string | undefined
                                const notSet = val?.notSet
                                if (
                                    summary &&
                                    summary.includes(
                                        `importantComponentsArrangements_${val.id}`
                                    )
                                ) {
                                    const firstComponent =
                                        val &&
                                        summary &&
                                        summary.includes(val['1stComponent']) &&
                                        extractComponentName(
                                            val['1stComponent'],
                                            formRef
                                        )
                                    const secondComponent =
                                        val &&
                                        summary &&
                                        summary.includes(val['2ndComponent']) &&
                                        extractComponentName(
                                            val['2ndComponent'],
                                            formRef
                                        )
                                    if (notSet) {
                                        const firstPart = val?.['1stPart']
                                        const secondPart = val?.['2ndPart']
                                        const thirdPart = val?.['3rdPart']
                                        if (
                                            firstComponent &&
                                            secondPart &&
                                            secondComponent
                                        ) {
                                            finalText = `${
                                                firstPart
                                                    ? `${firstPart} the `
                                                    : ''
                                            }${firstComponent} ${secondPart} the ${secondComponent} ${
                                                thirdPart || '__'
                                            }.`
                                        }
                                    } else {
                                        const arrangementType =
                                            val?.arrangementType
                                        if (
                                            firstComponent &&
                                            arrangementType &&
                                            secondComponent
                                        ) {
                                            finalText = `The ${firstComponent} is ${arrangementType.toLowerCase()} the ${secondComponent}.`
                                        }
                                    }
                                    if (finalText) {
                                        abstractFeatures.push(finalText)
                                    }
                                }
                            }
                        )
                    }
                    const functionalConnections = formRef.getFieldValue<
                        MachineValues['functionalConnections']
                    >(['functionalConnections'])
                    if (functionalConnections != null) {
                        Object.entries(functionalConnections).forEach(
                            (entry: any) => {
                                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                                const [key, val] = entry
                                let finalText: string | undefined
                                const notSet = val?.notSet
                                if (
                                    summary &&
                                    summary.includes(
                                        `functionalConnections_${val.id}`
                                    )
                                ) {
                                    const firstComponent =
                                        val &&
                                        summary &&
                                        summary.includes(val['1stComponent']) &&
                                        extractComponentName(
                                            val['1stComponent'],
                                            formRef
                                        )
                                    const secondComponent =
                                        val &&
                                        summary &&
                                        summary.includes(val['2ndComponent']) &&
                                        extractComponentName(
                                            val['2ndComponent'],
                                            formRef
                                        )
                                    if (notSet) {
                                        const firstPart = val?.['1stPart']
                                        const secondPart = val?.['2ndPart']
                                        const thirdPart = val?.['3rdPart']
                                        if (
                                            firstComponent &&
                                            secondPart &&
                                            secondComponent
                                        ) {
                                            finalText = `${
                                                firstPart
                                                    ? `${firstPart} the `
                                                    : ''
                                            }${firstComponent} ${secondPart} the ${secondComponent} ${
                                                thirdPart || '__'
                                            }.`
                                        }
                                    } else if (
                                        firstComponent &&
                                        secondComponent
                                    ) {
                                        finalText = `The ${firstComponent} is functionally connected with the ${secondComponent}.`
                                    }
                                    if (finalText) {
                                        abstractFeatures.push(finalText)
                                    }
                                }
                            }
                        )
                    }

                    summaryText = `In a first aspect, the invention comprises a machine. The machine comprises ${abstractComponents
                        .join(', ')
                        .replace(
                            /,(?=[^,]*$)/,
                            ' and'
                        )}. ${abstractSubComponents.join(
                        ' '
                    )} ${abstractFeatures.join(' ')}`

                    summaryArr.push(
                        new Paragraph({
                            heading: HeadingLevel.HEADING_6,
                            alignment: AlignmentType.LEFT,
                            ...(isDownload
                                ? {
                                      numbering: {
                                          reference: 'my-reference',
                                          level: 0,
                                      },
                                  }
                                : {}),
                            children: [
                                ...(!isDownload
                                    ? [
                                          new TextRun({
                                              text: `[${counter}]    `,
                                              color: '000000',
                                              size: 16,
                                          }),
                                      ]
                                    : []),
                                new TextRun({
                                    text: summaryText,
                                    color: '000000',
                                    size: 16,
                                }),
                            ],
                        })
                    )
                } else {
                    summaryArr.push(
                        new Paragraph({
                            heading: HeadingLevel.HEADING_6,
                            alignment: AlignmentType.LEFT,
                            ...(isDownload
                                ? {
                                      numbering: {
                                          reference: 'my-reference',
                                          level: 0,
                                      },
                                  }
                                : {}),
                            children: [
                                ...(!isDownload
                                    ? [
                                          new TextRun({
                                              text: `[${counter}]    `,
                                              color: '000000',
                                              size: 16,
                                          }),
                                      ]
                                    : []),
                                new TextRun({
                                    text: ``,
                                    color: '000000',
                                    size: 16,
                                }),
                            ],
                        })
                    )
                }
                /* FIX: Replaced static abstract retrieval with dynamic generation 
                   based on Independent Claim 1 components.
                */
                // Generate the Abstract text if components exist
                if (summaryText && summaryText.length > 0) {
                    // Update the form so UI reflects the dynamic abstract
                    formRef.setFieldsValue({
                        abstractOfTheDisclosure: {
                            defined: summaryText,
                        },
                    })
                    abstractArr.push(
                        new Paragraph({
                            heading: HeadingLevel.HEADING_1,
                            alignment: AlignmentType.CENTER,
                            spacing: {
                                before: 250,
                                after: 250,
                            },
                            children: [
                                new TextRun({
                                    text: `ABSTRACT OF THE DISCLOSURE`,
                                    color: '000000',
                                    size: 16,
                                    // bold: true,
                                    underline: {
                                        type: UnderlineType.SINGLE,
                                        color: '000000',
                                    },
                                }),
                            ],
                        })
                    )
                    abstractArr.push(
                        new Paragraph({
                            heading: HeadingLevel.HEADING_6,
                            alignment: AlignmentType.LEFT,
                            children: [
                                new TextRun({
                                    text: summaryText,
                                    color: '000000',
                                    size: 16,
                                }),
                            ],
                        })
                    )
                }
            }
        }
        if (
            question === MethodsNames.methodOutline &&
            isMethodsForm(formRef, question)
        ) {
            const steps = formRef.getFieldValue<MethodsForm['methodOutline']>(
                MethodsNames.methodOutline
            )
            if (steps) {
                arr.push(
                    new Paragraph({
                        heading: HeadingLevel.HEADING_1,
                        alignment: AlignmentType.CENTER,
                        spacing: {
                            before: 250,
                            after: 250,
                        },
                        children: [
                            new TextRun({
                                text: `CLAIMS`,
                                color: '000000',
                                size: 16,
                                // bold: true,
                                underline: {
                                    type: UnderlineType.SINGLE,
                                    color: '000000',
                                },
                            }),
                        ],
                    })
                )
                arr.push(
                    new Paragraph({
                        heading: HeadingLevel.HEADING_6,
                        alignment: AlignmentType.LEFT,
                        children: [
                            new TextRun({
                                text: `What is claimed is:`,
                                color: '000000',
                                size: 16,
                            }),
                        ],
                    })
                )
                claimsCounter++
                arr.push(
                    new Paragraph({
                        heading: HeadingLevel.HEADING_6,
                        alignment: AlignmentType.LEFT,
                        ...(!isDownload
                            ? {}
                            : {
                                  numbering: {
                                      reference: 'claims-numbering',
                                      level: 0,
                                  },
                              }),
                        children: [
                            ...(!isDownload
                                ? [
                                      new TextRun({
                                          text: `${claimsCounter}. `,
                                          color: '000000',
                                          size: 16,
                                      }),
                                  ]
                                : []),
                            new TextRun({
                                text: `A ${category} comprising:`,
                                color: '000000',
                                size: 16,
                            }),
                        ],
                    })
                )
                const filteredSteps = steps.filter((step) => step !== undefined)
                filteredSteps.forEach((step, i: number) => {
                    let lastButOne = false
                    let last = false
                    const { methodStep: name } = step
                    if (i === filteredSteps.length - 2) {
                        lastButOne = true
                    }
                    if (i === filteredSteps.length - 1) {
                        last = true
                    }
                    arr.push(
                        new Paragraph({
                            heading: HeadingLevel.HEADING_6,
                            alignment: AlignmentType.LEFT,
                            children: [
                                new TextRun({
                                    text: `     `,
                                    color: '000000',
                                    size: 16,
                                    // bold: true,
                                }),
                                new TextRun({
                                    text: `${name || ''}${last ? '.' : ';'} ${
                                        lastButOne ? 'and' : ''
                                    }`,
                                    color: '000000',
                                    size: 16,
                                }),
                            ],
                        })
                    )
                })
            }
        }
        if (
            question === 'methodOutlineDescription' &&
            isMethodsForm(formRef, question)
        ) {
            const steps =
                formRef.getFieldValue<MethodsForm['methodOutline']>(
                    'methodOutline'
                )
            const stepsDescription = formRef.getFieldValue<
                MethodsForm['methodOutlineDescription']
            >('methodOutlineDescription')

            if (stepsDescription) {
                for (let i = steps.length - 1; i >= 0; i--) {
                    const { methodStep: step } = steps[i]
                    let lastButOne = false
                    let last = false
                    if (i === steps.length - 2) {
                        lastButOne = true
                    }
                    if (i === steps.length - 1) {
                        last = true
                    }
                    if (stepsDescription[step]) {
                        if (stepsDescription[step].finalSentence) {
                            const { finalSentence } = stepsDescription[step]
                            arr[arr.length + (i - steps.length)] =
                                new Paragraph({
                                    heading: HeadingLevel.HEADING_6,
                                    alignment: AlignmentType.LEFT,
                                    children: [
                                        new TextRun({
                                            text: `     `,
                                            color: '000000',
                                            size: 16,
                                            // bold: true,
                                        }),
                                        new TextRun({
                                            text: `${finalSentence}${
                                                last ? '.' : ';'
                                            } ${lastButOne ? 'and' : ''}`,
                                            color: '000000',
                                            size: 16,
                                        }),
                                    ],
                                })
                        }
                    }
                }
            }
        }
        if (
            (question === MachineNames.primaryComponents ||
                question === 'partsOfIndependentClaim' ||
                question === 'summaryOfSelectedEmbodimentsOfTheInvention') &&
            isMachineForm(formRef, question)
        ) {
            const primaryComponents = formRef.getFieldValue<
                MachineForm['primaryComponents']
            >(MachineNames.primaryComponents)

            if (primaryComponents) {
                const summaryOfSelectedEmbodimentsOfTheInvention =
                    formRef.getFieldValue<
                        FormValues['summaryOfSelectedEmbodimentsOfTheInvention']
                    >(['summaryOfSelectedEmbodimentsOfTheInvention'])
                const importantComponents = formRef.getFieldValue<
                    ManufactureValues['importantComponents']
                >(['importantComponents'])
                const partsOfIndependentClaim = formRef.getFieldValue<
                    MachineValues['partsOfIndependentClaim']
                >(['partsOfIndependentClaim'])
                parentClaimPrimaryComponents = primaryComponents.filter(
                    (item) => {
                        if (
                            summaryOfSelectedEmbodimentsOfTheInvention &&
                            item !== undefined
                        ) {
                            return summaryOfSelectedEmbodimentsOfTheInvention.includes(
                                item.id ?? ''
                            )
                        }
                        if (importantComponents && item !== undefined) {
                            return importantComponents.includes(
                                item.component ?? ''
                            )
                        }
                        if (partsOfIndependentClaim && item !== undefined) {
                            return partsOfIndependentClaim.includes(
                                item.id ?? ''
                            )
                        }
                        return item !== undefined
                    }
                )
                let components: (
                    | PrimaryComponent
                    | PrimaryAdditionalComponent
                )[] = []
                components = components.concat(parentClaimPrimaryComponents)
                if (parentClaimAdditionalComponents.length > 0) {
                    components = components.concat(
                        parentClaimAdditionalComponents
                    )
                }
                if (
                    question === 'partsOfIndependentClaim' ||
                    question === 'summaryOfSelectedEmbodimentsOfTheInvention'
                ) {
                    parentClaimPrimaryComponents.forEach((item, i: number) => {
                        delete parentClaimArr[3 + i]
                    })
                }
                if (
                    components.length > 0 &&
                    question === MachineNames.primaryComponents
                ) {
                    parentClaimArr.push(
                        new Paragraph({
                            heading: HeadingLevel.HEADING_1,
                            alignment: AlignmentType.CENTER,
                            spacing: {
                                before: 250,
                                after: 250,
                            },
                            children: [
                                new TextRun({
                                    text: `CLAIMS`,
                                    color: '000000',
                                    size: 16,
                                    // bold: true,
                                    underline: {
                                        type: UnderlineType.SINGLE,
                                        color: '000000',
                                    },
                                }),
                            ],
                        })
                    )
                    parentClaimArr.push(
                        new Paragraph({
                            heading: HeadingLevel.HEADING_6,
                            alignment: AlignmentType.LEFT,
                            children: [
                                new TextRun({
                                    text: `What is claimed is:`,
                                    color: '000000',
                                    size: 16,
                                }),
                            ],
                        })
                    )
                    claimsCounter++
                    parentClaimArr.push(
                        new Paragraph({
                            heading: HeadingLevel.HEADING_6,
                            alignment: AlignmentType.LEFT,
                            ...(!isDownload
                                ? {}
                                : {
                                      numbering: {
                                          reference: 'claims-numbering',
                                          level: 0,
                                      },
                                  }),
                            children: [
                                ...(!isDownload
                                    ? [
                                          new TextRun({
                                              text: `${claimsCounter}. `,
                                              color: '000000',
                                              size: 16,
                                          }),
                                      ]
                                    : []),
                                new TextRun({
                                    text: `A ${category} comprising:`,
                                    color: '000000',
                                    size: 16,
                                }),
                            ],
                        })
                    )
                }
                components.forEach((item, i: number) => {
                    let lastButOne = false
                    let last = false
                    const { indefiniteArticle, component } = item
                    if (i === components.length - 2) {
                        lastButOne = true
                    }
                    if (i === components.length - 1) {
                        last = true
                    }
                    parentClaimArr.splice(
                        3 + i,
                        0,
                        new Paragraph({
                            heading: HeadingLevel.HEADING_6,
                            alignment: AlignmentType.LEFT,
                            children: [
                                new TextRun({
                                    text: `${
                                        (indefiniteArticle || '').trim()
                                            ? `${(
                                                  indefiniteArticle || ''
                                              ).trim()} `
                                            : ''
                                    }${lowerfirst((component || '').trim())}${
                                        last ? '.' : ';'
                                    }${lastButOne ? ' and' : ''}`,
                                    size: 16,
                                    color: '000000',
                                }),
                            ],
                        })
                    )
                })
                if (question === MachineNames.primaryComponents) {
                    const parentClaimPrimaryComponentsIdList =
                        parentClaimPrimaryComponents.map((comp) => comp.id)
                    const childClaimPrimaryComponents =
                        primaryComponents.filter((item) => {
                            if (
                                parentClaimPrimaryComponentsIdList &&
                                item !== undefined
                            ) {
                                return !parentClaimPrimaryComponentsIdList.includes(
                                    item.id ?? ''
                                )
                            }
                            return item !== undefined
                        })
                    if (childClaimPrimaryComponents.length > 0) {
                        claimsCounter++
                        childClaimPrimaryComponents.forEach(
                            (component, i: number) => {
                                registerClaim(claimsCounter, component.id ?? '')
                                childClaimArr.push(
                                    new Paragraph({
                                        heading: HeadingLevel.HEADING_6,
                                        alignment: AlignmentType.LEFT,
                                        ...(!isDownload
                                            ? {}
                                            : {
                                                  numbering: {
                                                      reference:
                                                          'claims-numbering',
                                                      level: 0,
                                                  },
                                              }),
                                        children: [
                                            ...(!isDownload
                                                ? [
                                                      new TextRun({
                                                          text: `${claimsCounter}. `,
                                                          color: '000000',
                                                          size: 16,
                                                      }),
                                                  ]
                                                : []),
                                            ...buildClaimRuns(
                                                `The ${category} of claim 1, wherein the ${category} comprises:`
                                            ),
                                        ],
                                    })
                                )
                                childClaimArr.push(
                                    new Paragraph({
                                        heading: HeadingLevel.HEADING_6,
                                        alignment: AlignmentType.LEFT,
                                        children: [
                                            new TextRun({
                                                text: `${
                                                    (
                                                        component.indefiniteArticle ||
                                                        ''
                                                    ).trim()
                                                        ? `${(
                                                              component.indefiniteArticle ||
                                                              ''
                                                          ).trim()} `
                                                        : ''
                                                }${(
                                                    component.component || ''
                                                ).trim()}.`,
                                                color: '000000',
                                                size: 16,
                                            }),
                                        ],
                                    })
                                )
                                claimsCounter++
                            }
                        )
                        claimsCounter--
                    }
                }
                let text = ''
                const commonName = formRef.getFieldValue(
                    SharedNames.commonName
                ) as FormValues['commonName']

                if (commonName.notSet) {
                    text = 'no common name'
                } else {
                    const { indefiniteArticle: article, commonName: name } =
                        commonName
                    text = `${article} ${name}`
                }

                if (
                    components.length > 0 &&
                    question === MachineNames.primaryComponents
                ) {
                    claimsCounter++
                    childClaimArr.push(
                        new Paragraph({
                            heading: HeadingLevel.HEADING_6,
                            alignment: AlignmentType.LEFT,
                            ...(!isDownload
                                ? {}
                                : {
                                      numbering: {
                                          reference: 'claims-numbering',
                                          level: 0,
                                      },
                                  }),
                            children: [
                                ...(!isDownload
                                    ? [
                                          new TextRun({
                                              text: `${claimsCounter}. `,
                                              color: '000000',
                                              size: 16,
                                          }),
                                      ]
                                    : []),
                                ...buildClaimRuns(
                                    `The ${category} of claim 1, wherein the ${category} is ${text.trim()}.`
                                ),
                            ],
                        })
                    )
                }
            }
        }
        /* TEST */
        if (
            question === MachineNames.primaryAdditionalComponents &&
            isMachineForm(formRef, question)
        ) {
            const additionalComponents = formRef.getFieldValue<
                MachineForm['primaryAdditionalComponents']
            >(MachineNames.primaryAdditionalComponents)

            if (additionalComponents) {
                const summaryOfSelectedEmbodimentsOfTheInvention =
                    formRef.getFieldValue<
                        FormValues['summaryOfSelectedEmbodimentsOfTheInvention']
                    >(['summaryOfSelectedEmbodimentsOfTheInvention'])
                const partsOfIndependentClaim = formRef.getFieldValue<
                    MachineValues['partsOfIndependentClaim']
                >(['partsOfIndependentClaim'])
                parentClaimAdditionalComponents = additionalComponents.filter(
                    (item) => {
                        if (
                            summaryOfSelectedEmbodimentsOfTheInvention &&
                            item !== undefined
                        ) {
                            return summaryOfSelectedEmbodimentsOfTheInvention.includes(
                                `add_${item.id}`
                            )
                        }
                        if (partsOfIndependentClaim && item !== undefined) {
                            return partsOfIndependentClaim.includes(
                                `add_${item.id}`
                            )
                        }
                        return false
                    }
                )
                const parentClaimAdditionalComponentsIdList =
                    parentClaimAdditionalComponents.map((comp) => comp.id)
                const childClaimAdditionalComponents =
                    additionalComponents.filter((item) => {
                        if (
                            parentClaimAdditionalComponentsIdList &&
                            item !== undefined
                        ) {
                            return !parentClaimAdditionalComponentsIdList.includes(
                                item.id ?? ''
                            )
                        }
                        return item !== undefined
                    })
                if (childClaimAdditionalComponents.length > 0) {
                    claimsCounter++
                    childClaimAdditionalComponents.forEach(
                        (additionalComponent, i: number) => {
                            const { component } = additionalComponent
                            const indefiniteArticle =
                                additionalComponent.indefiniteArticle ?? ''
                            registerClaim(
                                claimsCounter,
                                additionalComponent.id ?? ''
                            )
                            childClaimArr.push(
                                new Paragraph({
                                    heading: HeadingLevel.HEADING_6,
                                    alignment: AlignmentType.LEFT,
                                    ...(!isDownload
                                        ? {}
                                        : {
                                              numbering: {
                                                  reference: 'claims-numbering',
                                                  level: 0,
                                              },
                                          }),
                                    children: [
                                        ...(!isDownload
                                            ? [
                                                  new TextRun({
                                                      text: `${claimsCounter}. `,
                                                      color: '000000',
                                                      size: 16,
                                                  }),
                                              ]
                                            : []),
                                        ...buildClaimRuns(
                                            `The ${category} of claim 1, wherein the ${category} comprises:`
                                        ),
                                    ],
                                })
                            )
                            childClaimArr.push(
                                new Paragraph({
                                    heading: HeadingLevel.HEADING_6,
                                    alignment: AlignmentType.LEFT,
                                    children: [
                                        new TextRun({
                                            text: `${
                                                (indefiniteArticle || '').trim()
                                                    ? `${(
                                                          indefiniteArticle ||
                                                          ''
                                                      ).trim()} `
                                                    : ''
                                            }${lowerfirst(
                                                (component || '').trim()
                                            )}.`,
                                            color: '000000',
                                            size: 16,
                                        }),
                                    ],
                                })
                            )
                            claimsCounter++
                        }
                    )
                    claimsCounter--
                }
            }
        }
        /* TESTEND */
        if (
            question === MachineNames.primaryComponentSubComponentsList &&
            pushSubcomponents &&
            isMachineForm(formRef, question)
        ) {
            pushSubcomponents = false
            const list =
                formRef.getFieldValue<MachineForm['mainSubcomponents']>(
                    MachineNames.mainSubcomponents
                ) ?? []
            const summaryOfSelectedEmbodimentsOfTheInvention =
                formRef.getFieldValue<
                    FormValues['summaryOfSelectedEmbodimentsOfTheInvention']
                >(['summaryOfSelectedEmbodimentsOfTheInvention'])
            const partsOfIndependentClaim = formRef.getFieldValue<
                MachineValues['partsOfIndependentClaim']
            >(['partsOfIndependentClaim'])
            parentClaimSubComponentsIdList = []
            list.forEach((mainComponent) => {
                const allComponents = formRef.getFieldValue<
                    MachineForm['primaryComponents']
                >(MachineNames.primaryComponents)
                const componentData = allComponents.find(
                    (x) => x?.id === mainComponent
                )

                if (componentData) {
                    if (
                        isMachineForm(formRef, question) ||
                        isMethodsForm(formRef, question) ||
                        isManufactureForm(formRef, question)
                    ) {
                        const subComponents = formRef.getFieldValue([
                            MachineNames.primaryComponentSubComponentsList,
                            mainComponent,
                        ]) as ValueOf<
                            MachineForm['primaryComponentSubComponentsList']
                        >

                        if (subComponents) {
                            const primaryComponentData =
                                parentClaimPrimaryComponents.find(
                                    (x) => x?.id === mainComponent
                                )
                            if (primaryComponentData) {
                                const parentClaimSubComponents =
                                    subComponents.filter((item) => {
                                        if (
                                            summaryOfSelectedEmbodimentsOfTheInvention &&
                                            item !== undefined
                                        ) {
                                            return summaryOfSelectedEmbodimentsOfTheInvention.includes(
                                                `${componentData.id}+=>${item.id}`
                                            )
                                        }
                                        if (
                                            partsOfIndependentClaim &&
                                            item !== undefined
                                        ) {
                                            return partsOfIndependentClaim.includes(
                                                `${componentData.id}+=>${item.id}`
                                            )
                                        }
                                        return false
                                    })
                                if (parentClaimSubComponents.length > 0) {
                                    parentClaimSubComponentsIdList =
                                        parentClaimSubComponentsIdList.concat(
                                            parentClaimSubComponents.map(
                                                (comp) => comp.id
                                            )
                                        )
                                    parentClaimArr.push(
                                        new Paragraph({
                                            heading: HeadingLevel.HEADING_6,
                                            alignment: AlignmentType.LEFT,
                                            children: [
                                                new TextRun({
                                                    text: `wherein the ${componentData.component!} comprises:`,
                                                    color: '000000',
                                                    size: 16,
                                                }),
                                            ],
                                        })
                                    )
                                    parentClaimSubComponents.forEach(
                                        (subComponent, i: number) => {
                                            const { component } = subComponent
                                            const indefiniteArticle =
                                                subComponent.indefiniteArticle ??
                                                ''
                                            let lastButOne = false
                                            let last = false
                                            if (
                                                i ===
                                                parentClaimSubComponents.length -
                                                    2
                                            ) {
                                                lastButOne = true
                                            }
                                            if (
                                                i ===
                                                parentClaimSubComponents.length -
                                                    1
                                            ) {
                                                last = true
                                            }
                                            parentClaimArr.push(
                                                new Paragraph({
                                                    heading:
                                                        HeadingLevel.HEADING_6,
                                                    alignment:
                                                        AlignmentType.LEFT,
                                                    children: [
                                                        new TextRun({
                                                            text: `${
                                                                (
                                                                    indefiniteArticle ||
                                                                    ''
                                                                ).trim()
                                                                    ? `${(
                                                                          indefiniteArticle ||
                                                                          ''
                                                                      ).trim()} `
                                                                    : ''
                                                            }${(
                                                                component || ''
                                                            ).trim()}${
                                                                last ? '.' : ';'
                                                            }${
                                                                lastButOne
                                                                    ? ' and'
                                                                    : ''
                                                            }`,
                                                            color: '000000',
                                                            size: 16,
                                                        }),
                                                    ],
                                                })
                                            )
                                        }
                                    )
                                }
                            }
                            const childClaimSubComponents =
                                subComponents.filter((item) => {
                                    if (
                                        parentClaimSubComponentsIdList &&
                                        item !== undefined
                                    ) {
                                        return !parentClaimSubComponentsIdList.includes(
                                            item.id ?? ''
                                        )
                                    }
                                    return item !== undefined
                                })
                            if (childClaimSubComponents.length > 0) {
                                claimsCounter++
                                childClaimSubComponents.forEach(
                                    (subComponent, i: number) => {
                                        const { component } = subComponent
                                        const indefiniteArticle =
                                            subComponent.indefiniteArticle ?? ''
                                        registerClaim(
                                            claimsCounter,
                                            `${mainComponent}+=>${
                                                subComponent.id ?? ''
                                            }`
                                        )
                                        childClaimArr.push(
                                            new Paragraph({
                                                heading: HeadingLevel.HEADING_6,
                                                alignment: AlignmentType.LEFT,
                                                ...(!isDownload
                                                    ? {}
                                                    : {
                                                          numbering: {
                                                              reference:
                                                                  'claims-numbering',
                                                              level: 0,
                                                          },
                                                      }),
                                                children: [
                                                    ...(!isDownload
                                                        ? [
                                                              new TextRun({
                                                                  text: `${claimsCounter}. `,
                                                                  color: '000000',
                                                                  size: 16,
                                                              }),
                                                          ]
                                                        : []),
                                                    ...buildClaimRuns(
                                                        `The ${category} of claim ${getClaimForFeature(
                                                            componentData.id!
                                                        )}, wherein the ${componentData.component!} comprises:`
                                                    ),
                                                ],
                                            })
                                        )
                                        childClaimArr.push(
                                            new Paragraph({
                                                heading: HeadingLevel.HEADING_6,
                                                alignment: AlignmentType.LEFT,
                                                children: [
                                                    new TextRun({
                                                        text: `${
                                                            (
                                                                indefiniteArticle ||
                                                                ''
                                                            ).trim()
                                                                ? `${(
                                                                      indefiniteArticle ||
                                                                      ''
                                                                  ).trim()} `
                                                                : ''
                                                        }${(
                                                            component || ''
                                                        ).trim()}.`,
                                                        color: '000000',
                                                        size: 16,
                                                    }),
                                                ],
                                            })
                                        )
                                        claimsCounter++
                                    }
                                )
                                claimsCounter--
                            }
                        }
                    }
                }
            })
        }
        if (
            question === MachineNames.characteristic &&
            pushCharacteristics &&
            isMachineForm(formRef, question)
        ) {
            pushCharacteristics = false
            const primaryComponents = formRef.getFieldValue<
                MachineForm['primaryComponents']
            >(['primaryComponents'])
            const characteristicComponents = formRef.getFieldValue(
                MachineNames.componentsForCharacteristics
            ) as MachineValues['componentsForCharacteristics']
            const featuresPartsOfIndependentClaim = formRef.getFieldValue<
                MachineValues['featuresPartsOfIndependentClaim']
            >(['featuresPartsOfIndependentClaim'])
            const summaryOfSelectedEmbodimentsOfTheInvention =
                formRef.getFieldValue<
                    FormValues['summaryOfSelectedEmbodimentsOfTheInvention']
                >(['summaryOfSelectedEmbodimentsOfTheInvention'])
            characteristicComponents.forEach((component: string) => {
                const foundComponent = primaryComponents.find(
                    (x) => x?.id === component
                )
                if (foundComponent) {
                    const characteristic = formRef.getFieldValue([
                        MachineNames.characteristic,
                        component,
                    ]) as ValueOf<MachineValues['characteristic']>
                    if (characteristic) {
                        Object.entries(characteristic).forEach((entity) => {
                            const [entityKey, val] = entity
                            const key = entityKey as keyof typeof UNITS
                            if (val) {
                                const parentClaimPrimaryComponent =
                                    parentClaimPrimaryComponents.find(
                                        (x) => x?.id === component
                                    )
                                if (
                                    parentClaimPrimaryComponent &&
                                    (!summaryOfSelectedEmbodimentsOfTheInvention ||
                                        summaryOfSelectedEmbodimentsOfTheInvention.includes(
                                            `characteristic_${component}.${key}`
                                        )) &&
                                    (summaryOfSelectedEmbodimentsOfTheInvention ||
                                        !featuresPartsOfIndependentClaim ||
                                        featuresPartsOfIndependentClaim.includes(
                                            `characteristic_${component}.${key}`
                                        ))
                                ) {
                                    if (
                                        typeof val === 'object' &&
                                        val !== null
                                    ) {
                                        if (key === 'defined') {
                                            parentClaimArr.push(
                                                new Paragraph({
                                                    heading:
                                                        HeadingLevel.HEADING_6,
                                                    alignment:
                                                        AlignmentType.LEFT,
                                                    children: [
                                                        new TextRun({
                                                            text: `wherein the ${
                                                                'description' in
                                                                val
                                                                    ? val.description
                                                                    : '__'
                                                            }.`,
                                                            color: '000000',
                                                            size: 16,
                                                        }),
                                                    ],
                                                })
                                            )
                                        } else {
                                            parentClaimArr.push(
                                                new Paragraph({
                                                    heading:
                                                        HeadingLevel.HEADING_6,
                                                    alignment:
                                                        AlignmentType.LEFT,
                                                    children: [
                                                        new TextRun({
                                                            text: `wherein the ${lowerfirst(
                                                                foundComponent.component!
                                                            )} has a ${key} from ${
                                                                'from' in val
                                                                    ? val.from
                                                                    : '__'
                                                            } to ${
                                                                'to' in val
                                                                    ? val.to
                                                                    : '__'
                                                            } ${UNITS[key]}.`,
                                                            color: '000000',
                                                            size: 16,
                                                        }),
                                                    ],
                                                })
                                            )
                                        }
                                    } else if (
                                        typeof val !== 'object' &&
                                        val === null
                                    ) {
                                        if (key === 'defined') {
                                            parentClaimArr.push(
                                                new Paragraph({
                                                    heading:
                                                        HeadingLevel.HEADING_6,
                                                    alignment:
                                                        AlignmentType.LEFT,
                                                    children: [
                                                        new TextRun({
                                                            text: `wherein the ___.`,
                                                            color: '000000',
                                                            size: 16,
                                                        }),
                                                    ],
                                                })
                                            )
                                        } else {
                                            parentClaimArr.push(
                                                new Paragraph({
                                                    heading:
                                                        HeadingLevel.HEADING_6,
                                                    alignment:
                                                        AlignmentType.LEFT,
                                                    children: [
                                                        new TextRun({
                                                            text: `wherein the ${lowerfirst(
                                                                foundComponent.component!
                                                            )} has a ${key} from __ to __ ${
                                                                UNITS[key]
                                                            }.`,
                                                            color: '000000',
                                                            size: 16,
                                                        }),
                                                    ],
                                                })
                                            )
                                        }
                                    }
                                } else {
                                    claimsCounter++
                                    if (
                                        typeof val === 'object' &&
                                        val !== null
                                    ) {
                                        if (key === 'defined') {
                                            childClaimArr.push(
                                                new Paragraph({
                                                    heading:
                                                        HeadingLevel.HEADING_6,
                                                    alignment:
                                                        AlignmentType.LEFT,
                                                    ...(!isDownload
                                                        ? {}
                                                        : {
                                                              numbering: {
                                                                  reference:
                                                                      'claims-numbering',
                                                                  level: 0,
                                                              },
                                                          }),
                                                    children: [
                                                        ...(!isDownload
                                                            ? [
                                                                  new TextRun({
                                                                      text: `${claimsCounter}. `,
                                                                      color: '000000',
                                                                      size: 16,
                                                                  }),
                                                              ]
                                                            : []),
                                                        ...buildClaimRuns(
                                                            `The ${category}  of claim ${getClaimForFeature(
                                                                component
                                                            )}, wherein the ${
                                                                'description' in
                                                                val
                                                                    ? val.description
                                                                    : '__'
                                                            }.`
                                                        ),
                                                    ],
                                                })
                                            )
                                        } else {
                                            childClaimArr.push(
                                                new Paragraph({
                                                    heading:
                                                        HeadingLevel.HEADING_6,
                                                    alignment:
                                                        AlignmentType.LEFT,
                                                    ...(!isDownload
                                                        ? {}
                                                        : {
                                                              numbering: {
                                                                  reference:
                                                                      'claims-numbering',
                                                                  level: 0,
                                                              },
                                                          }),
                                                    children: [
                                                        ...(!isDownload
                                                            ? [
                                                                  new TextRun({
                                                                      text: `${claimsCounter}. `,
                                                                      color: '000000',
                                                                      size: 16,
                                                                  }),
                                                              ]
                                                            : []),
                                                        ...buildClaimRuns(
                                                            `The ${category} of claim ${getClaimForFeature(
                                                                component
                                                            )}, wherein the ${lowerfirst(
                                                                foundComponent.component!
                                                            )} has a ${key} from ${
                                                                'from' in val
                                                                    ? val.from
                                                                    : '__'
                                                            } to ${
                                                                'to' in val
                                                                    ? val.to
                                                                    : '__'
                                                            } ${UNITS[key]}.`
                                                        ),
                                                    ],
                                                })
                                            )
                                        }
                                    } else if (
                                        typeof val !== 'object' &&
                                        val === null
                                    ) {
                                        if (key === 'defined') {
                                            childClaimArr.push(
                                                new Paragraph({
                                                    heading:
                                                        HeadingLevel.HEADING_6,
                                                    alignment:
                                                        AlignmentType.LEFT,
                                                    ...(!isDownload
                                                        ? {}
                                                        : {
                                                              numbering: {
                                                                  reference:
                                                                      'claims-numbering',
                                                                  level: 0,
                                                              },
                                                          }),
                                                    children: [
                                                        ...(!isDownload
                                                            ? [
                                                                  new TextRun({
                                                                      text: `${claimsCounter}. `,
                                                                      color: '000000',
                                                                      size: 16,
                                                                  }),
                                                              ]
                                                            : []),
                                                        ...buildClaimRuns(
                                                            `The ${category} of claim ${getClaimForFeature(
                                                                component
                                                            )}, wherein the ___.`
                                                        ),
                                                    ],
                                                })
                                            )
                                        } else {
                                            childClaimArr.push(
                                                new Paragraph({
                                                    heading:
                                                        HeadingLevel.HEADING_6,
                                                    alignment:
                                                        AlignmentType.LEFT,
                                                    ...(!isDownload
                                                        ? {}
                                                        : {
                                                              numbering: {
                                                                  reference:
                                                                      'claims-numbering',
                                                                  level: 0,
                                                              },
                                                          }),
                                                    children: [
                                                        ...(!isDownload
                                                            ? [
                                                                  new TextRun({
                                                                      text: `${claimsCounter}. `,
                                                                      color: '000000',
                                                                      size: 16,
                                                                  }),
                                                              ]
                                                            : []),
                                                        ...buildClaimRuns(
                                                            `The ${category} of claim ${getClaimForFeature(
                                                                component
                                                            )}, wherein the ${lowerfirst(
                                                                foundComponent.component!
                                                            )} has a ${key} from __ to __ ${
                                                                UNITS[key]
                                                            }.`
                                                        ),
                                                    ],
                                                })
                                            )
                                        }
                                    }
                                }
                            }
                        })
                    }
                }
            })
        }
        if (question === 'characteristicCompositon' && pushCharacteristics) {
            let letAll = false
            const importantCharacteristic = (formRef as any).getFieldValue([
                'importantCharacteristic',
            ])
            if (!importantCharacteristic) {
                letAll = true
            }
            const characteristic = (formRef as any).getFieldValue(
                'characteristicCompositon'
            )
            if (characteristic) {
                Object.entries(characteristic).forEach((entity: any) => {
                    const [entityKey, val] = entity
                    const key = entityKey as keyof typeof UNITS
                    const minAcc = val['min-acceptable']
                    const maxAcc = val['max-acceptable']
                    const acc = minAcc || maxAcc
                    const minPref = val['min-preferable']
                    const maxPref = val['max-preferable']
                    const pref = minPref || maxPref
                    let text = ''
                    const commonName =
                        formRef.getFieldValue<FormValues['commonName']>(
                            'commonName'
                        )
                    if (commonName.notSet) text = 'COMMON NAME'
                    else {
                        const { commonName: name } = commonName
                        text = name
                    }
                    claimsCounter++
                    let additionalText = ''
                    let pushParagraph = false
                    Object.entries(val).forEach((item: any) => {
                        const [, value] = item
                        if (
                            value &&
                            (importantCharacteristic?.includes(key) || letAll)
                        ) {
                            pushParagraph = true
                        }
                    })
                    if (pushParagraph) {
                        if (key === 'brinell') {
                            additionalText = `Measured using ${
                                val.condition || '__'
                            }.`
                        }
                        if (key === 'viscosity') {
                            additionalText = `Pa*s at temperature equals ${
                                val.temperature || '__'
                            } [C] and Pa*s at shear rate is equal to ${
                                val.rate || '__'
                            } [s^-1].`
                        }
                        childClaimArr.push(
                            new Paragraph({
                                heading: HeadingLevel.HEADING_6,
                                alignment: AlignmentType.LEFT,
                                ...(!isDownload
                                    ? {}
                                    : {
                                          numbering: {
                                              reference: 'claims-numbering',
                                              level: 0,
                                          },
                                      }),
                                children: [
                                    ...(!isDownload
                                        ? [
                                              new TextRun({
                                                  text: `${claimsCounter}. `,
                                                  color: '000000',
                                                  size: 16,
                                              }),
                                          ]
                                        : []),
                                    new TextRun({
                                        text: `The ${text} having a ${
                                            acc
                                                ? `acceptable ${key} from ${
                                                      minAcc || '__'
                                                  } to ${maxAcc || '__'} ${
                                                      UNITS[key]
                                                  }`
                                                : ''
                                        } 
                                        ${acc && pref ? 'and' : ''} 
                                        ${
                                            pref
                                                ? `preferable ${key} from ${
                                                      minPref || '__'
                                                  } to ${maxPref || '__'} ${
                                                      UNITS[key]
                                                  }. ${additionalText}`
                                                : ''
                                        }`,
                                        color: '000000',
                                        size: 16,
                                    }),
                                ],
                            })
                        )
                    }
                })
            }
            const customCharacteristic = (formRef as any).getFieldValue(
                'characteristicCustom'
            )
            if (customCharacteristic) {
                Object.entries(customCharacteristic).forEach((entity: any) => {
                    const [entityKey, val] = entity
                    const key = entityKey
                    const minAcc = val['min-acceptable']
                    const maxAcc = val['max-acceptable']
                    const acc = minAcc || maxAcc
                    const minPref = val['min-preferable']
                    const maxPref = val['max-preferable']
                    const pref = minPref || maxPref
                    const units = val.unitSI
                    let text = ''
                    const commonName =
                        formRef.getFieldValue<FormValues['commonName']>(
                            'commonName'
                        )
                    if (commonName.notSet) text = 'COMMON NAME'
                    else {
                        const { commonName: name } = commonName
                        text = name
                    }
                    claimsCounter++
                    let pushParagraph = false
                    Object.entries(val).forEach((item: any) => {
                        const [, value] = item
                        if (
                            value &&
                            (importantCharacteristic?.includes(key) || letAll)
                        ) {
                            pushParagraph = true
                        }
                    })
                    if (pushParagraph) {
                        childClaimArr.push(
                            new Paragraph({
                                heading: HeadingLevel.HEADING_6,
                                alignment: AlignmentType.LEFT,
                                ...(!isDownload
                                    ? {}
                                    : {
                                          numbering: {
                                              reference: 'claims-numbering',
                                              level: 0,
                                          },
                                      }),
                                children: [
                                    ...(!isDownload
                                        ? [
                                              new TextRun({
                                                  text: `${claimsCounter}. `,
                                                  color: '000000',
                                                  size: 16,
                                              }),
                                          ]
                                        : []),
                                    new TextRun({
                                        text: `The ${text} having a ${
                                            acc
                                                ? `acceptable ${key} from ${
                                                      minAcc || '__'
                                                  } to ${
                                                      maxAcc || '__'
                                                  } ${units}`
                                                : ''
                                        } 
                                        ${acc && pref ? 'and' : ''} 
                                        ${
                                            pref
                                                ? `preferable ${key} from ${
                                                      minPref || '__'
                                                  } to ${
                                                      maxPref || '__'
                                                  } ${units}`
                                                : ''
                                        }`,
                                        color: '000000',
                                        size: 16,
                                    }),
                                ],
                            })
                        )
                    }
                })
            }
        }
        if (
            question === 'additionalCharacteristic' &&
            pushAdditionalCharacteristic &&
            isMachineForm(formRef, question)
        ) {
            pushAdditionalCharacteristic = false
            const primaryComponents = formRef.getFieldValue<
                MachineForm['primaryComponents']
            >(['primaryComponents'])
            const characteristicComponents = formRef.getFieldValue<
                MachineValues['componentsForCharacteristics']
            >('componentsForCharacteristics')
            const featuresPartsOfIndependentClaim = formRef.getFieldValue<
                MachineValues['featuresPartsOfIndependentClaim']
            >(['featuresPartsOfIndependentClaim'])
            const summaryOfSelectedEmbodimentsOfTheInvention =
                formRef.getFieldValue<
                    FormValues['summaryOfSelectedEmbodimentsOfTheInvention']
                >(['summaryOfSelectedEmbodimentsOfTheInvention'])
            characteristicComponents.forEach((component: string) => {
                const foundComponent = primaryComponents.find(
                    (x) => x?.id === component
                )
                if (foundComponent) {
                    const characteristic = formRef.getFieldValue<
                        ValueOf<MachineValues['additionalCharacteristic']>
                    >(['additionalCharacteristic', component])
                    if (characteristic) {
                        Object.entries(characteristic).forEach((entity) => {
                            const [entityKey, val] = entity
                            const key = entityKey as keyof typeof UNITS
                            if (val) {
                                const parentClaimPrimaryComponent =
                                    parentClaimPrimaryComponents.find(
                                        (x) => x?.id === component
                                    )
                                if (
                                    parentClaimPrimaryComponent &&
                                    (!summaryOfSelectedEmbodimentsOfTheInvention ||
                                        summaryOfSelectedEmbodimentsOfTheInvention.includes(
                                            `additionalCharacteristic_${component}.${key}`
                                        )) &&
                                    (summaryOfSelectedEmbodimentsOfTheInvention ||
                                        !featuresPartsOfIndependentClaim ||
                                        featuresPartsOfIndependentClaim.includes(
                                            `additionalCharacteristic_${component}.${key}`
                                        ))
                                ) {
                                    if (
                                        typeof val === 'object' &&
                                        val !== null
                                    ) {
                                        if ('description' in val) {
                                            if (key === 'defined') {
                                                parentClaimArr.push(
                                                    new Paragraph({
                                                        heading:
                                                            HeadingLevel.HEADING_6,
                                                        alignment:
                                                            AlignmentType.LEFT,
                                                        children: [
                                                            new TextRun({
                                                                text: `wherein the ${
                                                                    'description' in
                                                                    val
                                                                        ? val.description
                                                                        : '__'
                                                                }.`,
                                                                color: '000000',
                                                                size: 16,
                                                            }),
                                                        ],
                                                    })
                                                )
                                            }
                                        } else {
                                            if (key === 'thermalConductivity') {
                                                parentClaimArr.push(
                                                    new Paragraph({
                                                        heading:
                                                            HeadingLevel.HEADING_6,
                                                        alignment:
                                                            AlignmentType.LEFT,
                                                        children: [
                                                            new TextRun({
                                                                text: `wherein the ${lowerfirst(
                                                                    foundComponent.component
                                                                )} has a thermal conductivity from ${
                                                                    val.from ||
                                                                    '__'
                                                                } to ${
                                                                    val.to ||
                                                                    '__'
                                                                } ${
                                                                    UNITS[key]
                                                                }.`,
                                                                color: '000000',
                                                                size: 16,
                                                            }),
                                                        ],
                                                    })
                                                )
                                            }
                                            if (key === 'meltingPoint') {
                                                parentClaimArr.push(
                                                    new Paragraph({
                                                        heading:
                                                            HeadingLevel.HEADING_6,
                                                        alignment:
                                                            AlignmentType.LEFT,
                                                        children: [
                                                            new TextRun({
                                                                text: `wherein the ${lowerfirst(
                                                                    foundComponent.component
                                                                )} has a melting point from ${
                                                                    val.from ||
                                                                    '__'
                                                                } to ${
                                                                    val.to ||
                                                                    '__'
                                                                } ${
                                                                    UNITS[key]
                                                                }.`,
                                                                color: '000000',
                                                                size: 16,
                                                            }),
                                                        ],
                                                    })
                                                )
                                            }
                                            if (key === 'specificHeat') {
                                                parentClaimArr.push(
                                                    new Paragraph({
                                                        heading:
                                                            HeadingLevel.HEADING_6,
                                                        alignment:
                                                            AlignmentType.LEFT,
                                                        children: [
                                                            new TextRun({
                                                                text: `wherein the ${lowerfirst(
                                                                    foundComponent.component
                                                                )} has a specific heat from ${
                                                                    val.from ||
                                                                    '__'
                                                                } to ${
                                                                    val.to ||
                                                                    '__'
                                                                } ${
                                                                    UNITS[key]
                                                                }.`,
                                                                color: '000000',
                                                                size: 16,
                                                            }),
                                                        ],
                                                    })
                                                )
                                            }
                                        }
                                    } else if (
                                        typeof val !== 'object' &&
                                        val === null
                                    ) {
                                        if (key === 'thermalConductivity') {
                                            parentClaimArr.push(
                                                new Paragraph({
                                                    heading:
                                                        HeadingLevel.HEADING_6,
                                                    alignment:
                                                        AlignmentType.LEFT,
                                                    children: [
                                                        new TextRun({
                                                            text: `wherein the ${lowerfirst(
                                                                foundComponent.component
                                                            )} has a thermal conductivity from __ to __ ${
                                                                UNITS[key]
                                                            }.`,
                                                            color: '000000',
                                                            size: 16,
                                                        }),
                                                    ],
                                                })
                                            )
                                        }
                                        if (key === 'meltingPoint') {
                                            parentClaimArr.push(
                                                new Paragraph({
                                                    heading:
                                                        HeadingLevel.HEADING_6,
                                                    alignment:
                                                        AlignmentType.LEFT,
                                                    children: [
                                                        new TextRun({
                                                            text: `wherein the ${lowerfirst(
                                                                foundComponent.component
                                                            )} has a melting point from __ to __ ${
                                                                UNITS[key]
                                                            }.`,
                                                            color: '000000',
                                                            size: 16,
                                                        }),
                                                    ],
                                                })
                                            )
                                        }
                                        if (key === 'specificHeat') {
                                            parentClaimArr.push(
                                                new Paragraph({
                                                    heading:
                                                        HeadingLevel.HEADING_6,
                                                    alignment:
                                                        AlignmentType.LEFT,
                                                    children: [
                                                        new TextRun({
                                                            text: `wherein the ${lowerfirst(
                                                                foundComponent.component
                                                            )} has a specific heat from __ to __ ${
                                                                UNITS[key]
                                                            }.`,
                                                            color: '000000',
                                                            size: 16,
                                                        }),
                                                    ],
                                                })
                                            )
                                        }
                                        if (key === 'defined') {
                                            parentClaimArr.push(
                                                new Paragraph({
                                                    heading:
                                                        HeadingLevel.HEADING_6,
                                                    alignment:
                                                        AlignmentType.LEFT,
                                                    children: [
                                                        new TextRun({
                                                            text: `wherein the ___.`,
                                                            color: '000000',
                                                            size: 16,
                                                        }),
                                                    ],
                                                })
                                            )
                                        }
                                    }
                                } else {
                                    claimsCounter++
                                    if (
                                        typeof val === 'object' &&
                                        val !== null
                                    ) {
                                        if ('description' in val) {
                                            if (key === 'defined') {
                                                childClaimArr.push(
                                                    new Paragraph({
                                                        heading:
                                                            HeadingLevel.HEADING_6,
                                                        alignment:
                                                            AlignmentType.LEFT,
                                                        ...(!isDownload
                                                            ? {}
                                                            : {
                                                                  numbering: {
                                                                      reference:
                                                                          'claims-numbering',
                                                                      level: 0,
                                                                  },
                                                              }),
                                                        children: [
                                                            ...(!isDownload
                                                                ? [
                                                                      new TextRun(
                                                                          {
                                                                              text: `${claimsCounter}. `,
                                                                              color: '000000',
                                                                              size: 16,
                                                                          }
                                                                      ),
                                                                  ]
                                                                : []),
                                                            ...buildClaimRuns(
                                                                `The ${category} of claim ${getClaimForFeature(
                                                                    component
                                                                )}, wherein the ${
                                                                    'description' in
                                                                    val
                                                                        ? val.description
                                                                        : '__'
                                                                }.`
                                                            ),
                                                        ],
                                                    })
                                                )
                                            }
                                        } else {
                                            if (key === 'thermalConductivity') {
                                                childClaimArr.push(
                                                    new Paragraph({
                                                        heading:
                                                            HeadingLevel.HEADING_6,
                                                        alignment:
                                                            AlignmentType.LEFT,
                                                        ...(!isDownload
                                                            ? {}
                                                            : {
                                                                  numbering: {
                                                                      reference:
                                                                          'claims-numbering',
                                                                      level: 0,
                                                                  },
                                                              }),
                                                        children: [
                                                            ...(!isDownload
                                                                ? [
                                                                      new TextRun(
                                                                          {
                                                                              text: `${claimsCounter}. `,
                                                                              color: '000000',
                                                                              size: 16,
                                                                          }
                                                                      ),
                                                                  ]
                                                                : []),
                                                            ...buildClaimRuns(
                                                                `The ${category} of claim ${getClaimForFeature(
                                                                    component
                                                                )}, wherein the ${lowerfirst(
                                                                    foundComponent.component
                                                                )} has a thermal conductivity from ${
                                                                    val.from ||
                                                                    '__'
                                                                } to ${
                                                                    val.to ||
                                                                    '__'
                                                                } ${
                                                                    UNITS[key]
                                                                }.`
                                                            ),
                                                        ],
                                                    })
                                                )
                                            }
                                            if (key === 'meltingPoint') {
                                                childClaimArr.push(
                                                    new Paragraph({
                                                        heading:
                                                            HeadingLevel.HEADING_6,
                                                        alignment:
                                                            AlignmentType.LEFT,
                                                        ...(!isDownload
                                                            ? {}
                                                            : {
                                                                  numbering: {
                                                                      reference:
                                                                          'claims-numbering',
                                                                      level: 0,
                                                                  },
                                                              }),
                                                        children: [
                                                            ...(!isDownload
                                                                ? [
                                                                      new TextRun(
                                                                          {
                                                                              text: `${claimsCounter}. `,
                                                                              color: '000000',
                                                                              size: 16,
                                                                          }
                                                                      ),
                                                                  ]
                                                                : []),
                                                            ...buildClaimRuns(
                                                                `The ${category} of claim ${getClaimForFeature(
                                                                    component
                                                                )}, wherein the ${lowerfirst(
                                                                    foundComponent.component
                                                                )} has a melting point from ${
                                                                    val.from ||
                                                                    '__'
                                                                } to ${
                                                                    val.to ||
                                                                    '__'
                                                                } ${
                                                                    UNITS[key]
                                                                }.`
                                                            ),
                                                        ],
                                                    })
                                                )
                                            }
                                            if (key === 'specificHeat') {
                                                childClaimArr.push(
                                                    new Paragraph({
                                                        heading:
                                                            HeadingLevel.HEADING_6,
                                                        alignment:
                                                            AlignmentType.LEFT,
                                                        ...(!isDownload
                                                            ? {}
                                                            : {
                                                                  numbering: {
                                                                      reference:
                                                                          'claims-numbering',
                                                                      level: 0,
                                                                  },
                                                              }),
                                                        children: [
                                                            ...(!isDownload
                                                                ? [
                                                                      new TextRun(
                                                                          {
                                                                              text: `${claimsCounter}. `,
                                                                              color: '000000',
                                                                              size: 16,
                                                                          }
                                                                      ),
                                                                  ]
                                                                : []),
                                                            ...buildClaimRuns(
                                                                `The ${category} of claim ${getClaimForFeature(
                                                                    component
                                                                )}, wherein the ${lowerfirst(
                                                                    foundComponent.component
                                                                )} has a specific heat from ${
                                                                    val.from ||
                                                                    '__'
                                                                } to ${
                                                                    val.to ||
                                                                    '__'
                                                                } ${
                                                                    UNITS[key]
                                                                }.`
                                                            ),
                                                        ],
                                                    })
                                                )
                                            }
                                        }
                                    } else if (
                                        typeof val !== 'object' &&
                                        val === null
                                    ) {
                                        if (key === 'thermalConductivity') {
                                            childClaimArr.push(
                                                new Paragraph({
                                                    heading:
                                                        HeadingLevel.HEADING_6,
                                                    alignment:
                                                        AlignmentType.LEFT,
                                                    ...(!isDownload
                                                        ? {}
                                                        : {
                                                              numbering: {
                                                                  reference:
                                                                      'claims-numbering',
                                                                  level: 0,
                                                              },
                                                          }),
                                                    children: [
                                                        ...(!isDownload
                                                            ? [
                                                                  new TextRun({
                                                                      text: `${claimsCounter}. `,
                                                                      color: '000000',
                                                                      size: 16,
                                                                  }),
                                                              ]
                                                            : []),
                                                        ...buildClaimRuns(
                                                            `The ${category} of claim ${getClaimForFeature(
                                                                component
                                                            )}, wherein the ${lowerfirst(
                                                                foundComponent.component
                                                            )} has a thermal conductivity from __ to __ ${
                                                                UNITS[key]
                                                            }.`
                                                        ),
                                                    ],
                                                })
                                            )
                                        }
                                        if (key === 'meltingPoint') {
                                            childClaimArr.push(
                                                new Paragraph({
                                                    heading:
                                                        HeadingLevel.HEADING_6,
                                                    alignment:
                                                        AlignmentType.LEFT,
                                                    ...(!isDownload
                                                        ? {}
                                                        : {
                                                              numbering: {
                                                                  reference:
                                                                      'claims-numbering',
                                                                  level: 0,
                                                              },
                                                          }),
                                                    children: [
                                                        ...(!isDownload
                                                            ? [
                                                                  new TextRun({
                                                                      text: `${claimsCounter}. `,
                                                                      color: '000000',
                                                                      size: 16,
                                                                  }),
                                                              ]
                                                            : []),
                                                        ...buildClaimRuns(
                                                            `The ${category} of claim ${getClaimForFeature(
                                                                component
                                                            )}, wherein the ${lowerfirst(
                                                                foundComponent.component
                                                            )} has a melting point from __ to __ ${
                                                                UNITS[key]
                                                            }.`
                                                        ),
                                                    ],
                                                })
                                            )
                                        }
                                        if (key === 'specificHeat') {
                                            childClaimArr.push(
                                                new Paragraph({
                                                    heading:
                                                        HeadingLevel.HEADING_6,
                                                    alignment:
                                                        AlignmentType.LEFT,
                                                    ...(!isDownload
                                                        ? {}
                                                        : {
                                                              numbering: {
                                                                  reference:
                                                                      'claims-numbering',
                                                                  level: 0,
                                                              },
                                                          }),
                                                    children: [
                                                        ...(!isDownload
                                                            ? [
                                                                  new TextRun({
                                                                      text: `${claimsCounter}. `,
                                                                      color: '000000',
                                                                      size: 16,
                                                                  }),
                                                              ]
                                                            : []),
                                                        ...buildClaimRuns(
                                                            `The ${category} of claim ${getClaimForFeature(
                                                                component
                                                            )}, wherein the ${lowerfirst(
                                                                foundComponent.component
                                                            )} has a specific heat from __ to __ ${
                                                                UNITS[key]
                                                            }.`
                                                        ),
                                                    ],
                                                })
                                            )
                                        }
                                        if (key === 'defined') {
                                            childClaimArr.push(
                                                new Paragraph({
                                                    heading:
                                                        HeadingLevel.HEADING_6,
                                                    alignment:
                                                        AlignmentType.LEFT,
                                                    ...(!isDownload
                                                        ? {}
                                                        : {
                                                              numbering: {
                                                                  reference:
                                                                      'claims-numbering',
                                                                  level: 0,
                                                              },
                                                          }),
                                                    children: [
                                                        ...(!isDownload
                                                            ? [
                                                                  new TextRun({
                                                                      text: `${claimsCounter}. `,
                                                                      color: '000000',
                                                                      size: 16,
                                                                  }),
                                                              ]
                                                            : []),
                                                        ...buildClaimRuns(
                                                            `The ${category} of claim ${getClaimForFeature(
                                                                component
                                                            )}, wherein the ___.`
                                                        ),
                                                    ],
                                                })
                                            )
                                        }
                                    }
                                }
                            }
                        })
                    }
                }
            })
        }
        if (
            question === 'formatUserDefined' &&
            pushFormatUser &&
            isMachineForm(formRef, question)
        ) {
            pushFormatUser = false
            const primaryComponents = formRef.getFieldValue<
                MachineForm['primaryComponents']
            >(['primaryComponents'])
            const characteristicComponents = formRef.getFieldValue<
                MachineValues['componentsForCharacteristics']
            >('componentsForCharacteristics')
            const featuresPartsOfIndependentClaim = formRef.getFieldValue<
                MachineValues['featuresPartsOfIndependentClaim']
            >(['featuresPartsOfIndependentClaim'])
            const summaryOfSelectedEmbodimentsOfTheInvention =
                formRef.getFieldValue<
                    FormValues['summaryOfSelectedEmbodimentsOfTheInvention']
                >(['summaryOfSelectedEmbodimentsOfTheInvention'])

            characteristicComponents.forEach((component: string) => {
                const characteristic = formRef.getFieldValue<
                    ValueOf<MachineValues['formatUserDefined']>
                >(['formatUserDefined', component])
                if (characteristic) {
                    Object.entries(characteristic).forEach((entity: any) => {
                        const [entityKey, val] = entity
                        const key = entityKey
                        const foundComponent = primaryComponents.find(
                            (x) => x?.id === component
                        )
                        if (foundComponent) {
                            if (val) {
                                const parentClaimPrimaryComponent =
                                    parentClaimPrimaryComponents.find(
                                        (x) => x?.id === component
                                    )
                                if (
                                    parentClaimPrimaryComponent &&
                                    (!summaryOfSelectedEmbodimentsOfTheInvention ||
                                        summaryOfSelectedEmbodimentsOfTheInvention.includes(
                                            `formatUserDefined_${component}.${key}`
                                        )) &&
                                    (summaryOfSelectedEmbodimentsOfTheInvention ||
                                        !featuresPartsOfIndependentClaim ||
                                        featuresPartsOfIndependentClaim.includes(
                                            `formatUserDefined_${component}.${key}`
                                        ))
                                ) {
                                    if (
                                        typeof val === 'object' &&
                                        val !== null
                                    ) {
                                        if (key === 'from') {
                                            parentClaimArr.push(
                                                new Paragraph({
                                                    heading:
                                                        HeadingLevel.HEADING_6,
                                                    alignment:
                                                        AlignmentType.LEFT,
                                                    children: [
                                                        new TextRun({
                                                            text: `wherein the ${lowerfirst(
                                                                foundComponent.component!
                                                            )} has a ${
                                                                val.name || '__'
                                                            } from ${
                                                                val.minimum ||
                                                                '__'
                                                            } to ${
                                                                val.maximum ||
                                                                '__'
                                                            } ${
                                                                val.units || ''
                                                            }.`,
                                                            color: '000000',
                                                            size: 16,
                                                        }),
                                                    ],
                                                })
                                            )
                                        }
                                        if (key === 'equal') {
                                            parentClaimArr.push(
                                                new Paragraph({
                                                    heading:
                                                        HeadingLevel.HEADING_6,
                                                    alignment:
                                                        AlignmentType.LEFT,
                                                    children: [
                                                        new TextRun({
                                                            text: `wherein the ${
                                                                val.name || '__'
                                                            } of the ${lowerfirst(
                                                                foundComponent.component!
                                                            )} is equal to from ${
                                                                val.minimum ||
                                                                '__'
                                                            } to ${
                                                                val.maximum ||
                                                                '__'
                                                            } ${
                                                                val.units || ''
                                                            }.`,
                                                            color: '000000',
                                                            size: 16,
                                                        }),
                                                    ],
                                                })
                                            )
                                        }
                                        if (key === 'defined') {
                                            parentClaimArr.push(
                                                new Paragraph({
                                                    heading:
                                                        HeadingLevel.HEADING_6,
                                                    alignment:
                                                        AlignmentType.LEFT,
                                                    children: [
                                                        new TextRun({
                                                            text: `wherein the ${
                                                                val.description ||
                                                                '___'
                                                            }.`,
                                                            color: '000000',
                                                            size: 16,
                                                        }),
                                                    ],
                                                })
                                            )
                                        }
                                    } else if (
                                        typeof val !== 'object' &&
                                        val === null
                                    ) {
                                        if (key === 'body') {
                                            parentClaimArr.push(
                                                new Paragraph({
                                                    heading:
                                                        HeadingLevel.HEADING_6,
                                                    alignment:
                                                        AlignmentType.LEFT,
                                                    children: [
                                                        new TextRun({
                                                            text: `wherein the ___ has a ____ from __ to __.`,
                                                            color: '000000',
                                                            size: 16,
                                                        }),
                                                    ],
                                                })
                                            )
                                        }
                                        if (key === 'equal') {
                                            parentClaimArr.push(
                                                new Paragraph({
                                                    heading:
                                                        HeadingLevel.HEADING_6,
                                                    alignment:
                                                        AlignmentType.LEFT,
                                                    children: [
                                                        new TextRun({
                                                            text: `wherein the ___ of the ____ is equal to from __ to __.`,
                                                            color: '000000',
                                                            size: 16,
                                                        }),
                                                    ],
                                                })
                                            )
                                        }
                                        if (key === 'defined') {
                                            parentClaimArr.push(
                                                new Paragraph({
                                                    heading:
                                                        HeadingLevel.HEADING_6,
                                                    alignment:
                                                        AlignmentType.LEFT,
                                                    children: [
                                                        new TextRun({
                                                            text: `wherein the ___.`,
                                                            color: '000000',
                                                            size: 16,
                                                        }),
                                                    ],
                                                })
                                            )
                                        }
                                    }
                                } else {
                                    claimsCounter++
                                    if (
                                        typeof val === 'object' &&
                                        val !== null
                                    ) {
                                        if (key === 'from') {
                                            childClaimArr.push(
                                                new Paragraph({
                                                    heading:
                                                        HeadingLevel.HEADING_6,
                                                    alignment:
                                                        AlignmentType.LEFT,
                                                    ...(!isDownload
                                                        ? {}
                                                        : {
                                                              numbering: {
                                                                  reference:
                                                                      'claims-numbering',
                                                                  level: 0,
                                                              },
                                                          }),
                                                    children: [
                                                        ...(!isDownload
                                                            ? [
                                                                  new TextRun({
                                                                      text: `${claimsCounter}. `,
                                                                      color: '000000',
                                                                      size: 16,
                                                                  }),
                                                              ]
                                                            : []),
                                                        ...buildClaimRuns(
                                                            `The ${category} of claim ${getClaimForFeature(
                                                                component
                                                            )}, wherein the ${lowerfirst(
                                                                foundComponent.component!
                                                            )} has a ${
                                                                val.name || '__'
                                                            } from ${
                                                                val.minimum ||
                                                                '__'
                                                            } to ${
                                                                val.maximum ||
                                                                '__'
                                                            } ${
                                                                val.units || ''
                                                            }.`
                                                        ),
                                                    ],
                                                })
                                            )
                                        }
                                        if (key === 'equal') {
                                            childClaimArr.push(
                                                new Paragraph({
                                                    heading:
                                                        HeadingLevel.HEADING_6,
                                                    alignment:
                                                        AlignmentType.LEFT,
                                                    ...(!isDownload
                                                        ? {}
                                                        : {
                                                              numbering: {
                                                                  reference:
                                                                      'claims-numbering',
                                                                  level: 0,
                                                              },
                                                          }),
                                                    children: [
                                                        ...(!isDownload
                                                            ? [
                                                                  new TextRun({
                                                                      text: `${claimsCounter}. `,
                                                                      color: '000000',
                                                                      size: 16,
                                                                  }),
                                                              ]
                                                            : []),
                                                        ...buildClaimRuns(
                                                            `The ${category} of claim ${getClaimForFeature(
                                                                component
                                                            )}, wherein the ${
                                                                val.name || '__'
                                                            } of the ${lowerfirst(
                                                                foundComponent.component!
                                                            )} is equal to from ${
                                                                val.minimum ||
                                                                '__'
                                                            } to ${
                                                                val.maximum ||
                                                                '__'
                                                            } ${
                                                                val.units || ''
                                                            }.`
                                                        ),
                                                    ],
                                                })
                                            )
                                        }
                                        if (key === 'defined') {
                                            childClaimArr.push(
                                                new Paragraph({
                                                    heading:
                                                        HeadingLevel.HEADING_6,
                                                    alignment:
                                                        AlignmentType.LEFT,
                                                    ...(!isDownload
                                                        ? {}
                                                        : {
                                                              numbering: {
                                                                  reference:
                                                                      'claims-numbering',
                                                                  level: 0,
                                                              },
                                                          }),
                                                    children: [
                                                        ...(!isDownload
                                                            ? [
                                                                  new TextRun({
                                                                      text: `${claimsCounter}. `,
                                                                      color: '000000',
                                                                      size: 16,
                                                                  }),
                                                              ]
                                                            : []),
                                                        ...buildClaimRuns(
                                                            `The ${category} of claim ${getClaimForFeature(
                                                                component
                                                            )}, wherein the ${
                                                                val.description ||
                                                                '___'
                                                            }.`
                                                        ),
                                                    ],
                                                })
                                            )
                                        }
                                    } else if (
                                        typeof val !== 'object' &&
                                        val === null
                                    ) {
                                        if (key === 'body') {
                                            childClaimArr.push(
                                                new Paragraph({
                                                    heading:
                                                        HeadingLevel.HEADING_6,
                                                    alignment:
                                                        AlignmentType.LEFT,
                                                    ...(!isDownload
                                                        ? {}
                                                        : {
                                                              numbering: {
                                                                  reference:
                                                                      'claims-numbering',
                                                                  level: 0,
                                                              },
                                                          }),
                                                    children: [
                                                        ...(!isDownload
                                                            ? [
                                                                  new TextRun({
                                                                      text: `${claimsCounter}. `,
                                                                      color: '000000',
                                                                      size: 16,
                                                                  }),
                                                              ]
                                                            : []),
                                                        ...buildClaimRuns(
                                                            `The ${category} of claim ${getClaimForFeature(
                                                                component
                                                            )}, wherein the ___ has a ____ from __ to __.`
                                                        ),
                                                    ],
                                                })
                                            )
                                        }
                                        if (key === 'equal') {
                                            childClaimArr.push(
                                                new Paragraph({
                                                    heading:
                                                        HeadingLevel.HEADING_6,
                                                    alignment:
                                                        AlignmentType.LEFT,
                                                    ...(!isDownload
                                                        ? {}
                                                        : {
                                                              numbering: {
                                                                  reference:
                                                                      'claims-numbering',
                                                                  level: 0,
                                                              },
                                                          }),
                                                    children: [
                                                        ...(!isDownload
                                                            ? [
                                                                  new TextRun({
                                                                      text: `${claimsCounter}. `,
                                                                      color: '000000',
                                                                      size: 16,
                                                                  }),
                                                              ]
                                                            : []),
                                                        ...buildClaimRuns(
                                                            `The ${category} of claim ${getClaimForFeature(
                                                                component
                                                            )}, wherein the ___ of the ____ is equal to from __ to __.`
                                                        ),
                                                    ],
                                                })
                                            )
                                        }
                                        if (key === 'defined') {
                                            childClaimArr.push(
                                                new Paragraph({
                                                    heading:
                                                        HeadingLevel.HEADING_6,
                                                    alignment:
                                                        AlignmentType.LEFT,
                                                    ...(!isDownload
                                                        ? {}
                                                        : {
                                                              numbering: {
                                                                  reference:
                                                                      'claims-numbering',
                                                                  level: 0,
                                                              },
                                                          }),
                                                    children: [
                                                        ...(!isDownload
                                                            ? [
                                                                  new TextRun({
                                                                      text: `${claimsCounter}. `,
                                                                      color: '000000',
                                                                      size: 16,
                                                                  }),
                                                              ]
                                                            : []),
                                                        ...buildClaimRuns(
                                                            `The ${category} of claim ${getClaimForFeature(
                                                                component
                                                            )}, wherein the ___.`
                                                        ),
                                                    ],
                                                })
                                            )
                                        }
                                    }
                                }
                            }
                        }
                    })
                }
            })
        }
        if (
            question === 'subCharacteristic' &&
            pushSubcharacteristic &&
            isMachineForm(formRef, question)
        ) {
            pushSubcharacteristic = false
            const subcomponentsForCharacteristics = formRef.getFieldValue<
                MachineValues['subcomponentsForCharacteristics']
            >('subcomponentsForCharacteristics')

            const componentsForCharacteristics = formRef.getFieldValue<
                MachineValues['componentsForCharacteristics']
            >('componentsForCharacteristics')

            const subcharacteristicComponent = formRef.getFieldValue<
                ValueOf<MachineValues['primaryComponentSubComponentsList']>
            >(['primaryComponentSubComponentsList'])

            componentsForCharacteristics.forEach((component: string) => {
                if (subcomponentsForCharacteristics[component]) {
                    subcomponentsForCharacteristics[component].forEach(
                        (subcomponent: string) => {
                            const characteristicComponents = Array.isArray(
                                subcharacteristicComponent
                            )
                                ? subcharacteristicComponent
                                : (subcharacteristicComponent as any)[component]

                            const foundComponent =
                                characteristicComponents.find(
                                    (x: any) => x?.id === subcomponent
                                )
                            if (foundComponent) {
                                const characteristic = formRef.getFieldValue<
                                    ValueOf<MachineValues['subCharacteristic']>
                                >(['subCharacteristic', subcomponent])
                                if (characteristic) {
                                    Object.entries(characteristic).forEach(
                                        (entity: any) => {
                                            const [entityKey, val] = entity
                                            const key =
                                                entityKey as keyof typeof UNITS
                                            if (val) {
                                                claimsCounter++
                                                if (
                                                    typeof val === 'object' &&
                                                    val !== null
                                                ) {
                                                    if (key === 'defined') {
                                                        childClaimArr.push(
                                                            new Paragraph({
                                                                heading:
                                                                    HeadingLevel.HEADING_6,
                                                                alignment:
                                                                    AlignmentType.LEFT,
                                                                ...(!isDownload
                                                                    ? {}
                                                                    : {
                                                                          numbering:
                                                                              {
                                                                                  reference:
                                                                                      'claims-numbering',
                                                                                  level: 0,
                                                                              },
                                                                      }),
                                                                children: [
                                                                    ...(!isDownload
                                                                        ? [
                                                                              new TextRun(
                                                                                  {
                                                                                      text: `${claimsCounter}. `,
                                                                                      color: '000000',
                                                                                      size: 16,
                                                                                  }
                                                                              ),
                                                                          ]
                                                                        : []),
                                                                    ...buildClaimRuns(
                                                                        `The ${category}  of claim ${getClaimForFeature(
                                                                            `${component}+=>${subcomponent}`
                                                                        )}, wherein the ${
                                                                            val.description ||
                                                                            '__'
                                                                        } .`
                                                                    ),
                                                                ],
                                                            })
                                                        )
                                                    } else {
                                                        childClaimArr.push(
                                                            new Paragraph({
                                                                heading:
                                                                    HeadingLevel.HEADING_6,
                                                                alignment:
                                                                    AlignmentType.LEFT,
                                                                ...(!isDownload
                                                                    ? {}
                                                                    : {
                                                                          numbering:
                                                                              {
                                                                                  reference:
                                                                                      'claims-numbering',
                                                                                  level: 0,
                                                                              },
                                                                      }),
                                                                children: [
                                                                    ...(!isDownload
                                                                        ? [
                                                                              new TextRun(
                                                                                  {
                                                                                      text: `${claimsCounter}. `,
                                                                                      color: '000000',
                                                                                      size: 16,
                                                                                  }
                                                                              ),
                                                                          ]
                                                                        : []),
                                                                    ...buildClaimRuns(`The ${category} of claim ${getClaimForFeature(
                                                                        `${component}+=>${subcomponent}`
                                                                    )}, wherein the 
                                                                            ${lowerfirst(
                                                                                foundComponent.component!
                                                                            )} has a ${key} from ${
                                                                        val.from ||
                                                                        '__'
                                                                    } to ${
                                                                        val.to ||
                                                                        '__'
                                                                    } ${
                                                                        UNITS[
                                                                            key
                                                                        ]
                                                                    }.`),
                                                                ],
                                                            })
                                                        )
                                                    }
                                                } else if (
                                                    typeof val !== 'object' &&
                                                    val === null
                                                ) {
                                                    if (key === 'defined') {
                                                        childClaimArr.push(
                                                            new Paragraph({
                                                                heading:
                                                                    HeadingLevel.HEADING_6,
                                                                alignment:
                                                                    AlignmentType.LEFT,
                                                                ...(!isDownload
                                                                    ? {}
                                                                    : {
                                                                          numbering:
                                                                              {
                                                                                  reference:
                                                                                      'claims-numbering',
                                                                                  level: 0,
                                                                              },
                                                                      }),
                                                                children: [
                                                                    ...(!isDownload
                                                                        ? [
                                                                              new TextRun(
                                                                                  {
                                                                                      text: `${claimsCounter}. `,
                                                                                      color: '000000',
                                                                                      size: 16,
                                                                                  }
                                                                              ),
                                                                          ]
                                                                        : []),
                                                                    ...buildClaimRuns(
                                                                        `The ${category} of claim ${getClaimForFeature(
                                                                            `${component}+=>${subcomponent}`
                                                                        )}, wherein the ___.`
                                                                    ),
                                                                ],
                                                            })
                                                        )
                                                    } else {
                                                        childClaimArr.push(
                                                            new Paragraph({
                                                                heading:
                                                                    HeadingLevel.HEADING_6,
                                                                alignment:
                                                                    AlignmentType.LEFT,
                                                                ...(!isDownload
                                                                    ? {}
                                                                    : {
                                                                          numbering:
                                                                              {
                                                                                  reference:
                                                                                      'claims-numbering',
                                                                                  level: 0,
                                                                              },
                                                                      }),
                                                                children: [
                                                                    ...(!isDownload
                                                                        ? [
                                                                              new TextRun(
                                                                                  {
                                                                                      text: `${claimsCounter}. `,
                                                                                      color: '000000',
                                                                                      size: 16,
                                                                                  }
                                                                              ),
                                                                          ]
                                                                        : []),
                                                                    ...buildClaimRuns(
                                                                        `The ${category} of claim 1, wherein the ${foundComponent.component} has a ${key} from __ to __ ${UNITS[key]}.`
                                                                    ),
                                                                ],
                                                            })
                                                        )
                                                    }
                                                }
                                            }
                                        }
                                    )
                                }
                            }
                        }
                    )
                }
            })
        }
        if (
            question === 'subAdditionalCharacteristic' &&
            pushSubAdditionalCharacteristic &&
            isMachineForm(formRef, question)
        ) {
            pushSubAdditionalCharacteristic = false
            const subcomponentsForCharacteristics = formRef.getFieldValue<
                MachineValues['subcomponentsForCharacteristics']
            >('subcomponentsForCharacteristics')

            const componentsForCharacteristics = formRef.getFieldValue<
                MachineValues['componentsForCharacteristics']
            >('componentsForCharacteristics')

            const subcharacteristicComponent = formRef.getFieldValue<
                ValueOf<MachineValues['primaryComponentSubComponentsList']>
            >(['primaryComponentSubComponentsList'])

            componentsForCharacteristics.forEach((component: string) => {
                if (subcomponentsForCharacteristics[component] != null) {
                    subcomponentsForCharacteristics[component].forEach(
                        (subcomponent: string) => {
                            const characteristicComponents = Array.isArray(
                                subcharacteristicComponent
                            )
                                ? subcharacteristicComponent
                                : (subcharacteristicComponent as any)[component]

                            const foundComponent =
                                characteristicComponents.find(
                                    (x: any) => x?.id === subcomponent
                                )
                            if (foundComponent) {
                                const characteristic = formRef.getFieldValue<
                                    MachineValues['subAdditionalCharacteristic']
                                >(['subAdditionalCharacteristic', subcomponent])
                                if (characteristic) {
                                    Object.entries(characteristic).forEach(
                                        (entity: any) => {
                                            const [entityKey, val] = entity
                                            const key =
                                                entityKey as keyof typeof UNITS
                                            if (val) {
                                                claimsCounter++
                                                if (
                                                    typeof val === 'object' &&
                                                    val !== null
                                                ) {
                                                    if (
                                                        key ===
                                                        'thermalConductivity'
                                                    ) {
                                                        childClaimArr.push(
                                                            new Paragraph({
                                                                heading:
                                                                    HeadingLevel.HEADING_6,
                                                                alignment:
                                                                    AlignmentType.LEFT,
                                                                ...(!isDownload
                                                                    ? {}
                                                                    : {
                                                                          numbering:
                                                                              {
                                                                                  reference:
                                                                                      'claims-numbering',
                                                                                  level: 0,
                                                                              },
                                                                      }),
                                                                children: [
                                                                    ...(!isDownload
                                                                        ? [
                                                                              new TextRun(
                                                                                  {
                                                                                      text: `${claimsCounter}. `,
                                                                                      color: '000000',
                                                                                      size: 16,
                                                                                  }
                                                                              ),
                                                                          ]
                                                                        : []),
                                                                    ...buildClaimRuns(
                                                                        `The ${category} of claim ${getClaimForFeature(
                                                                            `${component}+=>${subcomponent}`
                                                                        )},  wherein the ${lowerfirst(
                                                                            foundComponent.component
                                                                        )} has a thermal conductivity from ${
                                                                            val.from ||
                                                                            '__'
                                                                        } to ${
                                                                            val.to ||
                                                                            '__'
                                                                        } ${
                                                                            UNITS[
                                                                                key
                                                                            ]
                                                                        }.`
                                                                    ),
                                                                ],
                                                            })
                                                        )
                                                    }
                                                    if (
                                                        key === 'meltingPoint'
                                                    ) {
                                                        childClaimArr.push(
                                                            new Paragraph({
                                                                heading:
                                                                    HeadingLevel.HEADING_6,
                                                                alignment:
                                                                    AlignmentType.LEFT,
                                                                ...(!isDownload
                                                                    ? {}
                                                                    : {
                                                                          numbering:
                                                                              {
                                                                                  reference:
                                                                                      'claims-numbering',
                                                                                  level: 0,
                                                                              },
                                                                      }),
                                                                children: [
                                                                    ...(!isDownload
                                                                        ? [
                                                                              new TextRun(
                                                                                  {
                                                                                      text: `${claimsCounter}. `,
                                                                                      color: '000000',
                                                                                      size: 16,
                                                                                  }
                                                                              ),
                                                                          ]
                                                                        : []),
                                                                    ...buildClaimRuns(
                                                                        `The ${category} of claim ${getClaimForFeature(
                                                                            `${component}+=>${subcomponent}`
                                                                        )}, wherein the ${lowerfirst(
                                                                            foundComponent.component
                                                                        )} has a melting point from ${
                                                                            val.from ||
                                                                            '__'
                                                                        } to ${
                                                                            val.to ||
                                                                            '__'
                                                                        } ${
                                                                            UNITS[
                                                                                key
                                                                            ]
                                                                        }.`
                                                                    ),
                                                                ],
                                                            })
                                                        )
                                                    }
                                                    if (
                                                        key === 'specificHeat'
                                                    ) {
                                                        childClaimArr.push(
                                                            new Paragraph({
                                                                heading:
                                                                    HeadingLevel.HEADING_6,
                                                                alignment:
                                                                    AlignmentType.LEFT,
                                                                ...(!isDownload
                                                                    ? {}
                                                                    : {
                                                                          numbering:
                                                                              {
                                                                                  reference:
                                                                                      'claims-numbering',
                                                                                  level: 0,
                                                                              },
                                                                      }),
                                                                children: [
                                                                    ...(!isDownload
                                                                        ? [
                                                                              new TextRun(
                                                                                  {
                                                                                      text: `${claimsCounter}. `,
                                                                                      color: '000000',
                                                                                      size: 16,
                                                                                  }
                                                                              ),
                                                                          ]
                                                                        : []),
                                                                    ...buildClaimRuns(
                                                                        `The ${category} of claim ${getClaimForFeature(
                                                                            `${component}+=>${subcomponent}`
                                                                        )}, wherein the ${lowerfirst(
                                                                            foundComponent.component
                                                                        )} has a specific heat from ${
                                                                            val.from ||
                                                                            '__'
                                                                        } to ${
                                                                            val.to ||
                                                                            '__'
                                                                        } ${
                                                                            UNITS[
                                                                                key
                                                                            ]
                                                                        }.`
                                                                    ),
                                                                ],
                                                            })
                                                        )
                                                    }
                                                    if (key === 'defined') {
                                                        childClaimArr.push(
                                                            new Paragraph({
                                                                heading:
                                                                    HeadingLevel.HEADING_6,
                                                                alignment:
                                                                    AlignmentType.LEFT,
                                                                ...(!isDownload
                                                                    ? {}
                                                                    : {
                                                                          numbering:
                                                                              {
                                                                                  reference:
                                                                                      'claims-numbering',
                                                                                  level: 0,
                                                                              },
                                                                      }),
                                                                children: [
                                                                    ...(!isDownload
                                                                        ? [
                                                                              new TextRun(
                                                                                  {
                                                                                      text: `${claimsCounter}. `,
                                                                                      color: '000000',
                                                                                      size: 16,
                                                                                  }
                                                                              ),
                                                                          ]
                                                                        : []),
                                                                    ...buildClaimRuns(
                                                                        `The ${category} of claim ${getClaimForFeature(
                                                                            `${component}+=>${subcomponent}`
                                                                        )}, wherein the ${
                                                                            val.description ||
                                                                            '__'
                                                                        } `
                                                                    ),
                                                                ],
                                                            })
                                                        )
                                                    }
                                                } else if (
                                                    typeof val !== 'object' &&
                                                    val === null
                                                ) {
                                                    if (
                                                        key ===
                                                        'thermalConductivity'
                                                    ) {
                                                        childClaimArr.push(
                                                            new Paragraph({
                                                                heading:
                                                                    HeadingLevel.HEADING_6,
                                                                alignment:
                                                                    AlignmentType.LEFT,
                                                                ...(!isDownload
                                                                    ? {}
                                                                    : {
                                                                          numbering:
                                                                              {
                                                                                  reference:
                                                                                      'claims-numbering',
                                                                                  level: 0,
                                                                              },
                                                                      }),
                                                                children: [
                                                                    ...(!isDownload
                                                                        ? [
                                                                              new TextRun(
                                                                                  {
                                                                                      text: `${claimsCounter}. `,
                                                                                      color: '000000',
                                                                                      size: 16,
                                                                                  }
                                                                              ),
                                                                          ]
                                                                        : []),
                                                                    ...buildClaimRuns(
                                                                        `The ${category} of claim ${getClaimForFeature(
                                                                            `${component}+=>${subcomponent}`
                                                                        )}, wherein the ${
                                                                            foundComponent.component
                                                                        } has a thermal conductivity from __ to __ ${
                                                                            UNITS[
                                                                                key
                                                                            ]
                                                                        }.`
                                                                    ),
                                                                ],
                                                            })
                                                        )
                                                    }
                                                    if (
                                                        key === 'meltingPoint'
                                                    ) {
                                                        childClaimArr.push(
                                                            new Paragraph({
                                                                heading:
                                                                    HeadingLevel.HEADING_6,
                                                                alignment:
                                                                    AlignmentType.LEFT,
                                                                ...(!isDownload
                                                                    ? {}
                                                                    : {
                                                                          numbering:
                                                                              {
                                                                                  reference:
                                                                                      'claims-numbering',
                                                                                  level: 0,
                                                                              },
                                                                      }),
                                                                children: [
                                                                    ...(!isDownload
                                                                        ? [
                                                                              new TextRun(
                                                                                  {
                                                                                      text: `${claimsCounter}. `,
                                                                                      color: '000000',
                                                                                      size: 16,
                                                                                  }
                                                                              ),
                                                                          ]
                                                                        : []),
                                                                    ...buildClaimRuns(
                                                                        `The ${category} of claim ${getClaimForFeature(
                                                                            `${component}+=>${subcomponent}`
                                                                        )}, wherein the ${
                                                                            foundComponent.component
                                                                        } has a melting point from __ to __ ${
                                                                            UNITS[
                                                                                key
                                                                            ]
                                                                        }.`
                                                                    ),
                                                                ],
                                                            })
                                                        )
                                                    }
                                                    if (
                                                        key === 'specificHeat'
                                                    ) {
                                                        childClaimArr.push(
                                                            new Paragraph({
                                                                heading:
                                                                    HeadingLevel.HEADING_6,
                                                                alignment:
                                                                    AlignmentType.LEFT,
                                                                ...(!isDownload
                                                                    ? {}
                                                                    : {
                                                                          numbering:
                                                                              {
                                                                                  reference:
                                                                                      'claims-numbering',
                                                                                  level: 0,
                                                                              },
                                                                      }),
                                                                children: [
                                                                    ...(!isDownload
                                                                        ? [
                                                                              new TextRun(
                                                                                  {
                                                                                      text: `${claimsCounter}. `,
                                                                                      color: '000000',
                                                                                      size: 16,
                                                                                  }
                                                                              ),
                                                                          ]
                                                                        : []),
                                                                    ...buildClaimRuns(
                                                                        `The ${category} of claim ${getClaimForFeature(
                                                                            `${component}+=>${subcomponent}`
                                                                        )}, wherein the ${
                                                                            foundComponent.component
                                                                        } has a specific heat from __ to __ ${
                                                                            UNITS[
                                                                                key
                                                                            ]
                                                                        }.`
                                                                    ),
                                                                ],
                                                            })
                                                        )
                                                    }
                                                    if (key === 'defined') {
                                                        childClaimArr.push(
                                                            new Paragraph({
                                                                heading:
                                                                    HeadingLevel.HEADING_6,
                                                                alignment:
                                                                    AlignmentType.LEFT,
                                                                ...(!isDownload
                                                                    ? {}
                                                                    : {
                                                                          numbering:
                                                                              {
                                                                                  reference:
                                                                                      'claims-numbering',
                                                                                  level: 0,
                                                                              },
                                                                      }),
                                                                children: [
                                                                    ...(!isDownload
                                                                        ? [
                                                                              new TextRun(
                                                                                  {
                                                                                      text: `${claimsCounter}. `,
                                                                                      color: '000000',
                                                                                      size: 16,
                                                                                  }
                                                                              ),
                                                                          ]
                                                                        : []),
                                                                    ...buildClaimRuns(
                                                                        `The ${category} of claim ${getClaimForFeature(
                                                                            `${component}+=>${subcomponent}`
                                                                        )}, wherein the ___`
                                                                    ),
                                                                ],
                                                            })
                                                        )
                                                    }
                                                }
                                            }
                                        }
                                    )
                                }
                            }
                        }
                    )
                }
            })
        }
        if (question === 'subFormatUserDefined' && pushSubFormatUser) {
            const typedFormRef = formRef as CustomFormInstance<MachineForm>
            pushSubFormatUser = false
            const subcomponentsForCharacteristics = typedFormRef.getFieldValue<
                MachineForm['subcomponentsForCharacteristics']
            >('subcomponentsForCharacteristics')

            const componentsForCharacteristics = typedFormRef.getFieldValue<
                MachineForm['componentsForCharacteristics']
            >('componentsForCharacteristics')

            const subcharacteristicComponent = formRef.getFieldValue<
                ValueOf<MachineValues['primaryComponentSubComponentsList']>
            >(['primaryComponentSubComponentsList'])

            componentsForCharacteristics.forEach((component: string) => {
                if (subcomponentsForCharacteristics[component] != null) {
                    subcomponentsForCharacteristics[component].forEach(
                        (subcomponent: string) => {
                            const characteristicComponents = Array.isArray(
                                subcharacteristicComponent
                            )
                                ? subcharacteristicComponent
                                : (subcharacteristicComponent as any)[component]

                            const foundComponent =
                                characteristicComponents.find(
                                    (x: any) => x?.id === subcomponent
                                )
                            if (foundComponent) {
                                const characteristic =
                                    typedFormRef.getFieldValue<
                                        MachineForm['subFormatUserDefined']
                                    >(['subFormatUserDefined', subcomponent])
                                if (characteristic) {
                                    Object.entries(characteristic).forEach(
                                        (entity: any) => {
                                            const [entityKey, val] = entity
                                            const key = entityKey
                                            if (val) {
                                                claimsCounter++
                                                if (
                                                    typeof val === 'object' &&
                                                    val !== null
                                                ) {
                                                    if (key === 'from') {
                                                        childClaimArr.push(
                                                            new Paragraph({
                                                                heading:
                                                                    HeadingLevel.HEADING_6,
                                                                alignment:
                                                                    AlignmentType.LEFT,
                                                                ...(!isDownload
                                                                    ? {}
                                                                    : {
                                                                          numbering:
                                                                              {
                                                                                  reference:
                                                                                      'claims-numbering',
                                                                                  level: 0,
                                                                              },
                                                                      }),
                                                                children: [
                                                                    ...(!isDownload
                                                                        ? [
                                                                              new TextRun(
                                                                                  {
                                                                                      text: `${claimsCounter}. `,
                                                                                      color: '000000',
                                                                                      size: 16,
                                                                                  }
                                                                              ),
                                                                          ]
                                                                        : []),
                                                                    ...buildClaimRuns(`The ${category} of claim ${getClaimForFeature(
                                                                        `${component}+=>${subcomponent}`
                                                                    )}, wherein the 
                                                                            ${lowerfirst(
                                                                                foundComponent.component
                                                                            )} has a ${
                                                                        val.name ||
                                                                        '__'
                                                                    } from ${
                                                                        val.minimum ||
                                                                        '__'
                                                                    } to ${
                                                                        val.maximum ||
                                                                        '__'
                                                                    } ${
                                                                        val.units ||
                                                                        ''
                                                                    } .`),
                                                                ],
                                                            })
                                                        )
                                                    }
                                                    if (key === 'equal') {
                                                        childClaimArr.push(
                                                            new Paragraph({
                                                                heading:
                                                                    HeadingLevel.HEADING_6,
                                                                alignment:
                                                                    AlignmentType.LEFT,
                                                                ...(!isDownload
                                                                    ? {}
                                                                    : {
                                                                          numbering:
                                                                              {
                                                                                  reference:
                                                                                      'claims-numbering',
                                                                                  level: 0,
                                                                              },
                                                                      }),
                                                                children: [
                                                                    ...(!isDownload
                                                                        ? [
                                                                              new TextRun(
                                                                                  {
                                                                                      text: `${claimsCounter}. `,
                                                                                      color: '000000',
                                                                                      size: 16,
                                                                                  }
                                                                              ),
                                                                          ]
                                                                        : []),
                                                                    ...buildClaimRuns(
                                                                        `The ${category} of claim ${getClaimForFeature(
                                                                            `${component}+=>${subcomponent}`
                                                                        )}, wherein the ${
                                                                            val.name ||
                                                                            '__'
                                                                        } of the ${lowerfirst(
                                                                            foundComponent.component
                                                                        )} is equal to from ${
                                                                            val.minimum ||
                                                                            '__'
                                                                        } to ${
                                                                            val.maximum ||
                                                                            '__'
                                                                        } ${
                                                                            val.units ||
                                                                            ''
                                                                        } .`
                                                                    ),
                                                                ],
                                                            })
                                                        )
                                                    }
                                                    if (key === 'defined') {
                                                        childClaimArr.push(
                                                            new Paragraph({
                                                                heading:
                                                                    HeadingLevel.HEADING_6,
                                                                alignment:
                                                                    AlignmentType.LEFT,
                                                                ...(!isDownload
                                                                    ? {}
                                                                    : {
                                                                          numbering:
                                                                              {
                                                                                  reference:
                                                                                      'claims-numbering',
                                                                                  level: 0,
                                                                              },
                                                                      }),
                                                                children: [
                                                                    ...(!isDownload
                                                                        ? [
                                                                              new TextRun(
                                                                                  {
                                                                                      text: `${claimsCounter}. `,
                                                                                      color: '000000',
                                                                                      size: 16,
                                                                                  }
                                                                              ),
                                                                          ]
                                                                        : []),
                                                                    ...buildClaimRuns(`The ${category} of claim ${getClaimForFeature(
                                                                        `${component}+=>${subcomponent}`
                                                                    )}, wherein the ${
                                                                        val.description ||
                                                                        '___'
                                                                    }
                                                             .`),
                                                                ],
                                                            })
                                                        )
                                                    }
                                                } else if (
                                                    typeof val !== 'object' &&
                                                    val === null
                                                ) {
                                                    if (key === 'body') {
                                                        childClaimArr.push(
                                                            new Paragraph({
                                                                heading:
                                                                    HeadingLevel.HEADING_6,
                                                                alignment:
                                                                    AlignmentType.LEFT,
                                                                ...(!isDownload
                                                                    ? {}
                                                                    : {
                                                                          numbering:
                                                                              {
                                                                                  reference:
                                                                                      'claims-numbering',
                                                                                  level: 0,
                                                                              },
                                                                      }),
                                                                children: [
                                                                    ...(!isDownload
                                                                        ? [
                                                                              new TextRun(
                                                                                  {
                                                                                      text: `${claimsCounter}. `,
                                                                                      color: '000000',
                                                                                      size: 16,
                                                                                  }
                                                                              ),
                                                                          ]
                                                                        : []),
                                                                    ...buildClaimRuns(
                                                                        `The ${category} of claim ${getClaimForFeature(
                                                                            `${component}+=>${subcomponent}`
                                                                        )}, wherein the ___ has a ____ from __ to __.`
                                                                    ),
                                                                ],
                                                            })
                                                        )
                                                    }
                                                    if (key === 'equal') {
                                                        childClaimArr.push(
                                                            new Paragraph({
                                                                heading:
                                                                    HeadingLevel.HEADING_6,
                                                                alignment:
                                                                    AlignmentType.LEFT,
                                                                ...(!isDownload
                                                                    ? {}
                                                                    : {
                                                                          numbering:
                                                                              {
                                                                                  reference:
                                                                                      'claims-numbering',
                                                                                  level: 0,
                                                                              },
                                                                      }),
                                                                children: [
                                                                    ...(!isDownload
                                                                        ? [
                                                                              new TextRun(
                                                                                  {
                                                                                      text: `${claimsCounter}. `,
                                                                                      color: '000000',
                                                                                      size: 16,
                                                                                  }
                                                                              ),
                                                                          ]
                                                                        : []),
                                                                    ...buildClaimRuns(
                                                                        `The ${category} of claim ${getClaimForFeature(
                                                                            `${component}+=>${subcomponent}`
                                                                        )}, wherein the ___ of the ____ is equal to from __ to __.`
                                                                    ),
                                                                ],
                                                            })
                                                        )
                                                    }
                                                    if (key === 'defined') {
                                                        childClaimArr.push(
                                                            new Paragraph({
                                                                heading:
                                                                    HeadingLevel.HEADING_6,
                                                                alignment:
                                                                    AlignmentType.LEFT,
                                                                ...(!isDownload
                                                                    ? {}
                                                                    : {
                                                                          numbering:
                                                                              {
                                                                                  reference:
                                                                                      'claims-numbering',
                                                                                  level: 0,
                                                                              },
                                                                      }),
                                                                children: [
                                                                    ...(!isDownload
                                                                        ? [
                                                                              new TextRun(
                                                                                  {
                                                                                      text: `${claimsCounter}. `,
                                                                                      color: '000000',
                                                                                      size: 16,
                                                                                  }
                                                                              ),
                                                                          ]
                                                                        : []),
                                                                    ...buildClaimRuns(
                                                                        `The ${category} of claim ${getClaimForFeature(
                                                                            `${component}+=>${subcomponent}`
                                                                        )}, wherein the ___.`
                                                                    ),
                                                                ],
                                                            })
                                                        )
                                                    }
                                                }
                                            }
                                        }
                                    )
                                }
                            }
                        }
                    )
                }
            })
        }
        if (
            question === MachineNames.addCompCharacteristic &&
            pushAddCompCharacteristics &&
            isMachineForm(formRef, question)
        ) {
            pushAddCompCharacteristics = false
            const additionalComponentsForCharacteristics =
                formRef.getFieldValue(
                    MachineNames.additionalComponentsForCharacteristics
                ) as MachineValues['additionalComponentsForCharacteristics']
            const featuresPartsOfIndependentClaim = formRef.getFieldValue<
                MachineValues['featuresPartsOfIndependentClaim']
            >(['featuresPartsOfIndependentClaim'])
            const summaryOfSelectedEmbodimentsOfTheInvention =
                formRef.getFieldValue<
                    FormValues['summaryOfSelectedEmbodimentsOfTheInvention']
                >(['summaryOfSelectedEmbodimentsOfTheInvention'])
            additionalComponentsForCharacteristics.forEach(
                (component: string) => {
                    const allComponents = formRef.getFieldValue<
                        MachineForm['primaryAdditionalComponents']
                    >(MachineNames.primaryAdditionalComponents)
                    const foundComponent = allComponents.find(
                        (x) => x?.id === component
                    )
                    if (foundComponent) {
                        const characteristic = formRef.getFieldValue([
                            MachineNames.addCompCharacteristic,
                            component,
                        ]) as ValueOf<MachineValues['addCompCharacteristic']>
                        if (characteristic) {
                            Object.entries(characteristic).forEach((entity) => {
                                const [entityKey, val] = entity
                                const key = entityKey as keyof typeof UNITS
                                const parentClaimAdditionalComponentData =
                                    parentClaimAdditionalComponents?.find(
                                        (x) => x?.id === component
                                    )
                                if (val) {
                                    if (
                                        parentClaimAdditionalComponentData &&
                                        (!summaryOfSelectedEmbodimentsOfTheInvention ||
                                            summaryOfSelectedEmbodimentsOfTheInvention.includes(
                                                `addCompCharacteristic_${component}.${key}`
                                            )) &&
                                        (summaryOfSelectedEmbodimentsOfTheInvention ||
                                            !featuresPartsOfIndependentClaim ||
                                            featuresPartsOfIndependentClaim.includes(
                                                `addCompCharacteristic_${component}.${key}`
                                            ))
                                    ) {
                                        if (
                                            typeof val === 'object' &&
                                            val !== null
                                        ) {
                                            if (key === 'defined') {
                                                parentClaimArr.push(
                                                    new Paragraph({
                                                        heading:
                                                            HeadingLevel.HEADING_6,
                                                        alignment:
                                                            AlignmentType.LEFT,
                                                        children: [
                                                            new TextRun({
                                                                text: `wherein the ${
                                                                    'description' in
                                                                    val
                                                                        ? val.description
                                                                        : '__'
                                                                }.`,
                                                                color: '000000',
                                                                size: 16,
                                                            }),
                                                        ],
                                                    })
                                                )
                                            } else {
                                                parentClaimArr.push(
                                                    new Paragraph({
                                                        heading:
                                                            HeadingLevel.HEADING_6,
                                                        alignment:
                                                            AlignmentType.LEFT,
                                                        children: [
                                                            new TextRun({
                                                                text: `wherein the ${foundComponent.component!} has a ${key} from ${
                                                                    'from' in
                                                                    val
                                                                        ? val.from
                                                                        : '__'
                                                                } to ${
                                                                    'to' in val
                                                                        ? val.to
                                                                        : '__'
                                                                } ${
                                                                    UNITS[key]
                                                                }.`,
                                                                color: '000000',
                                                                size: 16,
                                                            }),
                                                        ],
                                                    })
                                                )
                                            }
                                        } else if (
                                            typeof val !== 'object' &&
                                            val === null
                                        ) {
                                            if (key === 'defined') {
                                                parentClaimArr.push(
                                                    new Paragraph({
                                                        heading:
                                                            HeadingLevel.HEADING_6,
                                                        alignment:
                                                            AlignmentType.LEFT,
                                                        children: [
                                                            new TextRun({
                                                                text: `wherein the ___.`,
                                                                color: '000000',
                                                                size: 16,
                                                            }),
                                                        ],
                                                    })
                                                )
                                            } else {
                                                parentClaimArr.push(
                                                    new Paragraph({
                                                        heading:
                                                            HeadingLevel.HEADING_6,
                                                        alignment:
                                                            AlignmentType.LEFT,
                                                        children: [
                                                            new TextRun({
                                                                text: `wherein the ${foundComponent.component!} has a ${key} from __ to __ ${
                                                                    UNITS[key]
                                                                }.`,
                                                                color: '000000',
                                                                size: 16,
                                                            }),
                                                        ],
                                                    })
                                                )
                                            }
                                        }
                                    } else {
                                        claimsCounter++
                                        if (
                                            typeof val === 'object' &&
                                            val !== null
                                        ) {
                                            if (key === 'defined') {
                                                childClaimArr.push(
                                                    new Paragraph({
                                                        heading:
                                                            HeadingLevel.HEADING_6,
                                                        alignment:
                                                            AlignmentType.LEFT,
                                                        ...(!isDownload
                                                            ? {}
                                                            : {
                                                                  numbering: {
                                                                      reference:
                                                                          'claims-numbering',
                                                                      level: 0,
                                                                  },
                                                              }),
                                                        children: [
                                                            ...(!isDownload
                                                                ? [
                                                                      new TextRun(
                                                                          {
                                                                              text: `${claimsCounter}. `,
                                                                              color: '000000',
                                                                              size: 16,
                                                                          }
                                                                      ),
                                                                  ]
                                                                : []),
                                                            ...buildClaimRuns(
                                                                `The ${category}  of claim ${getClaimForFeature(
                                                                    component
                                                                )}, wherein the ${
                                                                    'description' in
                                                                    val
                                                                        ? val.description
                                                                        : '__'
                                                                }.`
                                                            ),
                                                        ],
                                                    })
                                                )
                                            } else {
                                                childClaimArr.push(
                                                    new Paragraph({
                                                        heading:
                                                            HeadingLevel.HEADING_6,
                                                        alignment:
                                                            AlignmentType.LEFT,
                                                        ...(!isDownload
                                                            ? {}
                                                            : {
                                                                  numbering: {
                                                                      reference:
                                                                          'claims-numbering',
                                                                      level: 0,
                                                                  },
                                                              }),
                                                        children: [
                                                            ...(!isDownload
                                                                ? [
                                                                      new TextRun(
                                                                          {
                                                                              text: `${claimsCounter}. `,
                                                                              color: '000000',
                                                                              size: 16,
                                                                          }
                                                                      ),
                                                                  ]
                                                                : []),
                                                            ...buildClaimRuns(
                                                                `The ${category} of claim ${getClaimForFeature(
                                                                    component
                                                                )}, wherein the ${foundComponent.component!} has a ${key} from ${
                                                                    'from' in
                                                                    val
                                                                        ? val.from
                                                                        : '__'
                                                                } to ${
                                                                    'to' in val
                                                                        ? val.to
                                                                        : '__'
                                                                } ${
                                                                    UNITS[key]
                                                                }.`
                                                            ),
                                                        ],
                                                    })
                                                )
                                            }
                                        } else if (
                                            typeof val !== 'object' &&
                                            val === null
                                        ) {
                                            if (key === 'defined') {
                                                childClaimArr.push(
                                                    new Paragraph({
                                                        heading:
                                                            HeadingLevel.HEADING_6,
                                                        alignment:
                                                            AlignmentType.LEFT,
                                                        ...(!isDownload
                                                            ? {}
                                                            : {
                                                                  numbering: {
                                                                      reference:
                                                                          'claims-numbering',
                                                                      level: 0,
                                                                  },
                                                              }),
                                                        children: [
                                                            ...(!isDownload
                                                                ? [
                                                                      new TextRun(
                                                                          {
                                                                              text: `${claimsCounter}. `,
                                                                              color: '000000',
                                                                              size: 16,
                                                                          }
                                                                      ),
                                                                  ]
                                                                : []),
                                                            ...buildClaimRuns(
                                                                `The ${category} of claim ${getClaimForFeature(
                                                                    component
                                                                )}, wherein the ___.`
                                                            ),
                                                        ],
                                                    })
                                                )
                                            } else {
                                                childClaimArr.push(
                                                    new Paragraph({
                                                        heading:
                                                            HeadingLevel.HEADING_6,
                                                        alignment:
                                                            AlignmentType.LEFT,
                                                        ...(!isDownload
                                                            ? {}
                                                            : {
                                                                  numbering: {
                                                                      reference:
                                                                          'claims-numbering',
                                                                      level: 0,
                                                                  },
                                                              }),
                                                        children: [
                                                            ...(!isDownload
                                                                ? [
                                                                      new TextRun(
                                                                          {
                                                                              text: `${claimsCounter}. `,
                                                                              color: '000000',
                                                                              size: 16,
                                                                          }
                                                                      ),
                                                                  ]
                                                                : []),
                                                            ...buildClaimRuns(
                                                                `The ${category} of claim ${getClaimForFeature(
                                                                    component
                                                                )}, wherein the ${foundComponent.component!} has a ${key} from __ to __ ${
                                                                    UNITS[key]
                                                                }.`
                                                            ),
                                                        ],
                                                    })
                                                )
                                            }
                                        }
                                    }
                                }
                            })
                        }
                    }
                }
            )
        }
        if (
            question === 'addCompAdditionalCharacteristic' &&
            pushAddCompAdditionalCharacteristic &&
            isMachineForm(formRef, question)
        ) {
            pushAddCompAdditionalCharacteristic = false
            const additionalComponentsForCharacteristics =
                formRef.getFieldValue<
                    MachineValues['additionalComponentsForCharacteristics']
                >('additionalComponentsForCharacteristics')
            const featuresPartsOfIndependentClaim = formRef.getFieldValue<
                MachineValues['featuresPartsOfIndependentClaim']
            >(['featuresPartsOfIndependentClaim'])
            const summaryOfSelectedEmbodimentsOfTheInvention =
                formRef.getFieldValue<
                    FormValues['summaryOfSelectedEmbodimentsOfTheInvention']
                >(['summaryOfSelectedEmbodimentsOfTheInvention'])
            additionalComponentsForCharacteristics.forEach(
                (component: string) => {
                    const allComponents = formRef.getFieldValue<
                        MachineForm['primaryAdditionalComponents']
                    >(MachineNames.primaryAdditionalComponents)
                    const foundComponent = allComponents.find(
                        (x) => x?.id === component
                    )
                    if (foundComponent) {
                        const characteristic = formRef.getFieldValue<
                            ValueOf<
                                MachineValues['addCompAdditionalCharacteristic']
                            >
                        >(['addCompAdditionalCharacteristic', component])
                        if (characteristic) {
                            Object.entries(characteristic).forEach((entity) => {
                                const [entityKey, val] = entity
                                const key = entityKey as keyof typeof UNITS
                                const parentClaimAdditionalComponentData =
                                    parentClaimAdditionalComponents?.find(
                                        (x) => x?.id === component
                                    )
                                if (val) {
                                    if (
                                        parentClaimAdditionalComponentData &&
                                        (!summaryOfSelectedEmbodimentsOfTheInvention ||
                                            summaryOfSelectedEmbodimentsOfTheInvention.includes(
                                                `addCompAdditionalCharacteristic_${component}.${key}`
                                            )) &&
                                        (summaryOfSelectedEmbodimentsOfTheInvention ||
                                            !featuresPartsOfIndependentClaim ||
                                            featuresPartsOfIndependentClaim.includes(
                                                `addCompAdditionalCharacteristic_${component}.${key}`
                                            ))
                                    ) {
                                        if (
                                            typeof val === 'object' &&
                                            val !== null
                                        ) {
                                            if ('description' in val) {
                                                if (key === 'defined') {
                                                    parentClaimArr.push(
                                                        new Paragraph({
                                                            heading:
                                                                HeadingLevel.HEADING_6,
                                                            alignment:
                                                                AlignmentType.LEFT,
                                                            children: [
                                                                new TextRun({
                                                                    text: `wherein the ${
                                                                        'description' in
                                                                        val
                                                                            ? val.description
                                                                            : '__'
                                                                    }.`,
                                                                    color: '000000',
                                                                    size: 16,
                                                                }),
                                                            ],
                                                        })
                                                    )
                                                }
                                            } else {
                                                if (
                                                    key ===
                                                    'thermalConductivity'
                                                ) {
                                                    parentClaimArr.push(
                                                        new Paragraph({
                                                            heading:
                                                                HeadingLevel.HEADING_6,
                                                            alignment:
                                                                AlignmentType.LEFT,
                                                            children: [
                                                                new TextRun({
                                                                    text: `wherein the ${
                                                                        foundComponent.component
                                                                    } has a thermal conductivity from ${
                                                                        val.from ||
                                                                        '__'
                                                                    } to ${
                                                                        val.to ||
                                                                        '__'
                                                                    } ${
                                                                        UNITS[
                                                                            key
                                                                        ]
                                                                    }.`,
                                                                    color: '000000',
                                                                    size: 16,
                                                                }),
                                                            ],
                                                        })
                                                    )
                                                }
                                                if (key === 'meltingPoint') {
                                                    parentClaimArr.push(
                                                        new Paragraph({
                                                            heading:
                                                                HeadingLevel.HEADING_6,
                                                            alignment:
                                                                AlignmentType.LEFT,
                                                            children: [
                                                                new TextRun({
                                                                    text: `wherein the ${
                                                                        foundComponent.component
                                                                    } has a melting point from ${
                                                                        val.from ||
                                                                        '__'
                                                                    } to ${
                                                                        val.to ||
                                                                        '__'
                                                                    } ${
                                                                        UNITS[
                                                                            key
                                                                        ]
                                                                    }.`,
                                                                    color: '000000',
                                                                    size: 16,
                                                                }),
                                                            ],
                                                        })
                                                    )
                                                }
                                                if (key === 'specificHeat') {
                                                    parentClaimArr.push(
                                                        new Paragraph({
                                                            heading:
                                                                HeadingLevel.HEADING_6,
                                                            alignment:
                                                                AlignmentType.LEFT,
                                                            children: [
                                                                new TextRun({
                                                                    text: `wherein the ${
                                                                        foundComponent.component
                                                                    } has a specific heat from ${
                                                                        val.from ||
                                                                        '__'
                                                                    } to ${
                                                                        val.to ||
                                                                        '__'
                                                                    } ${
                                                                        UNITS[
                                                                            key
                                                                        ]
                                                                    }.`,
                                                                    color: '000000',
                                                                    size: 16,
                                                                }),
                                                            ],
                                                        })
                                                    )
                                                }
                                            }
                                        } else if (
                                            typeof val !== 'object' &&
                                            val === null
                                        ) {
                                            if (key === 'thermalConductivity') {
                                                parentClaimArr.push(
                                                    new Paragraph({
                                                        heading:
                                                            HeadingLevel.HEADING_6,
                                                        alignment:
                                                            AlignmentType.LEFT,
                                                        children: [
                                                            new TextRun({
                                                                text: `wherein the ${foundComponent.component} has a thermal conductivity from __ to __ ${UNITS[key]}.`,
                                                                color: '000000',
                                                                size: 16,
                                                            }),
                                                        ],
                                                    })
                                                )
                                            }
                                            if (key === 'meltingPoint') {
                                                parentClaimArr.push(
                                                    new Paragraph({
                                                        heading:
                                                            HeadingLevel.HEADING_6,
                                                        alignment:
                                                            AlignmentType.LEFT,
                                                        children: [
                                                            new TextRun({
                                                                text: `wherein the ${foundComponent.component} has a melting point from __ to __ ${UNITS[key]}.`,
                                                                color: '000000',
                                                                size: 16,
                                                            }),
                                                        ],
                                                    })
                                                )
                                            }
                                            if (key === 'specificHeat') {
                                                parentClaimArr.push(
                                                    new Paragraph({
                                                        heading:
                                                            HeadingLevel.HEADING_6,
                                                        alignment:
                                                            AlignmentType.LEFT,
                                                        children: [
                                                            new TextRun({
                                                                text: `wherein the ${foundComponent.component} has a specific heat from __ to __ ${UNITS[key]}.`,
                                                                color: '000000',
                                                                size: 16,
                                                            }),
                                                        ],
                                                    })
                                                )
                                            }
                                            if (key === 'defined') {
                                                parentClaimArr.push(
                                                    new Paragraph({
                                                        heading:
                                                            HeadingLevel.HEADING_6,
                                                        alignment:
                                                            AlignmentType.LEFT,
                                                        children: [
                                                            new TextRun({
                                                                text: `wherein the ___.`,
                                                                color: '000000',
                                                                size: 16,
                                                            }),
                                                        ],
                                                    })
                                                )
                                            }
                                        }
                                    } else {
                                        claimsCounter++
                                        if (
                                            typeof val === 'object' &&
                                            val !== null
                                        ) {
                                            if ('description' in val) {
                                                if (key === 'defined') {
                                                    childClaimArr.push(
                                                        new Paragraph({
                                                            heading:
                                                                HeadingLevel.HEADING_6,
                                                            alignment:
                                                                AlignmentType.LEFT,
                                                            ...(!isDownload
                                                                ? {}
                                                                : {
                                                                      numbering:
                                                                          {
                                                                              reference:
                                                                                  'claims-numbering',
                                                                              level: 0,
                                                                          },
                                                                  }),
                                                            children: [
                                                                ...(!isDownload
                                                                    ? [
                                                                          new TextRun(
                                                                              {
                                                                                  text: `${claimsCounter}. `,
                                                                                  color: '000000',
                                                                                  size: 16,
                                                                              }
                                                                          ),
                                                                      ]
                                                                    : []),
                                                                ...buildClaimRuns(
                                                                    `The ${category} of claim ${getClaimForFeature(
                                                                        component
                                                                    )}, wherein the ${
                                                                        'description' in
                                                                        val
                                                                            ? val.description
                                                                            : '__'
                                                                    }.`
                                                                ),
                                                            ],
                                                        })
                                                    )
                                                }
                                            } else {
                                                if (
                                                    key ===
                                                    'thermalConductivity'
                                                ) {
                                                    childClaimArr.push(
                                                        new Paragraph({
                                                            heading:
                                                                HeadingLevel.HEADING_6,
                                                            alignment:
                                                                AlignmentType.LEFT,
                                                            ...(!isDownload
                                                                ? {}
                                                                : {
                                                                      numbering:
                                                                          {
                                                                              reference:
                                                                                  'claims-numbering',
                                                                              level: 0,
                                                                          },
                                                                  }),
                                                            children: [
                                                                ...(!isDownload
                                                                    ? [
                                                                          new TextRun(
                                                                              {
                                                                                  text: `${claimsCounter}. `,
                                                                                  color: '000000',
                                                                                  size: 16,
                                                                              }
                                                                          ),
                                                                      ]
                                                                    : []),
                                                                ...buildClaimRuns(
                                                                    `The ${category} of claim ${getClaimForFeature(
                                                                        component
                                                                    )}, wherein the ${
                                                                        foundComponent.component
                                                                    } has a thermal conductivity from ${
                                                                        val.from ||
                                                                        '__'
                                                                    } to ${
                                                                        val.to ||
                                                                        '__'
                                                                    } ${
                                                                        UNITS[
                                                                            key
                                                                        ]
                                                                    }.`
                                                                ),
                                                            ],
                                                        })
                                                    )
                                                }
                                                if (key === 'meltingPoint') {
                                                    childClaimArr.push(
                                                        new Paragraph({
                                                            heading:
                                                                HeadingLevel.HEADING_6,
                                                            alignment:
                                                                AlignmentType.LEFT,
                                                            ...(!isDownload
                                                                ? {}
                                                                : {
                                                                      numbering:
                                                                          {
                                                                              reference:
                                                                                  'claims-numbering',
                                                                              level: 0,
                                                                          },
                                                                  }),
                                                            children: [
                                                                ...(!isDownload
                                                                    ? [
                                                                          new TextRun(
                                                                              {
                                                                                  text: `${claimsCounter}. `,
                                                                                  color: '000000',
                                                                                  size: 16,
                                                                              }
                                                                          ),
                                                                      ]
                                                                    : []),
                                                                ...buildClaimRuns(
                                                                    `The ${category} of claim ${getClaimForFeature(
                                                                        component
                                                                    )}, wherein the ${
                                                                        foundComponent.component
                                                                    } has a melting point from ${
                                                                        val.from ||
                                                                        '__'
                                                                    } to ${
                                                                        val.to ||
                                                                        '__'
                                                                    } ${
                                                                        UNITS[
                                                                            key
                                                                        ]
                                                                    }.`
                                                                ),
                                                            ],
                                                        })
                                                    )
                                                }
                                                if (key === 'specificHeat') {
                                                    childClaimArr.push(
                                                        new Paragraph({
                                                            heading:
                                                                HeadingLevel.HEADING_6,
                                                            alignment:
                                                                AlignmentType.LEFT,
                                                            ...(!isDownload
                                                                ? {}
                                                                : {
                                                                      numbering:
                                                                          {
                                                                              reference:
                                                                                  'claims-numbering',
                                                                              level: 0,
                                                                          },
                                                                  }),
                                                            children: [
                                                                ...(!isDownload
                                                                    ? [
                                                                          new TextRun(
                                                                              {
                                                                                  text: `${claimsCounter}. `,
                                                                                  color: '000000',
                                                                                  size: 16,
                                                                              }
                                                                          ),
                                                                      ]
                                                                    : []),
                                                                ...buildClaimRuns(
                                                                    `The ${category} of claim ${getClaimForFeature(
                                                                        component
                                                                    )}, wherein the ${
                                                                        foundComponent.component
                                                                    } has a specific heat from ${
                                                                        val.from ||
                                                                        '__'
                                                                    } to ${
                                                                        val.to ||
                                                                        '__'
                                                                    } ${
                                                                        UNITS[
                                                                            key
                                                                        ]
                                                                    }.`
                                                                ),
                                                            ],
                                                        })
                                                    )
                                                }
                                            }
                                        } else if (
                                            typeof val !== 'object' &&
                                            val === null
                                        ) {
                                            if (key === 'thermalConductivity') {
                                                childClaimArr.push(
                                                    new Paragraph({
                                                        heading:
                                                            HeadingLevel.HEADING_6,
                                                        alignment:
                                                            AlignmentType.LEFT,
                                                        ...(!isDownload
                                                            ? {}
                                                            : {
                                                                  numbering: {
                                                                      reference:
                                                                          'claims-numbering',
                                                                      level: 0,
                                                                  },
                                                              }),
                                                        children: [
                                                            ...(!isDownload
                                                                ? [
                                                                      new TextRun(
                                                                          {
                                                                              text: `${claimsCounter}. `,
                                                                              color: '000000',
                                                                              size: 16,
                                                                          }
                                                                      ),
                                                                  ]
                                                                : []),
                                                            ...buildClaimRuns(
                                                                `The ${category} of claim ${getClaimForFeature(
                                                                    component
                                                                )}, wherein the ${
                                                                    foundComponent.component
                                                                } has a thermal conductivity from __ to __ ${
                                                                    UNITS[key]
                                                                }.`
                                                            ),
                                                        ],
                                                    })
                                                )
                                            }
                                            if (key === 'meltingPoint') {
                                                childClaimArr.push(
                                                    new Paragraph({
                                                        heading:
                                                            HeadingLevel.HEADING_6,
                                                        alignment:
                                                            AlignmentType.LEFT,
                                                        ...(!isDownload
                                                            ? {}
                                                            : {
                                                                  numbering: {
                                                                      reference:
                                                                          'claims-numbering',
                                                                      level: 0,
                                                                  },
                                                              }),
                                                        children: [
                                                            ...(!isDownload
                                                                ? [
                                                                      new TextRun(
                                                                          {
                                                                              text: `${claimsCounter}. `,
                                                                              color: '000000',
                                                                              size: 16,
                                                                          }
                                                                      ),
                                                                  ]
                                                                : []),
                                                            ...buildClaimRuns(
                                                                `The ${category} of claim ${getClaimForFeature(
                                                                    component
                                                                )}, wherein the ${
                                                                    foundComponent.component
                                                                } has a melting point from __ to __ ${
                                                                    UNITS[key]
                                                                }.`
                                                            ),
                                                        ],
                                                    })
                                                )
                                            }
                                            if (key === 'specificHeat') {
                                                childClaimArr.push(
                                                    new Paragraph({
                                                        heading:
                                                            HeadingLevel.HEADING_6,
                                                        alignment:
                                                            AlignmentType.LEFT,
                                                        ...(!isDownload
                                                            ? {}
                                                            : {
                                                                  numbering: {
                                                                      reference:
                                                                          'claims-numbering',
                                                                      level: 0,
                                                                  },
                                                              }),
                                                        children: [
                                                            ...(!isDownload
                                                                ? [
                                                                      new TextRun(
                                                                          {
                                                                              text: `${claimsCounter}. `,
                                                                              color: '000000',
                                                                              size: 16,
                                                                          }
                                                                      ),
                                                                  ]
                                                                : []),
                                                            ...buildClaimRuns(
                                                                `The ${category} of claim ${getClaimForFeature(
                                                                    component
                                                                )}, wherein the ${
                                                                    foundComponent.component
                                                                } has a specific heat from __ to __ ${
                                                                    UNITS[key]
                                                                }.`
                                                            ),
                                                        ],
                                                    })
                                                )
                                            }
                                            if (key === 'defined') {
                                                childClaimArr.push(
                                                    new Paragraph({
                                                        heading:
                                                            HeadingLevel.HEADING_6,
                                                        alignment:
                                                            AlignmentType.LEFT,
                                                        ...(!isDownload
                                                            ? {}
                                                            : {
                                                                  numbering: {
                                                                      reference:
                                                                          'claims-numbering',
                                                                      level: 0,
                                                                  },
                                                              }),
                                                        children: [
                                                            ...(!isDownload
                                                                ? [
                                                                      new TextRun(
                                                                          {
                                                                              text: `${claimsCounter}. `,
                                                                              color: '000000',
                                                                              size: 16,
                                                                          }
                                                                      ),
                                                                  ]
                                                                : []),
                                                            ...buildClaimRuns(
                                                                `The ${category} of claim ${getClaimForFeature(
                                                                    component
                                                                )}, wherein the ___.`
                                                            ),
                                                        ],
                                                    })
                                                )
                                            }
                                        }
                                    }
                                }
                            })
                        }
                    }
                }
            )
        }
        if (
            question === 'addCompFormatUserDefined' &&
            pushAddCompFormatUser &&
            isMachineForm(formRef, question)
        ) {
            pushAddCompFormatUser = false
            const additionalComponentsForCharacteristics =
                formRef.getFieldValue<
                    MachineValues['additionalComponentsForCharacteristics']
                >('additionalComponentsForCharacteristics')
            const featuresPartsOfIndependentClaim = formRef.getFieldValue<
                MachineValues['featuresPartsOfIndependentClaim']
            >(['featuresPartsOfIndependentClaim'])
            const summaryOfSelectedEmbodimentsOfTheInvention =
                formRef.getFieldValue<
                    FormValues['summaryOfSelectedEmbodimentsOfTheInvention']
                >(['summaryOfSelectedEmbodimentsOfTheInvention'])
            additionalComponentsForCharacteristics.forEach(
                (component: string) => {
                    const characteristic = formRef.getFieldValue<
                        ValueOf<MachineValues['addCompFormatUserDefined']>
                    >(['addCompFormatUserDefined', component])
                    if (characteristic) {
                        Object.entries(characteristic).forEach(
                            (entity: any) => {
                                const [entityKey, val] = entity
                                const key = entityKey
                                const allComponents = formRef.getFieldValue<
                                    MachineForm['primaryAdditionalComponents']
                                >(MachineNames.primaryAdditionalComponents)
                                const foundComponent = allComponents.find(
                                    (x) => x?.id === component
                                )
                                if (foundComponent) {
                                    const parentClaimAdditionalComponentData =
                                        parentClaimAdditionalComponents?.find(
                                            (x) => x?.id === component
                                        )
                                    if (val) {
                                        if (
                                            parentClaimAdditionalComponentData &&
                                            (!summaryOfSelectedEmbodimentsOfTheInvention ||
                                                summaryOfSelectedEmbodimentsOfTheInvention.includes(
                                                    `addCompFormatUserDefined_${component}.${key}`
                                                )) &&
                                            (summaryOfSelectedEmbodimentsOfTheInvention ||
                                                !featuresPartsOfIndependentClaim ||
                                                featuresPartsOfIndependentClaim.includes(
                                                    `addCompFormatUserDefined_${component}.${key}`
                                                ))
                                        ) {
                                            if (
                                                typeof val === 'object' &&
                                                val !== null
                                            ) {
                                                if (key === 'from') {
                                                    parentClaimArr.push(
                                                        new Paragraph({
                                                            heading:
                                                                HeadingLevel.HEADING_6,
                                                            alignment:
                                                                AlignmentType.LEFT,
                                                            children: [
                                                                new TextRun({
                                                                    text: `wherein the ${foundComponent.component!} has a ${
                                                                        val.name ||
                                                                        '__'
                                                                    } from ${
                                                                        val.minimum ||
                                                                        '__'
                                                                    } to ${
                                                                        val.maximum ||
                                                                        '__'
                                                                    } ${
                                                                        val.units ||
                                                                        ''
                                                                    }.`,
                                                                    color: '000000',
                                                                    size: 16,
                                                                }),
                                                            ],
                                                        })
                                                    )
                                                }
                                                if (key === 'equal') {
                                                    parentClaimArr.push(
                                                        new Paragraph({
                                                            heading:
                                                                HeadingLevel.HEADING_6,
                                                            alignment:
                                                                AlignmentType.LEFT,
                                                            children: [
                                                                new TextRun({
                                                                    text: `wherein the ${
                                                                        val.name ||
                                                                        '__'
                                                                    } of the ${foundComponent.component!} is equal to from ${
                                                                        val.minimum ||
                                                                        '__'
                                                                    } to ${
                                                                        val.maximum ||
                                                                        '__'
                                                                    } ${
                                                                        val.units ||
                                                                        ''
                                                                    }.`,
                                                                    color: '000000',
                                                                    size: 16,
                                                                }),
                                                            ],
                                                        })
                                                    )
                                                }
                                                if (key === 'defined') {
                                                    parentClaimArr.push(
                                                        new Paragraph({
                                                            heading:
                                                                HeadingLevel.HEADING_6,
                                                            alignment:
                                                                AlignmentType.LEFT,
                                                            children: [
                                                                new TextRun({
                                                                    text: `wherein the ${
                                                                        val.description ||
                                                                        '___'
                                                                    }.`,
                                                                    color: '000000',
                                                                    size: 16,
                                                                }),
                                                            ],
                                                        })
                                                    )
                                                }
                                            } else if (
                                                typeof val !== 'object' &&
                                                val === null
                                            ) {
                                                if (key === 'body') {
                                                    parentClaimArr.push(
                                                        new Paragraph({
                                                            heading:
                                                                HeadingLevel.HEADING_6,
                                                            alignment:
                                                                AlignmentType.LEFT,
                                                            children: [
                                                                new TextRun({
                                                                    text: `wherein the ___ has a ____ from __ to __.`,
                                                                    color: '000000',
                                                                    size: 16,
                                                                }),
                                                            ],
                                                        })
                                                    )
                                                }
                                                if (key === 'equal') {
                                                    parentClaimArr.push(
                                                        new Paragraph({
                                                            heading:
                                                                HeadingLevel.HEADING_6,
                                                            alignment:
                                                                AlignmentType.LEFT,
                                                            children: [
                                                                new TextRun({
                                                                    text: `wherein the ___ of the ____ is equal to from __ to __.`,
                                                                    color: '000000',
                                                                    size: 16,
                                                                }),
                                                            ],
                                                        })
                                                    )
                                                }
                                                if (key === 'defined') {
                                                    parentClaimArr.push(
                                                        new Paragraph({
                                                            heading:
                                                                HeadingLevel.HEADING_6,
                                                            alignment:
                                                                AlignmentType.LEFT,
                                                            children: [
                                                                new TextRun({
                                                                    text: `wherein the ___.`,
                                                                    color: '000000',
                                                                    size: 16,
                                                                }),
                                                            ],
                                                        })
                                                    )
                                                }
                                            }
                                        } else {
                                            claimsCounter++
                                            if (
                                                typeof val === 'object' &&
                                                val !== null
                                            ) {
                                                if (key === 'from') {
                                                    childClaimArr.push(
                                                        new Paragraph({
                                                            heading:
                                                                HeadingLevel.HEADING_6,
                                                            alignment:
                                                                AlignmentType.LEFT,
                                                            ...(!isDownload
                                                                ? {}
                                                                : {
                                                                      numbering:
                                                                          {
                                                                              reference:
                                                                                  'claims-numbering',
                                                                              level: 0,
                                                                          },
                                                                  }),
                                                            children: [
                                                                ...(!isDownload
                                                                    ? [
                                                                          new TextRun(
                                                                              {
                                                                                  text: `${claimsCounter}. `,
                                                                                  color: '000000',
                                                                                  size: 16,
                                                                              }
                                                                          ),
                                                                      ]
                                                                    : []),
                                                                ...buildClaimRuns(
                                                                    `The ${category} of claim ${getClaimForFeature(
                                                                        component
                                                                    )}, wherein the ${foundComponent.component!} has a ${
                                                                        val.name ||
                                                                        '__'
                                                                    } from ${
                                                                        val.minimum ||
                                                                        '__'
                                                                    } to ${
                                                                        val.maximum ||
                                                                        '__'
                                                                    } ${
                                                                        val.units ||
                                                                        ''
                                                                    }.`
                                                                ),
                                                            ],
                                                        })
                                                    )
                                                }
                                                if (key === 'equal') {
                                                    childClaimArr.push(
                                                        new Paragraph({
                                                            heading:
                                                                HeadingLevel.HEADING_6,
                                                            alignment:
                                                                AlignmentType.LEFT,
                                                            ...(!isDownload
                                                                ? {}
                                                                : {
                                                                      numbering:
                                                                          {
                                                                              reference:
                                                                                  'claims-numbering',
                                                                              level: 0,
                                                                          },
                                                                  }),
                                                            children: [
                                                                ...(!isDownload
                                                                    ? [
                                                                          new TextRun(
                                                                              {
                                                                                  text: `${claimsCounter}. `,
                                                                                  color: '000000',
                                                                                  size: 16,
                                                                              }
                                                                          ),
                                                                      ]
                                                                    : []),
                                                                ...buildClaimRuns(
                                                                    `The ${category} of claim ${getClaimForFeature(
                                                                        component
                                                                    )}, wherein the ${
                                                                        val.name ||
                                                                        '__'
                                                                    } of the ${foundComponent.component!} is equal to from ${
                                                                        val.minimum ||
                                                                        '__'
                                                                    } to ${
                                                                        val.maximum ||
                                                                        '__'
                                                                    } ${
                                                                        val.units ||
                                                                        ''
                                                                    }.`
                                                                ),
                                                            ],
                                                        })
                                                    )
                                                }
                                                if (key === 'defined') {
                                                    childClaimArr.push(
                                                        new Paragraph({
                                                            heading:
                                                                HeadingLevel.HEADING_6,
                                                            alignment:
                                                                AlignmentType.LEFT,
                                                            ...(!isDownload
                                                                ? {}
                                                                : {
                                                                      numbering:
                                                                          {
                                                                              reference:
                                                                                  'claims-numbering',
                                                                              level: 0,
                                                                          },
                                                                  }),
                                                            children: [
                                                                ...(!isDownload
                                                                    ? [
                                                                          new TextRun(
                                                                              {
                                                                                  text: `${claimsCounter}. `,
                                                                                  color: '000000',
                                                                                  size: 16,
                                                                              }
                                                                          ),
                                                                      ]
                                                                    : []),
                                                                ...buildClaimRuns(
                                                                    `The ${category} of claim ${getClaimForFeature(
                                                                        component
                                                                    )}, wherein the ${
                                                                        val.description ||
                                                                        '___'
                                                                    }.`
                                                                ),
                                                            ],
                                                        })
                                                    )
                                                }
                                            } else if (
                                                typeof val !== 'object' &&
                                                val === null
                                            ) {
                                                if (key === 'body') {
                                                    childClaimArr.push(
                                                        new Paragraph({
                                                            heading:
                                                                HeadingLevel.HEADING_6,
                                                            alignment:
                                                                AlignmentType.LEFT,
                                                            ...(!isDownload
                                                                ? {}
                                                                : {
                                                                      numbering:
                                                                          {
                                                                              reference:
                                                                                  'claims-numbering',
                                                                              level: 0,
                                                                          },
                                                                  }),
                                                            children: [
                                                                ...(!isDownload
                                                                    ? [
                                                                          new TextRun(
                                                                              {
                                                                                  text: `${claimsCounter}. `,
                                                                                  color: '000000',
                                                                                  size: 16,
                                                                              }
                                                                          ),
                                                                      ]
                                                                    : []),
                                                                ...buildClaimRuns(
                                                                    `The ${category} of claim ${getClaimForFeature(
                                                                        component
                                                                    )}, wherein the ___ has a ____ from __ to __.`
                                                                ),
                                                            ],
                                                        })
                                                    )
                                                }
                                                if (key === 'equal') {
                                                    childClaimArr.push(
                                                        new Paragraph({
                                                            heading:
                                                                HeadingLevel.HEADING_6,
                                                            alignment:
                                                                AlignmentType.LEFT,
                                                            ...(!isDownload
                                                                ? {}
                                                                : {
                                                                      numbering:
                                                                          {
                                                                              reference:
                                                                                  'claims-numbering',
                                                                              level: 0,
                                                                          },
                                                                  }),
                                                            children: [
                                                                ...(!isDownload
                                                                    ? [
                                                                          new TextRun(
                                                                              {
                                                                                  text: `${claimsCounter}. `,
                                                                                  color: '000000',
                                                                                  size: 16,
                                                                              }
                                                                          ),
                                                                      ]
                                                                    : []),
                                                                ...buildClaimRuns(
                                                                    `The ${category} of claim ${getClaimForFeature(
                                                                        component
                                                                    )}, wherein the ___ of the ____ is equal to from __ to __.`
                                                                ),
                                                            ],
                                                        })
                                                    )
                                                }
                                                if (key === 'defined') {
                                                    childClaimArr.push(
                                                        new Paragraph({
                                                            heading:
                                                                HeadingLevel.HEADING_6,
                                                            alignment:
                                                                AlignmentType.LEFT,
                                                            ...(!isDownload
                                                                ? {}
                                                                : {
                                                                      numbering:
                                                                          {
                                                                              reference:
                                                                                  'claims-numbering',
                                                                              level: 0,
                                                                          },
                                                                  }),
                                                            children: [
                                                                ...(!isDownload
                                                                    ? [
                                                                          new TextRun(
                                                                              {
                                                                                  text: `${claimsCounter}. `,
                                                                                  color: '000000',
                                                                                  size: 16,
                                                                              }
                                                                          ),
                                                                      ]
                                                                    : []),
                                                                ...buildClaimRuns(
                                                                    `The ${category} of claim ${getClaimForFeature(
                                                                        component
                                                                    )}, wherein the ___.`
                                                                ),
                                                            ],
                                                        })
                                                    )
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        )
                    }
                }
            )
        }
        if (question === 'importantComponentsConnections') {
            const importantComponentsConnections = formRef.getFieldValue<
                MachineForm['importantComponentsConnections']
            >(['importantComponentsConnections'])
            if (importantComponentsConnections != null) {
                // console.log(
                //     'filteredComponents',
                //     filteredComponents,
                //     importantComponentsConnections
                // )
                Object.entries(importantComponentsConnections).forEach(
                    (entry: any) => {
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        const [key, val] = entry
                        // console.log('key', key, entry)
                        // const comp = filteredComponents.find(
                        //     (fComp: any) => fComp.component === val?.['1stComponent']
                        // )
                        // if (!comp) return
                        let finalText: string | undefined
                        const notSet = val?.notSet
                        const partsOfIndependentClaim = formRef.getFieldValue<
                            MachineValues['partsOfIndependentClaim']
                        >(['partsOfIndependentClaim'])
                        const summaryOfSelectedEmbodimentsOfTheInvention =
                            formRef.getFieldValue<
                                FormValues['summaryOfSelectedEmbodimentsOfTheInvention']
                            >(['summaryOfSelectedEmbodimentsOfTheInvention'])
                        const featuresPartsOfIndependentClaim =
                            formRef.getFieldValue<
                                MachineValues['featuresPartsOfIndependentClaim']
                            >(['featuresPartsOfIndependentClaim'])
                        const isfeaturesPartsOfParentClaim =
                            (!summaryOfSelectedEmbodimentsOfTheInvention ||
                                summaryOfSelectedEmbodimentsOfTheInvention.includes(
                                    `importantComponentsConnections_${val.id}`
                                )) &&
                            (summaryOfSelectedEmbodimentsOfTheInvention ||
                                !featuresPartsOfIndependentClaim ||
                                featuresPartsOfIndependentClaim.includes(
                                    `importantComponentsConnections_${val.id}`
                                ))
                        const firstComponent =
                            val &&
                            ((partsOfIndependentClaim &&
                                partsOfIndependentClaim.includes(
                                    val['1stComponent']
                                )) ||
                                (summaryOfSelectedEmbodimentsOfTheInvention &&
                                    summaryOfSelectedEmbodimentsOfTheInvention.includes(
                                        val['1stComponent']
                                    ))) &&
                            lowerfirst(
                                extractComponentName(
                                    val['1stComponent'],
                                    formRef
                                )
                            )
                        const secondComponent =
                            val &&
                            ((partsOfIndependentClaim &&
                                partsOfIndependentClaim.includes(
                                    val['2ndComponent']
                                )) ||
                                (summaryOfSelectedEmbodimentsOfTheInvention &&
                                    summaryOfSelectedEmbodimentsOfTheInvention.includes(
                                        val['2ndComponent']
                                    ))) &&
                            lowerfirst(
                                extractComponentName(
                                    val['2ndComponent'],
                                    formRef
                                )
                            )
                        if (notSet) {
                            const firstPart = val?.['1stPart']
                            const secondPart = val?.['2ndPart']
                            const thirdPart = val?.['3rdPart']
                            if (
                                firstPart &&
                                firstComponent &&
                                secondPart &&
                                secondComponent &&
                                thirdPart
                            ) {
                                if (isfeaturesPartsOfParentClaim) {
                                    finalText = `${firstPart} the ${firstComponent} ${secondPart} the ${secondComponent} ${
                                        thirdPart || '__'
                                    }`
                                } else {
                                    // finalText = `The ${category} of claim 1, ${firstPart} the ${firstComponent} ${secondPart} the ${secondComponent} ${
                                    //     thirdPart || '__'
                                    // }`
                                    finalText = `The ${category} of claim ${getClaimRefForTwoComponents(
                                        val['1stComponent'],
                                        val['2ndComponent']
                                    )}, ${getComp1ReintroductionClause(
                                        val['1stComponent'],
                                        val['2ndComponent']
                                    )}${firstPart} the ${firstComponent} ${secondPart} the ${secondComponent} ${
                                        thirdPart || '__'
                                    }`
                                }
                            }
                        } else {
                            const foundSubConnectionType = connectionRelation[
                                val?.connectionType
                            ]?.find((connection: any) => {
                                return (
                                    connection.value === val?.subConnectionType
                                )
                            })
                            const connectionType = foundSubConnectionType
                                ? val?.subConnectionType || val?.connectionType
                                : val?.connectionType
                            if (
                                firstComponent &&
                                connectionType &&
                                secondComponent
                            ) {
                                if (isfeaturesPartsOfParentClaim) {
                                    finalText = `wherein the ${firstComponent} is ${connectionType.toLowerCase()} the ${secondComponent}`
                                } else {
                                    // finalText = `The ${category} of claim 1, wherein the ${firstComponent} is ${connectionType.toLowerCase()} the ${secondComponent}`
                                    finalText = `The ${category} of claim ${getClaimRefForTwoComponents(
                                        val['1stComponent'],
                                        val['2ndComponent']
                                    )}, ${getComp1ReintroductionClause(
                                        val['1stComponent'],
                                        val['2ndComponent']
                                    )}wherein the ${firstComponent} is ${connectionType.toLowerCase()} the ${secondComponent}`
                                }
                            }
                        }
                        if (finalText) {
                            if (isfeaturesPartsOfParentClaim) {
                                parentClaimArr.push(
                                    new Paragraph({
                                        heading: HeadingLevel.HEADING_6,
                                        alignment: AlignmentType.LEFT,
                                        children: [
                                            ...buildClaimRuns(`${finalText}.`),
                                        ],
                                    })
                                )
                            } else {
                                claimsCounter++
                                childClaimArr.push(
                                    new Paragraph({
                                        heading: HeadingLevel.HEADING_6,
                                        alignment: AlignmentType.LEFT,
                                        ...(!isDownload
                                            ? {}
                                            : {
                                                  numbering: {
                                                      reference:
                                                          'claims-numbering',
                                                      level: 0,
                                                  },
                                              }),
                                        children: [
                                            ...(!isDownload
                                                ? [
                                                      new TextRun({
                                                          text: `${claimsCounter}. `,
                                                          color: '000000',
                                                          size: 16,
                                                      }),
                                                  ]
                                                : []),
                                            ...buildClaimRuns(`${finalText}.`),
                                        ],
                                    })
                                )
                            }
                        }
                    }
                )
            }
        }

        if (question === 'importantComponentsArrangements') {
            const importantComponentsArrangements = formRef.getFieldValue<
                MachineForm['importantComponentsArrangements']
            >(['importantComponentsArrangements'])
            if (importantComponentsArrangements != null) {
                Object.entries(importantComponentsArrangements).forEach(
                    (entry: any) => {
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        const [key, val] = entry
                        let finalText: string | undefined
                        const notSet = val?.notSet
                        const partsOfIndependentClaim = formRef.getFieldValue<
                            MachineValues['partsOfIndependentClaim']
                        >(['partsOfIndependentClaim'])
                        const summaryOfSelectedEmbodimentsOfTheInvention =
                            formRef.getFieldValue<
                                FormValues['summaryOfSelectedEmbodimentsOfTheInvention']
                            >(['summaryOfSelectedEmbodimentsOfTheInvention'])
                        const featuresPartsOfIndependentClaim =
                            formRef.getFieldValue<
                                MachineValues['featuresPartsOfIndependentClaim']
                            >(['featuresPartsOfIndependentClaim'])
                        const isfeaturesPartsOfParentClaim =
                            (!summaryOfSelectedEmbodimentsOfTheInvention ||
                                summaryOfSelectedEmbodimentsOfTheInvention.includes(
                                    `importantComponentsArrangements_${val.id}`
                                )) &&
                            (summaryOfSelectedEmbodimentsOfTheInvention ||
                                !featuresPartsOfIndependentClaim ||
                                featuresPartsOfIndependentClaim.includes(
                                    `importantComponentsArrangements_${val.id}`
                                ))
                        const firstComponent =
                            val &&
                            ((partsOfIndependentClaim &&
                                partsOfIndependentClaim.includes(
                                    val['1stComponent']
                                )) ||
                                (summaryOfSelectedEmbodimentsOfTheInvention &&
                                    summaryOfSelectedEmbodimentsOfTheInvention.includes(
                                        val['1stComponent']
                                    ))) &&
                            lowerfirst(
                                extractComponentName(
                                    val['1stComponent'],
                                    formRef
                                )
                            )
                        const secondComponent =
                            val &&
                            ((partsOfIndependentClaim &&
                                partsOfIndependentClaim.includes(
                                    val['2ndComponent']
                                )) ||
                                (summaryOfSelectedEmbodimentsOfTheInvention &&
                                    summaryOfSelectedEmbodimentsOfTheInvention.includes(
                                        val['2ndComponent']
                                    ))) &&
                            lowerfirst(
                                extractComponentName(
                                    val['2ndComponent'],
                                    formRef
                                )
                            )
                        if (notSet) {
                            const firstPart = val?.['1stPart']
                            const secondPart = val?.['2ndPart']
                            const thirdPart = val?.['3rdPart']
                            if (
                                firstComponent &&
                                secondPart &&
                                secondComponent
                            ) {
                                if (isfeaturesPartsOfParentClaim) {
                                    finalText = `${
                                        firstPart ? `${firstPart} the ` : ''
                                    }${firstComponent} ${secondPart} the ${secondComponent} ${
                                        thirdPart || '__'
                                    }`
                                } else {
                                    // finalText = `The ${category} of claim 1, ${
                                    //     firstPart ? `${firstPart} the ` : ''
                                    // }${firstComponent} ${secondPart} the ${secondComponent} ${
                                    //     thirdPart || '__'
                                    // }`
                                    finalText = `The ${category} of claim ${getClaimRefForTwoComponents(
                                        val['1stComponent'],
                                        val['2ndComponent']
                                    )}, ${getComp1ReintroductionClause(
                                        val['1stComponent'],
                                        val['2ndComponent']
                                    )}${
                                        firstPart ? `${firstPart} the ` : ''
                                    }${firstComponent} ${secondPart} the ${secondComponent} ${
                                        thirdPart || '__'
                                    }`
                                }
                            }
                        } else {
                            const arrangementType = val?.arrangementType
                            if (
                                firstComponent &&
                                arrangementType &&
                                secondComponent
                            ) {
                                if (isfeaturesPartsOfParentClaim) {
                                    finalText = `wherein the ${firstComponent} is ${arrangementType.toLowerCase()} the ${secondComponent}`
                                } else {
                                    //  finalText = `The ${category} of claim 1, wherein the ${firstComponent} is ${arrangementType.toLowerCase()} the ${secondComponent}`
                                    finalText = `The ${category} of claim ${getClaimRefForTwoComponents(
                                        val['1stComponent'],
                                        val['2ndComponent']
                                    )}, ${getComp1ReintroductionClause(
                                        val['1stComponent'],
                                        val['2ndComponent']
                                    )}wherein the ${firstComponent} is ${arrangementType.toLowerCase()} the ${secondComponent}`
                                }
                            }
                        }
                        if (finalText) {
                            if (isfeaturesPartsOfParentClaim) {
                                parentClaimArr.push(
                                    new Paragraph({
                                        heading: HeadingLevel.HEADING_6,
                                        alignment: AlignmentType.LEFT,
                                        children: [
                                            ...buildClaimRuns(`${finalText}.`),
                                        ],
                                    })
                                )
                            } else {
                                claimsCounter++
                                childClaimArr.push(
                                    new Paragraph({
                                        heading: HeadingLevel.HEADING_6,
                                        alignment: AlignmentType.LEFT,
                                        ...(!isDownload
                                            ? {}
                                            : {
                                                  numbering: {
                                                      reference:
                                                          'claims-numbering',
                                                      level: 0,
                                                  },
                                              }),
                                        children: [
                                            ...(!isDownload
                                                ? [
                                                      new TextRun({
                                                          text: `${claimsCounter}. `,
                                                          color: '000000',
                                                          size: 16,
                                                      }),
                                                  ]
                                                : []),
                                            ...buildClaimRuns(`${finalText}.`),
                                        ],
                                    })
                                )
                            }
                        }
                    }
                )
            }
        }
        if (question === 'functionalConnections') {
            const functionalConnections = formRef.getFieldValue<
                MachineForm['functionalConnections']
            >(['functionalConnections'])
            if (functionalConnections != null) {
                Object.entries(functionalConnections).forEach((entry: any) => {
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    const [key, val] = entry
                    let finalText: string | undefined
                    const notSet = val?.notSet
                    const partsOfIndependentClaim = formRef.getFieldValue<
                        MachineValues['partsOfIndependentClaim']
                    >(['partsOfIndependentClaim'])
                    const summaryOfSelectedEmbodimentsOfTheInvention =
                        formRef.getFieldValue<
                            FormValues['summaryOfSelectedEmbodimentsOfTheInvention']
                        >(['summaryOfSelectedEmbodimentsOfTheInvention'])
                    const featuresPartsOfIndependentClaim =
                        formRef.getFieldValue<
                            MachineValues['featuresPartsOfIndependentClaim']
                        >(['featuresPartsOfIndependentClaim'])
                    const isfeaturesPartsOfParentClaim =
                        (!summaryOfSelectedEmbodimentsOfTheInvention ||
                            summaryOfSelectedEmbodimentsOfTheInvention.includes(
                                `functionalConnections_${val.id}`
                            )) &&
                        (summaryOfSelectedEmbodimentsOfTheInvention ||
                            !featuresPartsOfIndependentClaim ||
                            featuresPartsOfIndependentClaim.includes(
                                `functionalConnections_${val.id}`
                            ))
                    const firstComponent =
                        val &&
                        ((partsOfIndependentClaim &&
                            partsOfIndependentClaim.includes(
                                val['1stComponent']
                            )) ||
                            (summaryOfSelectedEmbodimentsOfTheInvention &&
                                summaryOfSelectedEmbodimentsOfTheInvention.includes(
                                    val['1stComponent']
                                ))) &&
                        lowerfirst(
                            extractComponentName(val['1stComponent'], formRef)
                        )
                    const secondComponent =
                        val &&
                        ((partsOfIndependentClaim &&
                            partsOfIndependentClaim.includes(
                                val['2ndComponent']
                            )) ||
                            (summaryOfSelectedEmbodimentsOfTheInvention &&
                                summaryOfSelectedEmbodimentsOfTheInvention.includes(
                                    val['2ndComponent']
                                ))) &&
                        lowerfirst(
                            extractComponentName(val['2ndComponent'], formRef)
                        )
                    if (notSet) {
                        const firstPart = val?.['1stPart']
                        const secondPart = val?.['2ndPart']
                        const thirdPart = val?.['3rdPart']
                        if (firstComponent && secondPart && secondComponent) {
                            if (isfeaturesPartsOfParentClaim) {
                                finalText = `${
                                    firstPart ? `${firstPart} the ` : ''
                                }${firstComponent} ${secondPart} the ${secondComponent} ${
                                    thirdPart || '__'
                                }`
                            } else {
                                // finalText = `The ${category} of claim 1, ${
                                //     firstPart ? `${firstPart} the ` : ''
                                // }${firstComponent} ${secondPart} the ${secondComponent} ${
                                //     thirdPart || '__'
                                // }`
                                finalText = `The ${category} of claim ${getClaimRefForTwoComponents(
                                    val['1stComponent'],
                                    val['2ndComponent']
                                )}, ${getComp1ReintroductionClause(
                                    val['1stComponent'],
                                    val['2ndComponent']
                                )}${
                                    firstPart ? `${firstPart} the ` : ''
                                }${firstComponent} ${secondPart} the ${secondComponent} ${
                                    thirdPart || '__'
                                }`
                            }
                        }
                    } else if (firstComponent && secondComponent) {
                        if (isfeaturesPartsOfParentClaim) {
                            finalText = `wherein the ${firstComponent} is functionally connected with the ${secondComponent}`
                        } else {
                            //  finalText = `The ${category} of claim 1, wherein the ${firstComponent} is functionally connected with the ${secondComponent}`
                            finalText = `The ${category} of claim ${getClaimRefForTwoComponents(
                                val['1stComponent'],
                                val['2ndComponent']
                            )}, ${getComp1ReintroductionClause(
                                val['1stComponent'],
                                val['2ndComponent']
                            )}wherein the ${firstComponent} is functionally connected with the ${secondComponent}`
                        }
                    }
                    if (finalText) {
                        if (isfeaturesPartsOfParentClaim) {
                            parentClaimArr.push(
                                new Paragraph({
                                    heading: HeadingLevel.HEADING_6,
                                    alignment: AlignmentType.LEFT,
                                    children: [
                                        ...buildClaimRuns(`${finalText}.`),
                                    ],
                                })
                            )
                        } else {
                            claimsCounter++
                            childClaimArr.push(
                                new Paragraph({
                                    heading: HeadingLevel.HEADING_6,
                                    alignment: AlignmentType.LEFT,
                                    ...(!isDownload
                                        ? {}
                                        : {
                                              numbering: {
                                                  reference: 'claims-numbering',
                                                  level: 0,
                                              },
                                          }),
                                    children: [
                                        ...(!isDownload
                                            ? [
                                                  new TextRun({
                                                      text: `${claimsCounter}. `,
                                                      color: '000000',
                                                      size: 16,
                                                  }),
                                              ]
                                            : []),
                                        ...buildClaimRuns(`${finalText}.`),
                                    ],
                                })
                            )
                        }
                    }
                })
            }
        }
        if (question === 'dependantComponentsConnections') {
            const dependantComponentsConnections = formRef.getFieldValue<
                MachineForm['dependantComponentsConnections']
            >(['dependantComponentsConnections'])
            if (dependantComponentsConnections != null) {
                Object.entries(dependantComponentsConnections).forEach(
                    (entry: any) => {
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        const [key, val] = entry
                        let finalText: string | undefined
                        claimsCounter++
                        const notSet = val?.notSet
                        const firstComponent = lowerfirst(
                            extractComponentName(val?.['1stComponent'], formRef)
                        )
                        const secondComponent = lowerfirst(
                            extractComponentName(val?.['2ndComponent'], formRef)
                        )
                        if (notSet) {
                            const firstPart = val?.['1stPart']
                            const secondPart = val?.['2ndPart']
                            const thirdPart = val?.['3rdPart']
                            if (
                                firstPart &&
                                firstComponent &&
                                secondPart &&
                                secondComponent &&
                                thirdPart
                            ) {
                                // finalText = `The ${category} of claim 1, ${firstPart} the ${firstComponent} ${secondPart} the ${secondComponent} ${
                                //     thirdPart || '__'
                                // }`
                                finalText = `The ${category} of claim ${getClaimRefForTwoComponents(
                                    val['1stComponent'],
                                    val['2ndComponent']
                                )}, ${getComp1ReintroductionClause(
                                    val['1stComponent'],
                                    val['2ndComponent']
                                )}${firstPart} the ${firstComponent} ${secondPart} the ${secondComponent} ${
                                    thirdPart || '__'
                                }`
                            }
                        } else {
                            const foundSubConnectionType = connectionRelation[
                                val?.connectionType
                            ]?.find((connection: any) => {
                                return (
                                    connection.value === val?.subConnectionType
                                )
                            })
                            const connectionType = foundSubConnectionType
                                ? val?.subConnectionType || val?.connectionType
                                : val?.connectionType
                            if (
                                firstComponent &&
                                connectionType &&
                                secondComponent
                            ) {
                                //    finalText = `The ${category} of claim 1, wherein the ${firstComponent} is ${connectionType.toLowerCase()} the ${secondComponent}`
                                finalText = `The ${category} of claim ${getClaimRefForTwoComponents(
                                    val['1stComponent'],
                                    val['2ndComponent']
                                )}, ${getComp1ReintroductionClause(
                                    val['1stComponent'],
                                    val['2ndComponent']
                                )}wherein the ${firstComponent} is ${connectionType.toLowerCase()} the ${secondComponent}`
                            }
                        }
                        if (finalText) {
                            childClaimArr.push(
                                new Paragraph({
                                    heading: HeadingLevel.HEADING_6,
                                    alignment: AlignmentType.LEFT,
                                    ...(!isDownload
                                        ? {}
                                        : {
                                              numbering: {
                                                  reference: 'claims-numbering',
                                                  level: 0,
                                              },
                                          }),
                                    children: [
                                        ...(!isDownload
                                            ? [
                                                  new TextRun({
                                                      text: `${claimsCounter}. `,
                                                      color: '000000',
                                                      size: 16,
                                                  }),
                                              ]
                                            : []),
                                        ...buildClaimRuns(`${finalText}.`),
                                    ],
                                })
                            )
                        }
                    }
                )
            }
        }
        if (question === 'componentWeights') {
            const typedFormRef = formRef as any
            const weightImportance = typedFormRef.getFieldValue([
                'componentWeights',
                'weightImportance',
            ])
            const weights = typedFormRef.getFieldValue('componentWeights')
            if (weights) {
                Object.entries(weights).forEach((entry: any) => {
                    const [key, val] = entry
                    let pushWeight = false
                    Object.entries(val).forEach((valEntry: any) => {
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        const [entryKey, entryVal] = valEntry
                        if (entryVal) {
                            pushWeight = true
                        }
                    })
                    if (pushWeight) {
                        const minAcc = val['min-acceptable-weight']
                        const maxAcc = val['max-acceptable-weight']
                        const ifAcc = val['acceptable-weight-importance']
                        const acc =
                            (minAcc || maxAcc) && (ifAcc || !weightImportance)
                        const minPref = val['min-preferable-weight']
                        const maxPref = val['max-preferable-weight']
                        const ifPref = val['preferable-weight-importance']
                        const pref =
                            (minPref || maxPref) &&
                            (ifPref || !weightImportance)
                        claimsCounter++
                        if (ifAcc || ifPref) {
                            childClaimArr.push(
                                new Paragraph({
                                    heading: HeadingLevel.HEADING_6,
                                    alignment: AlignmentType.LEFT,
                                    ...(!isDownload
                                        ? {}
                                        : {
                                              numbering: {
                                                  reference: 'claims-numbering',
                                                  level: 0,
                                              },
                                          }),
                                    children: [
                                        ...(!isDownload
                                            ? [
                                                  new TextRun({
                                                      text: `${claimsCounter}. `,
                                                      color: '000000',
                                                      size: 16,
                                                  }),
                                              ]
                                            : []),
                                        ...buildClaimRuns(`The ${category} of claim ${getClaimForFeature(
                                            key
                                        )}, wherein the ${key} has a ${
                                            acc
                                                ? `acceptable percentage of weight from ${
                                                      minAcc || '__'
                                                  }% to ${maxAcc || '__ '}%`
                                                : ''
                                        } 
                                        ${acc && pref ? 'and' : ''}
                                        ${
                                            pref
                                                ? `preferable percentage of weight from ${
                                                      minPref || '__'
                                                  }% to ${maxPref || '__'}%`
                                                : ''
                                        }.`),
                                    ],
                                })
                            )
                        }
                    }
                })
            }
        }
        if (question === 'characteristicExplanation') {
            const typedFormRef = formRef as any
            const characteristicExplanation = typedFormRef.getFieldValue(
                'characteristicExplanation'
            )
            if (characteristicExplanation) {
                Object.entries(characteristicExplanation).forEach(
                    (entry: any) => {
                        const [key, val] = entry
                        if (val) {
                            claimsCounter++

                            childClaimArr.push(
                                new Paragraph({
                                    heading: HeadingLevel.HEADING_6,
                                    alignment: AlignmentType.LEFT,
                                    ...(!isDownload
                                        ? {}
                                        : {
                                              numbering: {
                                                  reference: 'claims-numbering',
                                                  level: 0,
                                              },
                                          }),
                                    children: [
                                        ...(!isDownload
                                            ? [
                                                  new TextRun({
                                                      text: `${claimsCounter}. `,
                                                      color: '000000',
                                                      size: 16,
                                                  }),
                                              ]
                                            : []),
                                        new TextRun({
                                            text: `Provided ${key} characteristic being advantagous because of ${val}`,
                                            color: '000000',
                                            size: 16,
                                        }),
                                    ],
                                })
                            )
                        }
                    }
                )
            }
        }
    })

    function normalizeParagraphs(paragraphs: Paragraph[]): Paragraph[] {
        try {
            return paragraphs.map((paragraph, index) => {
                const isLast = index === paragraphs.length - 1
                const isSecondLast = index === paragraphs.length - 2

                // Get heading and alignment from properties
                const heading =
                    paragraph.properties?.root?.find(
                        (prop: any) => prop?.rootKey === 'w:pStyle'
                    )?.root?.[0]?.root?.val ?? HeadingLevel.HEADING_6
                const alignment =
                    paragraph.properties?.root?.find(
                        (prop: any) => prop?.rootKey === 'w:jc'
                    )?.root?.[0]?.root?.val ?? AlignmentType.LEFT
                const spacing = paragraph.properties?.root?.find(
                    (prop: any) => prop?.rootKey === 'w:spacing'
                )?.root?.[0]?.root
                const numberingReference =
                    paragraph.properties?.numberingReferences?.[0]?.reference

                // Get the children (TextRun objects) from the paragraph
                const children: any[] = []

                // Try to extract text from paragraph children
                try {
                    // Paragraphs have children array that contains TextRun objects
                    const paragraphChildren = (paragraph as any).root

                    if (paragraphChildren && Array.isArray(paragraphChildren)) {
                        paragraphChildren.forEach(
                            (child: any, childIdx: number) => {
                                if (childIdx === 0) return // Skip first child if it's not a TextRun
                                const textProperties = child?.root?.[0]?.root
                                const textContent = child?.root?.[1]?.root?.[1]

                                if (textContent) {
                                    const isLastTextRun =
                                        childIdx ===
                                        paragraphChildren.length - 1

                                    let modifiedText = textContent

                                    // Only modify the last TextRun of non-last paragraphs
                                    if (!isLast && isLastTextRun) {
                                        const startsWithWherein = textContent
                                            .toLowerCase()
                                            .startsWith('wherein')

                                        modifiedText = textContent.replace(
                                            '.',
                                            `;${
                                                isSecondLast &&
                                                startsWithWherein
                                                    ? ' and'
                                                    : ''
                                            }`
                                        )
                                    }

                                    children.push(
                                        new TextRun({
                                            text: modifiedText,
                                            color: textProperties?.find(
                                                (prop: any) =>
                                                    prop?.rootKey === 'w:color'
                                            )?.root?.[0]?.root?.val,
                                            size: textProperties?.find(
                                                (prop: any) =>
                                                    prop?.rootKey === 'w:sz'
                                            )?.root?.[0]?.root?.val,
                                            underline:
                                                textProperties?.find(
                                                    (prop: any) =>
                                                        prop?.rootKey === 'w:u'
                                                )?.root?.[0]?.root?.val ===
                                                'single',
                                        })
                                    )
                                }
                            }
                        )
                    }
                } catch (innerError) {
                    console.error(
                        'Error extracting text from paragraph:',
                        innerError
                    )
                }

                // If we couldn't extract children, return original paragraph
                if (children.length === 0) {
                    return paragraph
                }

                return new Paragraph({
                    heading,
                    alignment,
                    spacing,
                    ...(!numberingReference
                        ? {}
                        : {
                              numbering: {
                                  reference: numberingReference,
                                  level: 0,
                              },
                          }),
                    children,
                })
            })
        } catch (e) {
            console.error('Error normalizing paragraphs:', e)
            return paragraphs
        }
    }

    const docx = new Document({
        sections: [
            {
                properties: {},
                footers: {
                    default: new Footer({
                        children: [
                            new Paragraph({
                                children: [
                                    new TextRun({
                                        size: 16,
                                        color: '000000',
                                        children: [
                                            'United States Patent Application Page ',
                                            PageNumber.CURRENT,
                                            ' of ',
                                            PageNumber.TOTAL_PAGES,
                                            ' ',
                                            formRef.getFieldValue([
                                                'titleComponent',
                                                'footerNumber',
                                            ]),
                                        ],
                                    }),
                                ],
                                alignment: AlignmentType.CENTER,
                            }),
                        ],
                    }),
                },
                children: [
                    ...headingArr,
                    ...titleArr,
                    ...backgroundInventionArr,
                    ...summaryArr,
                    ...drawingsDescriptionArr,
                    ...detailedDescriptionArr,
                    ...arr,
                    ...normalizeParagraphs(parentClaimArr),
                    ...childClaimArr,
                    ...abstractArr,
                ],
            },
        ],
        styles: docxStyles,
        numbering: {
            config: [
                {
                    reference: 'my-reference',
                    levels: [
                        {
                            level: 0,
                            format: LevelFormat.DECIMAL,
                            text: '[%1]  ',
                            alignment: AlignmentType.START,
                            suffix: LevelSuffix.SPACE,
                            style: {
                                paragraph: {
                                    indent: { left: 0 },
                                },
                                run: {
                                    size: 16,
                                },
                            },
                        },
                        {
                            level: 1,
                            format: LevelFormat.DECIMAL,
                            text: '[%1]     %2.  ',
                            alignment: AlignmentType.START,
                            suffix: LevelSuffix.SPACE,
                            style: {
                                paragraph: {
                                    indent: { left: 0 },
                                },
                                run: {
                                    size: 16,
                                },
                            },
                        },
                    ],
                },
                {
                    reference: 'claims-numbering',

                    levels: [
                        {
                            level: 0,
                            format: LevelFormat.DECIMAL,
                            text: '%1. ',
                            alignment: AlignmentType.START,
                            suffix: LevelSuffix.SPACE,
                            style: {
                                paragraph: {
                                    indent: { left: 0 },
                                },
                                run: {
                                    size: 16,
                                },
                            },
                        },
                        {
                            level: 1,
                            format: LevelFormat.DECIMAL,
                            text: '[%1]     %2.  ',
                            alignment: AlignmentType.START,
                            suffix: LevelSuffix.SPACE,
                            style: {
                                paragraph: {
                                    indent: { left: 0 },
                                },
                                run: {
                                    size: 16,
                                },
                            },
                        },
                    ],
                },
            ],
        },
    })
    return docx
}
